import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { locationApi, positionApi, timeStationApi, scheduleApi, payrollDepartmentApi, roleApi, breakTimeSettingsApi, systemSettingsApi, trainingTypeApi, companySettingsApi, publicHolidayApi, publicHolidayPolicyApi, shiftTemplateApi, faceApi, auditLogConfigApi, adminUploadsApi, getApiBaseUrl } from '../services/api';
import { Location, Position, TimeStation, Schedule, PayrollDepartment, Role, Permission, BreakTimeSettings, BreakTimeRule, SystemSetting, TrainingType, TimeCardSettings, CompanySettings, PublicHoliday, CreateLocationRequest, CreatePositionRequest, CreateTimeStationRequest, CreateScheduleRequest, CreatePayrollDepartmentRequest, CreateRoleRequest, UpdateRoleRequest, CreateBreakTimeSettingsRequest, CreateTrainingTypeRequest, UpdateTrainingTypeRequest, CreateCompanySettingsRequest, UpdateCompanySettingsRequest, CreatePublicHolidayRequest, UpdatePublicHolidayRequest, ShiftTemplate, CreateShiftTemplateRequest, PublicHolidayPolicy, CreatePublicHolidayPolicyRequest, UpdatePublicHolidayPolicyRequest, MfaMethod, MfaRequirement } from '../types';
import { MapPin, Briefcase, Calendar, Monitor, Shield, Clock, GraduationCap, Plus, Edit, Trash2, Filter, Wifi, WifiOff, Tablet, Smartphone, Laptop, Save, Menu, X, Search, Building2, User, RefreshCw, CheckCircle2, AlertTriangle, Database, Cloud, Trash, Currency, Power, PowerOff, SlidersHorizontal } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BasketSelect from '../components/BasketSelect';
import { useToast, ToastContainer } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useLocationContext } from '../context/LocationContext';
import { formatCompanyDate } from '../utils/companyDateFormat';
import * as signalR from '@microsoft/signalr';
import LocationMapPicker from '../components/LocationMapPicker';
import {
  buildShiftTemplateColorMap,
  resolveShiftTemplateColor,
  shiftTemplateLightBackground,
  sortShiftTemplatesByStartTime,
} from '../utils/shiftTemplateDisplay';
import NurseBandingSettingsPanel from '../components/NurseBandingSettingsPanel';
import ShiftGroupsSettingsPanel from '../components/ShiftGroupsSettingsPanel';

type AuditLogEntity =
  | 'Employees'
  | 'Shifts'
  | 'Timecards'
  | 'Schedules'
  | 'Locations'
  | 'Positions'
  | 'Incidents'
  | 'Holidays'
  | 'Tasks'
  | 'Attendance'
  | 'Leave Requests'
  | 'Payroll'
  | 'Training Types'
  | 'Roles'
  | 'Time Stations'
  | 'Shift Templates'
  | 'Public Holidays'
  | 'Company Settings'
  | 'Payroll Departments';
type AuditLogAction =
  | 'Created'
  | 'Updated'
  | 'Deleted'
  | 'Activated'
  | 'Deactivated'
  | 'Approved'
  | 'Unapproved'
  | 'Denied'
  | 'Submitted'
  | 'Cancelled'
  | 'Assigned'
  | 'Unassigned'
  | 'Clocked In'
  | 'Clocked Out'
  | 'Logged In'
  | 'Logged Out'
  | 'Password Changed'
  | 'Face Enrolled'
  | 'Face Deleted'
  | 'Started'
  | 'Completed'
  | 'Resolved'
  | 'Escalated'
  | 'Imported'
  | 'Bulk Deleted'
  | 'Week Cleared'
  | 'Published'
  | 'Unpublished'
  | 'Marked Changed'
  | 'Sort Order Updated';

interface AuditLogFieldOption {
  id: string;
  label: string;
  captures: string[];
}

interface AuditLogConfig {
  id: string;
  entity: AuditLogEntity;
  actions: AuditLogAction[];
  fields: string[];
  isActive: boolean;
}

const auditLogEntities: AuditLogEntity[] = [
  'Employees',
  'Shifts',
  'Timecards',
  'Schedules',
  'Locations',
  'Positions',
  'Incidents',
  'Holidays',
  'Tasks',
  'Attendance',
  'Leave Requests',
  'Payroll',
  'Training Types',
  'Roles',
  'Time Stations',
  'Shift Templates',
  'Public Holidays',
  'Company Settings',
  'Payroll Departments',
];

const auditLogEntityActions: Record<AuditLogEntity, AuditLogAction[]> = {
  Employees: ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated', 'Logged In', 'Logged Out', 'Password Changed', 'Face Enrolled', 'Face Deleted'],
  Shifts: ['Created', 'Updated', 'Deleted', 'Imported', 'Bulk Deleted', 'Week Cleared'],
  Timecards: ['Created', 'Updated', 'Deleted', 'Approved', 'Unapproved'],
  Schedules: ['Created', 'Updated', 'Deleted', 'Published', 'Unpublished', 'Marked Changed', 'Sort Order Updated'],
  Locations: ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated'],
  Positions: ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated'],
  Incidents: ['Created', 'Updated', 'Deleted', 'Resolved', 'Escalated'],
  Holidays: ['Created', 'Updated', 'Deleted', 'Approved', 'Denied', 'Submitted', 'Cancelled'],
  Tasks: ['Created', 'Updated', 'Deleted', 'Assigned', 'Unassigned', 'Started', 'Completed'],
  Attendance: ['Created', 'Updated', 'Deleted', 'Clocked In', 'Clocked Out'],
  'Leave Requests': ['Created', 'Updated', 'Approved', 'Denied', 'Submitted', 'Cancelled'],
  Payroll: ['Created', 'Updated', 'Approved'],
  'Training Types': ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated'],
  Roles: ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated'],
  'Time Stations': ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated', 'Approved'],
  'Shift Templates': ['Created', 'Updated', 'Deleted'],
  'Public Holidays': ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated'],
  'Company Settings': ['Updated'],
  'Payroll Departments': ['Created', 'Updated', 'Deleted', 'Activated', 'Deactivated'],
};

const auditLogEntityFields: Record<AuditLogEntity, AuditLogFieldOption[]> = {
  Employees: [
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    {
      id: 'homeAddress',
      label: 'Home address',
      captures: ['address', 'address1', 'address2', 'address3', 'county', 'zipCode'],
    },
    { id: 'email', label: 'Email', captures: ['email'] },
    { id: 'phone', label: 'Phone', captures: ['phone'] },
    { id: 'position', label: 'Position', captures: ['positionId', 'positionName'] },
    { id: 'location', label: 'Location', captures: ['locationId', 'locationName'] },
    { id: 'payrollDepartment', label: 'Payroll Department', captures: ['payrollDepartmentId', 'payrollDepartmentName'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
    { id: 'startDate', label: 'Start Date', captures: ['startDate'] },
    { id: 'role', label: 'Role', captures: ['roleId', 'roleName'] },
  ],
  Shifts: [
    { id: 'shift', label: 'Shift', captures: ['shiftId'] },
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    { id: 'position', label: 'Position', captures: ['positionId', 'positionName'] },
    { id: 'location', label: 'Location', captures: ['locationId', 'locationName'] },
    { id: 'schedule', label: 'Schedule', captures: ['scheduleId', 'scheduleName'] },
    { id: 'startTime', label: 'Start Time', captures: ['startTime'] },
    { id: 'endTime', label: 'End Time', captures: ['endTime'] },
    { id: 'paidHours', label: 'Paid Hours', captures: ['paidHours'] },
    { id: 'unpaidHours', label: 'Unpaid Hours', captures: ['unpaidHours'] },
    { id: 'breakTimePaid', label: 'Break Paid', captures: ['breakTimePaid'] },
    { id: 'breakUseGlobalSettings', label: 'Use Global Break Settings', captures: ['breakUseGlobalSettings'] },
    { id: 'color', label: 'Color', captures: ['color'] },
    { id: 'displayOption', label: 'Display Option', captures: ['displayOption'] },
    { id: 'notes', label: 'Notes', captures: ['notes'] },
    { id: 'importSummary', label: 'Import Summary', captures: ['importedCount', 'failedCount', 'skippedCount', 'fileName', 'batchId'] },
    { id: 'bulkDeleteSummary', label: 'Bulk Delete Summary', captures: ['deletedCount', 'batchId'] },
    { id: 'weekClearedSummary', label: 'Week Cleared Summary', captures: ['deletedCount', 'weekStartDate'] },
  ],
  Timecards: [
    { id: 'timecard', label: 'Timecard', captures: ['timecardId'] },
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    { id: 'position', label: 'Position', captures: ['positionId', 'positionName'] },
    { id: 'location', label: 'Location', captures: ['locationId', 'locationName'] },
    { id: 'clockIn', label: 'Clock In', captures: ['clockIn'] },
    { id: 'clockOut', label: 'Clock Out', captures: ['clockOut'] },
    { id: 'breakMinutes', label: 'Break Minutes', captures: ['breakMinutes'] },
    { id: 'approval', label: 'Approval', captures: ['approvedById', 'approvedByName', 'approvedAt'] },
  ],
  Schedules: [
    { id: 'schedule', label: 'Schedule', captures: ['scheduleId', 'scheduleName'] },
    { id: 'description', label: 'Description', captures: ['description'] },
    { id: 'location', label: 'Location', captures: ['locationIds', 'locationNames', 'locationName'] },
    { id: 'week', label: 'Week', captures: ['weekStartDate'] },
    { id: 'publication', label: 'Publication', captures: ['status'] },
    { id: 'sortOrder', label: 'Sort Order', captures: ['employeeCount'] },
  ],
  Locations: [
    { id: 'location', label: 'Location', captures: ['locationId', 'locationName'] },
    { id: 'address', label: 'Address', captures: ['address'] },
    { id: 'timezone', label: 'Timezone', captures: ['timezone'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  Positions: [
    { id: 'position', label: 'Position', captures: ['positionId', 'positionName'] },
    { id: 'description', label: 'Description', captures: ['description'] },
    { id: 'color', label: 'Color', captures: ['color'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  Incidents: [
    { id: 'incident', label: 'Incident', captures: ['incidentId', 'incidentTitle'] },
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    { id: 'location', label: 'Location', captures: ['locationId', 'locationName'] },
    { id: 'status', label: 'Status', captures: ['status'] },
    { id: 'severity', label: 'Severity', captures: ['severity'] },
    { id: 'reportedAt', label: 'Reported At', captures: ['reportedAt'] },
  ],
  Holidays: [
    { id: 'holiday', label: 'Holiday', captures: ['holidayId', 'holidayName'] },
    { id: 'date', label: 'Date', captures: ['date'] },
    { id: 'country', label: 'Country', captures: ['country'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  Tasks: [
    { id: 'task', label: 'Task', captures: ['taskId', 'taskTitle'] },
    { id: 'assignee', label: 'Assignee', captures: ['assigneeId', 'assigneeName'] },
    { id: 'dueDate', label: 'Due Date', captures: ['dueDate'] },
    { id: 'priority', label: 'Priority', captures: ['priority'] },
    { id: 'status', label: 'Status', captures: ['status'] },
  ],
  Attendance: [
    { id: 'attendance', label: 'Attendance', captures: ['attendanceId'] },
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    { id: 'date', label: 'Date', captures: ['date'] },
    { id: 'status', label: 'Status', captures: ['status'] },
    { id: 'notes', label: 'Notes', captures: ['notes'] },
  ],
  'Leave Requests': [
    { id: 'leaveRequest', label: 'Leave Request', captures: ['leaveRequestId'] },
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    { id: 'leaveType', label: 'Leave Type', captures: ['leaveType'] },
    { id: 'dateRange', label: 'Date Range', captures: ['startDate', 'endDate'] },
    { id: 'status', label: 'Status', captures: ['status'] },
  ],
  Payroll: [
    { id: 'payrollRun', label: 'Payroll Run', captures: ['payrollId', 'payrollName'] },
    { id: 'employee', label: 'Employee', captures: ['employeeId', 'employeeName'] },
    { id: 'period', label: 'Pay Period', captures: ['periodStart', 'periodEnd'] },
    { id: 'amount', label: 'Amount', captures: ['grossPay', 'netPay'] },
    { id: 'status', label: 'Status', captures: ['status'] },
  ],
  'Training Types': [
    { id: 'trainingType', label: 'Training Type', captures: ['trainingTypeId', 'trainingTypeName'] },
    { id: 'frequency', label: 'Frequency', captures: ['frequency'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  Roles: [
    { id: 'role', label: 'Role', captures: ['roleId', 'roleName'] },
    { id: 'permissions', label: 'Permissions', captures: ['permissionIds', 'permissionNames'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  'Time Stations': [
    { id: 'timeStation', label: 'Time Station', captures: ['timeStationId', 'timeStationName'] },
    { id: 'device', label: 'Device', captures: ['deviceId'] },
    { id: 'location', label: 'Location', captures: ['locationId', 'locationName'] },
    { id: 'status', label: 'Status', captures: ['status', 'isActive'] },
  ],
  'Shift Templates': [
    { id: 'shiftTemplate', label: 'Shift Template', captures: ['shiftTemplateId', 'shiftTemplateName'] },
    { id: 'timeRange', label: 'Time Range', captures: ['startTime', 'endTime'] },
    { id: 'location', label: 'Location', captures: ['locationIds', 'locationNames'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  'Public Holidays': [
    { id: 'holiday', label: 'Holiday', captures: ['holidayId', 'holidayName'] },
    { id: 'date', label: 'Date', captures: ['date'] },
    { id: 'country', label: 'Country', captures: ['country'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
  'Company Settings': [
    { id: 'company', label: 'Company', captures: ['companyId', 'companyName'] },
    { id: 'timezone', label: 'Timezone', captures: ['timeZone'] },
    { id: 'currency', label: 'Currency', captures: ['currency'] },
    { id: 'dateFormat', label: 'Date Format', captures: ['dateFormat'] },
  ],
  'Payroll Departments': [
    { id: 'payrollDepartment', label: 'Payroll Department', captures: ['payrollDepartmentId', 'payrollDepartmentName'] },
    { id: 'code', label: 'Code', captures: ['code'] },
    { id: 'status', label: 'Status', captures: ['isActive'] },
  ],
};

const VALID_SETTINGS_SECTIONS = new Set<string>([
  'locations',
  'positions',
  'schedules',
  'payroll-departments',
  'shift-templates',
  'time-stations',
  'roles',
  'attendance-settings',
  'time-cards',
  'audit-logs',
  'feature-access',
  'nurse-banding',
  'training-types',
  'company-settings',
  'public-holidays',
  'face-sync-status',
  'uploads-migration',
]);

type SettingsSectionId =
  | 'locations'
  | 'positions'
  | 'schedules'
  | 'payroll-departments'
  | 'shift-templates'
  | 'time-stations'
  | 'roles'
  | 'attendance-settings'
  | 'time-cards'
  | 'audit-logs'
  | 'feature-access'
  | 'nurse-banding'
  | 'training-types'
  | 'company-settings'
  | 'public-holidays'
  | 'face-sync-status'
  | 'uploads-migration';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, hasSuperAdminRole, healthcareNurseBandingPayrollEnabled } = usePermissions();
  const { reloadLocations: reloadGlobalLocations } = useLocationContext();
  const toast = useToast();
  const hasFetchedRef = useRef(false);
  const loadedSectionsRef = useRef<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState<SettingsSectionId>(() => {
    const raw = searchParams.get('section')?.trim().toLowerCase();
    if (raw && VALID_SETTINGS_SECTIONS.has(raw)) return raw as SettingsSectionId;
    return 'locations';
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [timeStations, setTimeStations] = useState<TimeStation[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [schedulesSubTab, setSchedulesSubTab] = useState<'schedules' | 'shift-groups'>('schedules');
  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [featureAccessScclEnabled, setFeatureAccessScclEnabled] = useState(false);
  const [featureAccessMandatoryScheduleInputEnabled, setFeatureAccessMandatoryScheduleInputEnabled] = useState(false);
  const [featureAccessHealthcareComplianceEnabled, setFeatureAccessHealthcareComplianceEnabled] = useState(false);
  const [featureAccessHealthcareNurseBandingPayrollEnabled, setFeatureAccessHealthcareNurseBandingPayrollEnabled] = useState(false);
  const [featureAccessMfaEnabled, setFeatureAccessMfaEnabled] = useState(false);
  const [featureAccessMfaAllowAuthenticator, setFeatureAccessMfaAllowAuthenticator] = useState(true);
  const [featureAccessMfaAllowEmail, setFeatureAccessMfaAllowEmail] = useState(false);
  const [featureAccessMfaAllowSms, setFeatureAccessMfaAllowSms] = useState(false);
  const [featureAccessJwtExpiryValue, setFeatureAccessJwtExpiryValue] = useState(14);
  const [featureAccessJwtExpiryUnit, setFeatureAccessJwtExpiryUnit] = useState<'Hours' | 'Days'>('Days');
  const [featureAccessSaving, setFeatureAccessSaving] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [publicHolidayPolicies, setPublicHolidayPolicies] = useState<PublicHolidayPolicy[]>([]);
  const [breakTimeSettings, setBreakTimeSettings] = useState<BreakTimeSettings | null>(null);
  const [systemSettings, setSystemSettings] = useState<SystemSetting[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const shiftTemplateColorMap = useMemo(
    () => buildShiftTemplateColorMap(shiftTemplates),
    [shiftTemplates]
  );
  const [loading, setLoading] = useState(false);
  const [sectionLoading, setSectionLoading] = useState<Record<string, boolean>>(() => {
    const raw = searchParams.get('section')?.trim().toLowerCase();
    const initial =
      raw && VALID_SETTINGS_SECTIONS.has(raw) ? raw : 'locations';
    return { [initial]: true };
  });
  
  // Face Sync Status state
  const [faceSyncStatus, setFaceSyncStatus] = useState<{
    collectionId?: string;
    databaseFaceCount: number;
    rekognitionFaceCount: number;
    isInSync: boolean;
    orphanedFacesCount: number;
    missingFacesCount: number;
    orphanedFaceIds: string[];
    missingFaceIds: string[];
    rekognitionAvailable?: boolean;
    rekognitionErrorMessage?: string;
  } | null>(null);
  const [syncingFaces, setSyncingFaces] = useState(false);

  // Upload migration state (Super Admin only)
  const [uploadsContext, setUploadsContext] = useState<any>(null);
  const [migratingUploads, setMigratingUploads] = useState(false);
  const [uploadsMigrationResult, setUploadsMigrationResult] = useState<any>(null);
  
  // Filter states
  const [locationFilter, setLocationFilter] = useState('active');
  const [positionFilter, setPositionFilter] = useState('active');
  const [timeStationFilter, setTimeStationFilter] = useState('all');
  const [scheduleFilter, setScheduleFilter] = useState('active');
  const [payrollDepartmentFilter, setPayrollDepartmentFilter] = useState('active');
  const [roleFilter, setRoleFilter] = useState('active');
  const [trainingTypeFilter, setTrainingTypeFilter] = useState('active');
  // Search states
  const [locationSearch, setLocationSearch] = useState('');
  const [positionSearch, setPositionSearch] = useState('');
  const [timeStationSearch, setTimeStationSearch] = useState('');
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [payrollDepartmentSearch, setPayrollDepartmentSearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [trainingTypeSearch, setTrainingTypeSearch] = useState('');
  const [shiftTemplateSearch, setShiftTemplateSearch] = useState('');
  const [shiftTemplateFilter, setShiftTemplateFilter] = useState('active');
  
  // Location filter for schedules
  const [scheduleLocationFilter, setScheduleLocationFilter] = useState<number[]>([]);
  
  // Location filter for shift templates
  const [shiftTemplateLocationFilter, setShiftTemplateLocationFilter] = useState<number[]>([]);
  
  // Pagination states
  const [locationPage, setLocationPage] = useState(1);
  const [positionPage, setPositionPage] = useState(1);
  const [timeStationPage, setTimeStationPage] = useState(1);
  const [schedulePage, setSchedulePage] = useState(1);
  const [payrollDepartmentPage, setPayrollDepartmentPage] = useState(1);
  const [rolePage, setRolePage] = useState(1);
  const [trainingTypePage, setTrainingTypePage] = useState(1);
  const [shiftTemplatePage, setShiftTemplatePage] = useState(1);
  const itemsPerPage = 10;
  
  // Location modal state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  
  // Location activation/deactivation confirmation dialog state
  const [locationConfirmDialog, setLocationConfirmDialog] = useState<{
    open: boolean;
    locationId: number | null;
    locationName: string;
    action: 'activate' | 'deactivate' | null;
  }>({
    open: false,
    locationId: null,
    locationName: '',
    action: null,
  });
  const [locationForm, setLocationForm] = useState<CreateLocationRequest>({
    name: '',
    address: '',
    timezone: 'Europe/Dublin',
    latitude: undefined,
    longitude: undefined,
    nightStart: '',
    nightEnd: '',
  });
  
  // Google Maps API Key - should be in environment variables
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  // Position modal state
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [positionForm, setPositionForm] = useState<CreatePositionRequest>({
    title: '',
    description: '',
    color: '#3B82F6'
  });

  // Time Station modal state
  const [showTimeStationModal, setShowTimeStationModal] = useState(false);
  const [editingTimeStation, setEditingTimeStation] = useState<TimeStation | null>(null);
  const [isWifiRestricted, setIsWifiRestricted] = useState(true);
  const [timeStationForm, setTimeStationForm] = useState<CreateTimeStationRequest>({
    name: '',
    deviceId: '',
    description: '',
    ipAddress: '',
    locationId: 1,
    status: 'Active',
    allowPhotoCapture: true,
    locationTracking: false,
    allowedWifis: [{ ssid: '', bssid: '' }],
    allowedIpRanges: [{ ipRange: '' }]
  });

  // Schedule modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [scheduleForm, setScheduleForm] = useState<CreateScheduleRequest>({
    name: '',
    description: '',
    locationIds: []
  });

  // Payroll Department modal state
  const [showPayrollDepartmentModal, setShowPayrollDepartmentModal] = useState(false);
  const [editingPayrollDepartment, setEditingPayrollDepartment] = useState<PayrollDepartment | null>(null);
  const [payrollDepartmentForm, setPayrollDepartmentForm] = useState<CreatePayrollDepartmentRequest>({
    name: '',
    description: '',
    code: '',
    isActive: true
  });

  // Shift Template modal state
  const [showShiftTemplateModal, setShowShiftTemplateModal] = useState(false);
  const [editingShiftTemplate, setEditingShiftTemplate] = useState<ShiftTemplate | null>(null);
  const [breakMinsManuallySet, setBreakMinsManuallySet] = useState(false);
  const [shiftTemplateForm, setShiftTemplateForm] = useState<CreateShiftTemplateRequest>({
    name: '',
    startTime: '',
    endTime: '',
    hours: 0,
    unpaidBreakMins: 0,
    breakTimePaid: false,
    breakUseGlobalSettings: true,
    totalHours: 0,
    color: '#3b82f6',
    displayOption: 'time',
    locationIds: [],
  });

  // Role modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState<CreateRoleRequest>({
    name: '',
    description: '',
    isActive: true,
    displayOrder: 0,
    permissionIds: [],
    mfaRequirement: 'None',
    mfaAllowAuthenticator: false,
    mfaAllowEmail: false,
    mfaAllowSms: false,
    mfaDefaultMethod: null,
  });
  const [tenantMfaEnabled, setTenantMfaEnabled] = useState(false);
  const [tenantMfaAllowAuthenticator, setTenantMfaAllowAuthenticator] = useState(false);
  const [tenantMfaAllowEmail, setTenantMfaAllowEmail] = useState(false);
  const [tenantMfaAllowSms, setTenantMfaAllowSms] = useState(false);

  // Attendance Settings state
  const [isPaid, setIsPaid] = useState(false);
  const [unpaidPunchRequired, setUnpaidPunchRequired] = useState(false);
  const [unpaidForceMinFromRules, setUnpaidForceMinFromRules] = useState(true);
  const [unpaidUseShiftBreakAsFloor, setUnpaidUseShiftBreakAsFloor] = useState(true);
  const [paidPunchRequired, setPaidPunchRequired] = useState(false);
  const [weekStartDay, setWeekStartDay] = useState('monday');
  const [breakTimeRules, setBreakTimeRules] = useState<BreakTimeRule[]>([
    { minHours: 0, maxHours: 4.5, breakMinutes: 0 },
    { minHours: 4.5, maxHours: 6.5, breakMinutes: 15 },
    { minHours: 6.5, maxHours: 10, breakMinutes: 30 },
    { minHours: 10, maxHours: 24, breakMinutes: 45 },
  ]);
  const [savingBreakTime, setSavingBreakTime] = useState(false);

  const applyBreakPolicyFromApi = (breakTimeData: BreakTimeSettings | null | undefined) => {
    setIsPaid(Boolean(breakTimeData?.isPaid));
    setUnpaidPunchRequired(Boolean(breakTimeData?.unpaidPunchRequired));
    setUnpaidForceMinFromRules(breakTimeData?.unpaidForceMinFromRules ?? true);
    setUnpaidUseShiftBreakAsFloor(breakTimeData?.unpaidUseShiftBreakAsFloor ?? true);
    setPaidPunchRequired(Boolean(breakTimeData?.paidPunchRequired));
    if (breakTimeData?.rules && Array.isArray(breakTimeData.rules) && breakTimeData.rules.length > 0) {
      setBreakTimeRules(breakTimeData.rules);
    } else {
      setBreakTimeRules([
        { minHours: 0, maxHours: 4.5, breakMinutes: 0 },
        { minHours: 4.5, maxHours: 6.5, breakMinutes: 15 },
        { minHours: 6.5, maxHours: 10, breakMinutes: 30 },
        { minHours: 10, maxHours: 24, breakMinutes: 45 },
      ]);
    }
  };

  const buildBreakTimeSettingsPayload = (): CreateBreakTimeSettingsRequest => ({
    isPaid: Boolean(isPaid),
    unpaidPunchRequired,
    unpaidForceMinFromRules,
    unpaidUseShiftBreakAsFloor,
    paidPunchRequired,
    rules: breakTimeRules.map(rule => ({
      minHours: rule.minHours,
      maxHours: rule.maxHours,
      breakMinutes: rule.breakMinutes
    })),
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Training Type modal state
  const [showTrainingTypeModal, setShowTrainingTypeModal] = useState(false);
  const [editingTrainingType, setEditingTrainingType] = useState<TrainingType | null>(null);
  const [trainingTypeForm, setTrainingTypeForm] = useState<CreateTrainingTypeRequest>({
    name: '',
    description: '',
    frequency: 'One-Time',
    recurringValue: undefined,
    recurringUnit: undefined,
    isActive: true
  });


  // Time Card Settings state
  const [timeCardSettings, setTimeCardSettings] = useState<TimeCardSettings>({
    timeDisplayFormat: 'punch_times',
    separateSundayHours: false,
    includePositions: true,
    clockInEarlierThan: 'schedule_time',
    clockInLaterThan: 'punch_time',
    clockInLateGraceMinutes: 0,
    clockOutEarlierThan: 'punch_time',
    clockOutLaterThan: 'punch_time'
  });
  const [savingTimeCardSettings, setSavingTimeCardSettings] = useState(false);

  // Audit Log Settings state
  const [auditLogConfigs, setAuditLogConfigs] = useState<AuditLogConfig[]>([]);
  const [auditLogForm, setAuditLogForm] = useState<{
    entities: AuditLogEntity[];
  }>({
    entities: [],
  });

  // Company Settings state
  const [companySettingsForm, setCompanySettingsForm] = useState<CreateCompanySettingsRequest>({
    companyName: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    postZipCode: '',
    country: 'Ireland',
    currency: 'EUR €',
    timeZone: '(GMT+01:00) Dublin',
    dateFormat: 'dd/mm/yyyy',
    endOfDayTime: '00:00',
    nightStart: undefined,
    nightEnd: undefined,
    otRate1: undefined,
    otRate2: undefined,
    ot1RateMultiplier: 1.0,
    ot2RateMultiplier: 1.0,
    sundayRateMultiplier: 1.0,
    bankHolidayRateMultiplier: 1.0,
    nightShiftRateMultiplier: 1.0,
    // Leave management defaults
    leaveYearStartMonth: 1,
    leaveYearStartDay: 1,
    leaveYearType: 'Calendar',
    anniversaryFallbackUsesFiscalBoundary: false,
    carryForwardAllowed: true,
    carryForwardMaxDays: 5,
    carryForwardExpiryMonth: 3,
    carryForwardExpiryDay: 31,
    carryForwardRequiresApproval: true,
    sicknessProtectedCarryForward: true,
    sicknessCarryForwardExpiryMonths: 15,
    accrualsBasedOnHours: false,
    fullTimeStandardHoursPerWeek: 40,
    includeAgencyWorkersInPayroll: false,
    allowMobileClockInOutWithoutFaceVerification: false,
    notificationRetentionDays: 90,
    leavePayReferencePeriodWeeks: 13,
    payrollPhase2PhPremiumMultiplyRecordAndPolicy: false,
    payrollPhase2OtRateIncludesCalendarAndNightPremiums: false,
    payrollPhase2PaidLeaveUseAverageEarnings: false,
    payrollCycleWeeklyEnabled: true,
    payrollCycleFortnightlyEnabled: true,
    payrollCycleHourly2928Enabled: true,
    payrollCycleSalariedMonthlyEnabled: true,
    payrollPhNotEligibleWorkedRateMode: 0,
    payrollUnpaidLeaveSameDayWorkedRule: 0,
    payrollLeaveLegacyCutoverDate: null,
    clearPayrollLeaveLegacyCutoverDate: false,
    payrollUseAdditivePremiums: true,
    sundayPremiumPerHour: 0.5
  });
  const [savingCompanySettings, setSavingCompanySettings] = useState(false);

  // Public Holiday modal state
  const [showPublicHolidayModal, setShowPublicHolidayModal] = useState(false);
  const [editingPublicHoliday, setEditingPublicHoliday] = useState<PublicHoliday | null>(null);
  const [publicHolidayForm, setPublicHolidayForm] = useState<CreatePublicHolidayRequest>({
    name: '',
    date: '',
    year: new Date().getFullYear(),
    country: 'Ireland',
    isActive: true
  });
  const [publicHolidayFilter, setPublicHolidayFilter] = useState('active');
  const [publicHolidaySearch, setPublicHolidaySearch] = useState('');
  const [publicHolidayTab, setPublicHolidayTab] = useState<'holidays' | 'policy'>('holidays');
  
  // Public Holiday Policy modal state
  const [showPublicHolidayPolicyModal, setShowPublicHolidayPolicyModal] = useState(false);
  const [editingPublicHolidayPolicy, setEditingPublicHolidayPolicy] = useState<PublicHolidayPolicy | null>(null);
  const [publicHolidayPolicyForm, setPublicHolidayPolicyForm] = useState<CreatePublicHolidayPolicyRequest>({
    version: 1,
    effectiveFrom: '',
    effectiveTo: null,
    eligibilityRuleType: 'Statutory40Hours',
    requiredHoursLastWeeks: 40,
    weeksLookback: 5,
    defaultEntitlementType: 'PaidOff',
    workOnHolidayEntitlementType: undefined,
    holidayPremiumRate: 1.0,
    dayInLieuExpiryDays: 30,
    dayInLieuAutoCredit: false,
    creditToAnnualLeave: false
  });

  // SignalR connection for real-time timestation updates
  useEffect(() => {
    const getHubUrl = () => {
      const apiBaseUrl = getApiBaseUrl();
      // Convert API URL to SignalR hub URL
      // If API URL ends with /api, replace it with /hubs/timestation
      // Otherwise, append /hubs/timestation
      const hubUrl = apiBaseUrl.endsWith('/api') 
        ? apiBaseUrl.replace('/api', '/hubs/timestation')
        : `${apiBaseUrl.replace(/\/$/, '')}/hubs/timestation`;
      
      return hubUrl;
    };

    const hubUrl = getHubUrl();
    console.log('SignalR: Connecting to hub at:', hubUrl);
    
    // Get auth token if available (same as API calls)
    const getAuthToken = async () => {
      // Try to get token from localStorage or wherever it's stored
      // For now, we'll let SignalR handle it through the API
      return null;
    };
    
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        // Access token will be added automatically if using same domain
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.elapsedMilliseconds < 60000) {
            // If we've been reconnecting for less than 60 seconds, wait 2 seconds before retrying
            return 2000;
          } else {
            // Otherwise, stop reconnecting
            return null;
          }
        }
      })
      .build();

    // Set up event handlers - use inline async function to ensure fresh reference
    connection.on('TimeStationCreated', async (timeStation: TimeStation) => {
      console.log('SignalR: TimeStation created event received', timeStation);
      try {
        console.log('SignalR: Fetching updated timestations list...');
        const timeStationsData = await timeStationApi.getAll();
        console.log('SignalR: Received timestations data, count:', timeStationsData?.length);
        if (timeStationsData && Array.isArray(timeStationsData)) {
          setTimeStations(timeStationsData);
          loadedSectionsRef.current.add('time-stations');
          console.log('SignalR: Timestations list updated successfully');
        } else {
          console.warn('SignalR: Received invalid timestations data:', timeStationsData);
        }
      } catch (error) {
        console.error('Error refreshing timestations after create:', error);
      }
    });

    connection.on('TimeStationUpdated', async (timeStation: TimeStation) => {
      console.log('SignalR: TimeStation updated event received', timeStation);
      try {
        console.log('SignalR: Fetching updated timestations list...');
        const timeStationsData = await timeStationApi.getAll();
        console.log('SignalR: Received timestations data after update, count:', timeStationsData?.length);
        if (timeStationsData && Array.isArray(timeStationsData)) {
          setTimeStations(timeStationsData);
          loadedSectionsRef.current.add('time-stations');
          console.log('SignalR: Timestations list updated successfully');
        } else {
          console.warn('SignalR: Received invalid timestations data:', timeStationsData);
        }
      } catch (error) {
        console.error('Error refreshing timestations after update:', error);
      }
    });

    connection.on('TimeStationDeleted', async (timeStationId: number) => {
      console.log('SignalR: TimeStation deleted event received', timeStationId);
      try {
        console.log('SignalR: Fetching updated timestations list...');
        const timeStationsData = await timeStationApi.getAll();
        console.log('SignalR: Received timestations data after delete, count:', timeStationsData?.length);
        if (timeStationsData && Array.isArray(timeStationsData)) {
          setTimeStations(timeStationsData);
          loadedSectionsRef.current.add('time-stations');
          console.log('SignalR: Timestations list updated successfully');
        } else {
          console.warn('SignalR: Received invalid timestations data:', timeStationsData);
        }
      } catch (error) {
        console.error('Error refreshing timestations after delete:', error);
      }
    });

    // Connection state change handlers for debugging
    connection.onreconnecting((error) => {
      console.log('SignalR: Reconnecting...', error);
    });

    connection.onreconnected((connectionId) => {
      console.log('SignalR: Reconnected with connection ID:', connectionId);
    });

    connection.onclose((error) => {
      console.log('SignalR: Connection closed', error);
    });

    // Start connection
    let connectionStarted = false;
    connection.start()
      .then(() => {
        connectionStarted = true;
        console.log('SignalR connected to TimeStationHub, connection ID:', connection.connectionId);
      })
      .catch((error) => {
        console.error('SignalR connection error:', error);
        console.error('Hub URL:', hubUrl);
        // Stop trying to reconnect if initial connection fails
        connection.stop().catch(() => {});
      });

    // Cleanup on unmount
    return () => {
      if (connectionStarted) {
        connection.stop().catch(() => {});
      }
    };
  }, []);

  // Deep link / stay on section: /settings?section=roles (do not strip — remount must restore it)
  useLayoutEffect(() => {
    const raw = searchParams.get('section')?.trim().toLowerCase();
    if (!raw) return;
    if (!VALID_SETTINGS_SECTIONS.has(raw)) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('section');
          return p;
        },
        { replace: true }
      );
      return;
    }
    const next = raw as SettingsSectionId;
    setActiveSection((prev) => (prev === next ? prev : next));
  }, [searchParams, setSearchParams]);

  // Load data only when a section is selected (skip if already loaded; ignore stale completions)
  useEffect(() => {
    const section = activeSection;
    let cancelled = false;

    const loadSectionData = async () => {
      if (loadedSectionsRef.current.has(section)) {
        setSectionLoading(prev => ({ ...prev, [section]: false }));
        return;
      }

      setSectionLoading(prev => ({ ...prev, [section]: true }));

      try {
        switch (section) {
          case 'locations': {
            const locationsData = await locationApi.getAll();
            if (cancelled) return;
            setLocations(locationsData || []);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'positions': {
            const positionsData = await positionApi.getAll();
            if (cancelled) return;
            setPositions(positionsData || []);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'time-stations': {
            const timeStationsData = await timeStationApi.getAll();
            if (cancelled) return;
            setTimeStations(timeStationsData || []);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'schedules': {
            const schedulesData = await scheduleApi.getAll();
            if (cancelled) return;
            const schedulesWithLocationIds = (schedulesData || []).map((schedule: any) => {
              let locationIds: number[] = [];
              if (schedule.locationIds && Array.isArray(schedule.locationIds)) {
                locationIds = schedule.locationIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
              } else if ((schedule as any).LocationIds && Array.isArray((schedule as any).LocationIds)) {
                locationIds = (schedule as any).LocationIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
              }
              return { ...schedule, locationIds };
            });
            setSchedules(schedulesWithLocationIds);
            const locationsData = await locationApi.getAll();
            if (cancelled) return;
            setLocations(locationsData || []);
            loadedSectionsRef.current.add('locations');
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'shift-templates': {
            const allTemplates = await shiftTemplateApi.getAll(undefined, true);
            if (cancelled) return;
            const templatesWithLocationIds = (allTemplates || []).map(normalizeShiftTemplate);
            setShiftTemplates(templatesWithLocationIds);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'payroll-departments': {
            const payrollDepartmentsData = await payrollDepartmentApi.getAll();
            if (cancelled) return;
            setPayrollDepartments(payrollDepartmentsData || []);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'roles': {
            const [rolesData, permissionsData, rolesCompanySettings] = await Promise.all([
              roleApi.getAll(),
              roleApi.getPermissions(),
              companySettingsApi.get().catch(() => null),
            ]);
            if (cancelled) return;
            setRoles(Array.isArray(rolesData) ? rolesData : []);
            setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
            setTenantMfaEnabled(Boolean(rolesCompanySettings?.mfaEnabled));
            setTenantMfaAllowAuthenticator(Boolean(rolesCompanySettings?.mfaAllowAuthenticator));
            setTenantMfaAllowEmail(Boolean(rolesCompanySettings?.mfaAllowEmail));
            setTenantMfaAllowSms(Boolean(rolesCompanySettings?.mfaAllowSms));
            if (rolesCompanySettings) {
              setCompanySettings(rolesCompanySettings);
            }
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'training-types': {
            const trainingTypesData = await trainingTypeApi.getAll().catch(() => []);
            if (cancelled) return;
            setTrainingTypes(trainingTypesData || []);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'company-settings': {
            const companySettingsData = await companySettingsApi.get().catch(() => null);
            if (cancelled) return;
            setCompanySettings(companySettingsData);
            if (companySettingsData) {
              setCompanySettingsForm({
                companyName: companySettingsData.companyName || '',
                addressLine1: companySettingsData.addressLine1 || '',
                addressLine2: companySettingsData.addressLine2 || '',
                addressLine3: companySettingsData.addressLine3 || '',
                postZipCode: companySettingsData.postZipCode || '',
                country: companySettingsData.country || 'Ireland',
                currency: companySettingsData.currency || 'EUR €',
                timeZone: companySettingsData.timeZone || '(GMT+01:00) Dublin',
                dateFormat: companySettingsData.dateFormat || 'dd/mm/yyyy',
                endOfDayTime: companySettingsData.endOfDayTime || '00:00',
                nightStart: companySettingsData.nightStart,
                nightEnd: companySettingsData.nightEnd,
                otRate1: companySettingsData.otRate1,
                otRate2: companySettingsData.otRate2,
                ot1RateMultiplier: companySettingsData.ot1RateMultiplier ?? 1.0,
                ot2RateMultiplier: companySettingsData.ot2RateMultiplier ?? 1.0,
                sundayRateMultiplier: companySettingsData.sundayRateMultiplier ?? 1.0,
                bankHolidayRateMultiplier: companySettingsData.bankHolidayRateMultiplier ?? 1.0,
                nightShiftRateMultiplier: companySettingsData.nightShiftRateMultiplier ?? 1.0,
                leaveYearStartMonth: companySettingsData.leaveYearStartMonth ?? 1,
                leaveYearStartDay: companySettingsData.leaveYearStartDay ?? 1,
                leaveYearType: companySettingsData.leaveYearType || 'Calendar',
                anniversaryFallbackUsesFiscalBoundary: companySettingsData.anniversaryFallbackUsesFiscalBoundary ?? false,
                carryForwardAllowed: companySettingsData.carryForwardAllowed ?? true,
                carryForwardMaxDays: companySettingsData.carryForwardMaxDays ?? 5,
                carryForwardExpiryMonth: companySettingsData.carryForwardExpiryMonth ?? 3,
                carryForwardExpiryDay: companySettingsData.carryForwardExpiryDay ?? 31,
                carryForwardRequiresApproval: companySettingsData.carryForwardRequiresApproval ?? true,
                sicknessProtectedCarryForward: companySettingsData.sicknessProtectedCarryForward ?? true,
                sicknessCarryForwardExpiryMonths: companySettingsData.sicknessCarryForwardExpiryMonths ?? 15,
                accrualsBasedOnHours: companySettingsData.accrualsBasedOnHours ?? false,

                fullTimeStandardHoursPerWeek: companySettingsData.fullTimeStandardHoursPerWeek ?? 40,

                includeAgencyWorkersInPayroll: companySettingsData.includeAgencyWorkersInPayroll ?? false,

                allowMobileClockInOutWithoutFaceVerification: companySettingsData.allowMobileClockInOutWithoutFaceVerification ?? false,

                notificationRetentionDays: companySettingsData.notificationRetentionDays ?? 90,

                leavePayReferencePeriodWeeks: companySettingsData.leavePayReferencePeriodWeeks ?? 13,

                payrollLeaveLegacyCutoverDate: companySettingsData.payrollLeaveLegacyCutoverDate ?? null,

                clearPayrollLeaveLegacyCutoverDate: false,
                payrollPhase2PhPremiumMultiplyRecordAndPolicy: companySettingsData.payrollPhase2PhPremiumMultiplyRecordAndPolicy ?? false,
                payrollPhase2OtRateIncludesCalendarAndNightPremiums: companySettingsData.payrollPhase2OtRateIncludesCalendarAndNightPremiums ?? false,
                payrollPhase2PaidLeaveUseAverageEarnings: companySettingsData.payrollPhase2PaidLeaveUseAverageEarnings ?? false,
                payrollCycleWeeklyEnabled: companySettingsData.payrollCycleWeeklyEnabled ?? true,
                payrollCycleFortnightlyEnabled: companySettingsData.payrollCycleFortnightlyEnabled ?? true,
                payrollCycleHourly2928Enabled: companySettingsData.payrollCycleHourly2928Enabled ?? true,
                payrollCycleSalariedMonthlyEnabled: companySettingsData.payrollCycleSalariedMonthlyEnabled ?? true,
                payrollPhNotEligibleWorkedRateMode: companySettingsData.payrollPhNotEligibleWorkedRateMode ?? 0,
                payrollUnpaidLeaveSameDayWorkedRule: companySettingsData.payrollUnpaidLeaveSameDayWorkedRule ?? 0,
                payrollUseAdditivePremiums: companySettingsData.payrollUseAdditivePremiums ?? true,
                sundayPremiumPerHour: companySettingsData.sundayPremiumPerHour ?? 0.5
              });
            }
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'public-holidays': {
            const publicHolidaysData = await publicHolidayApi.getAll().catch(() => []);
            if (cancelled) return;
            setPublicHolidays(publicHolidaysData || []);
            const policiesData = await publicHolidayPolicyApi.getAll().catch(() => []);
            if (cancelled) return;
            setPublicHolidayPolicies(policiesData || []);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'attendance-settings': {
            const breakTimeData = await breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] }));
            if (cancelled) return;
            setBreakTimeSettings(breakTimeData || { id: undefined, isPaid: false, rules: [] });
            applyBreakPolicyFromApi(breakTimeData);
            loadedSectionsRef.current.add(section);
            break;
          }

          case 'time-cards': {
            const systemSettingsData = await systemSettingsApi.getAll().catch(() => []);
            if (cancelled) return;
            setSystemSettings(systemSettingsData || []);
            if (systemSettingsData && systemSettingsData.length > 0) {
              const timeDisplayFormat = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_display_format')?.value || 'punch_times';
              const separateSundayHours = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_separate_sunday')?.value === 'true';
              const includePositions = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_include_positions')?.value === 'true';
              const clockInEarlierThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_early')?.value || 'schedule_time';
              const clockInLaterThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_late')?.value || 'punch_time';
              const clockInLateGraceMinutes = parseInt(systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_late_grace_minutes')?.value || '0', 10) || 0;
              const clockOutEarlierThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_out_early')?.value || 'punch_time';
              const clockOutLaterThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_out_late')?.value || 'punch_time';

              setTimeCardSettings({
                timeDisplayFormat,
                separateSundayHours,
                includePositions,
                clockInEarlierThan,
                clockInLaterThan,
                clockInLateGraceMinutes,
                clockOutEarlierThan,
                clockOutLaterThan
              });
            }
            loadedSectionsRef.current.add(section);
            break;
          }
          case 'audit-logs': {
            const auditConfigs = await auditLogConfigApi.getAll().catch(() => []);
            if (cancelled) return;
            const mappedConfigs = (auditConfigs || []).map((config) => ({
              id: String(config.id),
              entity: config.entity as AuditLogEntity,
              actions: (config.actions || []) as AuditLogAction[],
              fields: config.fields || [],
              isActive: config.isActive,
            }));
            setAuditLogConfigs(mappedConfigs);
            setAuditLogForm({
              entities: mappedConfigs.map((config) => config.entity),
            });
            loadedSectionsRef.current.add(section);
            break;
          }
          case 'feature-access': {
            const faSettings = await companySettingsApi.get().catch(() => null);
            if (cancelled) return;
            setFeatureAccessScclEnabled(Boolean(faSettings?.securityCommandControlLogEnabled));
            setFeatureAccessMandatoryScheduleInputEnabled(Boolean(faSettings?.mandatoryScheduleInputEnabled));
            setFeatureAccessHealthcareComplianceEnabled(Boolean(faSettings?.healthcareComplianceEnabled));
            setFeatureAccessHealthcareNurseBandingPayrollEnabled(Boolean(faSettings?.healthcareNurseBandingPayrollEnabled));
            setFeatureAccessMfaEnabled(Boolean(faSettings?.mfaEnabled));
            setFeatureAccessMfaAllowAuthenticator(faSettings?.mfaAllowAuthenticator ?? true);
            setFeatureAccessMfaAllowEmail(Boolean(faSettings?.mfaAllowEmail));
            setFeatureAccessMfaAllowSms(Boolean(faSettings?.mfaAllowSms));
            setFeatureAccessJwtExpiryValue(faSettings?.jwtExpiryValue ?? 14);
            setFeatureAccessJwtExpiryUnit(faSettings?.jwtExpiryUnit === 'Hours' ? 'Hours' : 'Days');
            if (faSettings) {
              setCompanySettings(faSettings);
              setTenantMfaEnabled(Boolean(faSettings.mfaEnabled));
              setTenantMfaAllowAuthenticator(Boolean(faSettings.mfaAllowAuthenticator));
              setTenantMfaAllowEmail(Boolean(faSettings.mfaAllowEmail));
              setTenantMfaAllowSms(Boolean(faSettings.mfaAllowSms));
            }
            loadedSectionsRef.current.add(section);
            break;
          }
          case 'face-sync-status': {
            const syncStatusData = await faceApi.getSyncStatus().catch(() => null);
            if (cancelled) return;
            setFaceSyncStatus(syncStatusData);
            loadedSectionsRef.current.add(section);
            break;
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error(`Error loading ${section} data:`, error);
        }
      } finally {
        if (!cancelled) {
          setSectionLoading(prev => ({ ...prev, [section]: false }));
        }
      }
    };

    loadSectionData();
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  // Helper functions to refresh specific sections
  const refreshLocations = async () => {
    const locationsData = await locationApi.fetchAllFresh();
    setLocations(locationsData);
    loadedSectionsRef.current.add('locations'); // Mark as loaded
    await reloadGlobalLocations();
  };

  const refreshPositions = async () => {
    const positionsData = await positionApi.getAll();
    setPositions(positionsData || []);
    loadedSectionsRef.current.add('positions');
  };

  const refreshTimeStations = async () => {
    const timeStationsData = await timeStationApi.getAll();
    setTimeStations(timeStationsData || []);
    loadedSectionsRef.current.add('time-stations');
  };

  const refreshSchedules = async () => {
    const schedulesData = await scheduleApi.getAll();
    const schedulesWithLocationIds = (schedulesData || []).map((schedule: any) => {
      let locationIds: number[] = [];
      if (schedule.locationIds && Array.isArray(schedule.locationIds)) {
        locationIds = schedule.locationIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
      } else if ((schedule as any).LocationIds && Array.isArray((schedule as any).LocationIds)) {
        locationIds = (schedule as any).LocationIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
      }
      return { ...schedule, locationIds };
    });
    setSchedules(schedulesWithLocationIds);
    loadedSectionsRef.current.add('schedules');
  };

  const refreshPayrollDepartments = async () => {
    const payrollDepartmentsData = await payrollDepartmentApi.getAll();
    setPayrollDepartments(payrollDepartmentsData || []);
    loadedSectionsRef.current.add('payroll-departments');
  };

  const normalizeShiftTemplate = (template: any): ShiftTemplate => {
    let locationIds: number[] = [];
    if (template.locationIds && Array.isArray(template.locationIds)) {
      locationIds = template.locationIds
        .map((id: any) => Number(id))
        .filter((id: number) => !isNaN(id));
    }
    const isActive =
      typeof template.isActive === 'boolean'
        ? template.isActive
        : typeof template.IsActive === 'boolean'
          ? template.IsActive
          : true;
    return { ...template, locationIds, isActive };
  };

  const refreshShiftTemplates = async () => {
    const allTemplates = await shiftTemplateApi.getAll(undefined, true);
    const templatesWithLocationIds = (allTemplates || []).map(normalizeShiftTemplate);
    setShiftTemplates(templatesWithLocationIds);
    loadedSectionsRef.current.add('shift-templates');
  };

  const refreshRoles = async () => {
    const [rolesData, permissionsData] = await Promise.all([
      roleApi.fetchAllFresh(),
      roleApi.getPermissions()
    ]);
    setRoles(Array.isArray(rolesData) ? rolesData : []);
    setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
    loadedSectionsRef.current.add('roles');
  };

  const refreshTrainingTypes = async () => {
    const trainingTypesData = await trainingTypeApi.getAll().catch(() => []);
    setTrainingTypes(trainingTypesData || []);
    loadedSectionsRef.current.add('training-types');
  };


  const refreshCompanySettings = async () => {
    const companySettingsData = await companySettingsApi.get().catch(() => null);
    setCompanySettings(companySettingsData);
    if (companySettingsData) {
      setCompanySettingsForm({
        companyName: companySettingsData.companyName || '',
        addressLine1: companySettingsData.addressLine1 || '',
        addressLine2: companySettingsData.addressLine2 || '',
        addressLine3: companySettingsData.addressLine3 || '',
        postZipCode: companySettingsData.postZipCode || '',
        country: companySettingsData.country || 'Ireland',
        currency: companySettingsData.currency || 'EUR €',
        timeZone: companySettingsData.timeZone || '(GMT+01:00) Dublin',
        dateFormat: companySettingsData.dateFormat || 'dd/mm/yyyy',
        endOfDayTime: companySettingsData.endOfDayTime || '00:00',
        nightStart: companySettingsData.nightStart,
        nightEnd: companySettingsData.nightEnd,
        otRate1: companySettingsData.otRate1,
        otRate2: companySettingsData.otRate2,
        ot1RateMultiplier: companySettingsData.ot1RateMultiplier ?? 1.0,
        ot2RateMultiplier: companySettingsData.ot2RateMultiplier ?? 1.0,
        sundayRateMultiplier: companySettingsData.sundayRateMultiplier ?? 1.0,
        bankHolidayRateMultiplier: companySettingsData.bankHolidayRateMultiplier ?? 1.0,
        nightShiftRateMultiplier: companySettingsData.nightShiftRateMultiplier ?? 1.0,
        leaveYearStartMonth: companySettingsData.leaveYearStartMonth ?? 1,
        leaveYearStartDay: companySettingsData.leaveYearStartDay ?? 1,
        leaveYearType: companySettingsData.leaveYearType || 'Calendar',
        anniversaryFallbackUsesFiscalBoundary: companySettingsData.anniversaryFallbackUsesFiscalBoundary ?? false,
        carryForwardAllowed: companySettingsData.carryForwardAllowed ?? true,
        carryForwardMaxDays: companySettingsData.carryForwardMaxDays ?? 5,
        carryForwardExpiryMonth: companySettingsData.carryForwardExpiryMonth ?? 3,
        carryForwardExpiryDay: companySettingsData.carryForwardExpiryDay ?? 31,
        carryForwardRequiresApproval: companySettingsData.carryForwardRequiresApproval ?? true,
        sicknessProtectedCarryForward: companySettingsData.sicknessProtectedCarryForward ?? true,
        sicknessCarryForwardExpiryMonths: companySettingsData.sicknessCarryForwardExpiryMonths ?? 15,
        accrualsBasedOnHours: companySettingsData.accrualsBasedOnHours ?? false,

        fullTimeStandardHoursPerWeek: companySettingsData.fullTimeStandardHoursPerWeek ?? 40,

        includeAgencyWorkersInPayroll: companySettingsData.includeAgencyWorkersInPayroll ?? false,

        allowMobileClockInOutWithoutFaceVerification: companySettingsData.allowMobileClockInOutWithoutFaceVerification ?? false,

        notificationRetentionDays: companySettingsData.notificationRetentionDays ?? 90,

        leavePayReferencePeriodWeeks: companySettingsData.leavePayReferencePeriodWeeks ?? 13,

        payrollLeaveLegacyCutoverDate: companySettingsData.payrollLeaveLegacyCutoverDate ?? null,

        clearPayrollLeaveLegacyCutoverDate: false,
        payrollPhase2PhPremiumMultiplyRecordAndPolicy: companySettingsData.payrollPhase2PhPremiumMultiplyRecordAndPolicy ?? false,
        payrollPhase2OtRateIncludesCalendarAndNightPremiums: companySettingsData.payrollPhase2OtRateIncludesCalendarAndNightPremiums ?? false,
        payrollPhase2PaidLeaveUseAverageEarnings: companySettingsData.payrollPhase2PaidLeaveUseAverageEarnings ?? false,
        payrollCycleWeeklyEnabled: companySettingsData.payrollCycleWeeklyEnabled ?? true,
        payrollCycleFortnightlyEnabled: companySettingsData.payrollCycleFortnightlyEnabled ?? true,
        payrollCycleHourly2928Enabled: companySettingsData.payrollCycleHourly2928Enabled ?? true,
        payrollCycleSalariedMonthlyEnabled: companySettingsData.payrollCycleSalariedMonthlyEnabled ?? true,
        payrollPhNotEligibleWorkedRateMode: companySettingsData.payrollPhNotEligibleWorkedRateMode ?? 0,
        payrollUnpaidLeaveSameDayWorkedRule: companySettingsData.payrollUnpaidLeaveSameDayWorkedRule ?? 0,
        payrollUseAdditivePremiums: companySettingsData.payrollUseAdditivePremiums ?? true,
        sundayPremiumPerHour: companySettingsData.sundayPremiumPerHour ?? 0.5
      });
    }
    loadedSectionsRef.current.add('company-settings');
  };

  const refreshPublicHolidays = async () => {
    const publicHolidaysData = await publicHolidayApi.getAll().catch(() => []);
    setPublicHolidays(publicHolidaysData || []);
  };

  const refreshPublicHolidayPolicies = async () => {
    const policiesData = await publicHolidayPolicyApi.getAll().catch(() => []);
    setPublicHolidayPolicies(policiesData || []);
    loadedSectionsRef.current.add('public-holidays');
  };

  const refreshFaceSyncStatus = async () => {
    const syncStatusData = await faceApi.getSyncStatus().catch(() => null);
    setFaceSyncStatus(syncStatusData);
    loadedSectionsRef.current.add('face-sync-status');
  };

  const handleSyncFaces = async () => {
    if (!window.confirm('This will delete orphaned faces from Rekognition and clear missing FaceIds from the database. Continue?')) {
      return;
    }

    try {
      setSyncingFaces(true);
      const result = await faceApi.syncFaces();
      alert(`Sync completed!\n\n${result.message}\n\nDeleted ${result.orphanedFacesDeleted} orphaned faces.\nCleared ${result.missingFacesCleared} missing FaceIds.`);
      // Refresh the status after sync
      await refreshFaceSyncStatus();
    } catch (error: any) {
      console.error('Error syncing faces:', error);
      alert(`Error syncing faces: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setSyncingFaces(false);
    }
  };

  const refreshAttendanceSettings = async () => {
    const breakTimeData = await breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] }));
    setBreakTimeSettings(breakTimeData || { id: undefined, isPaid: false, rules: [] });
    applyBreakPolicyFromApi(breakTimeData);
    loadedSectionsRef.current.add('attendance-settings');
  };

  const refreshTimeCardSettings = async () => {
    const systemSettingsData = await systemSettingsApi.getAll().catch(() => []);
    setSystemSettings(systemSettingsData || []);
    if (systemSettingsData && systemSettingsData.length > 0) {
      const timeDisplayFormat = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_display_format')?.value || 'punch_times';
      const separateSundayHours = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_separate_sunday')?.value === 'true';
      const includePositions = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_include_positions')?.value === 'true';
      const clockInEarlierThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_early')?.value || 'schedule_time';
      const clockInLaterThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_late')?.value || 'punch_time';
      const clockInLateGraceMinutes = parseInt(systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_late_grace_minutes')?.value || '0', 10) || 0;
      const clockOutEarlierThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_out_early')?.value || 'punch_time';
      const clockOutLaterThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_out_late')?.value || 'punch_time';
      
      setTimeCardSettings({
        timeDisplayFormat,
        separateSundayHours,
        includePositions,
        clockInEarlierThan,
        clockInLaterThan,
        clockInLateGraceMinutes,
        clockOutEarlierThan,
        clockOutLaterThan
      });
    }
    loadedSectionsRef.current.add('time-cards');
  };

  // Legacy fetchData function - kept for backward compatibility but should be replaced
  const fetchData = async () => {
    try {
      const [locationsData, positionsData, timeStationsData, schedulesData, rolesData, permissionsData, trainingTypesData, companySettingsData, publicHolidaysData, breakTimeData, systemSettingsData] = await Promise.all([
        locationApi.getAll(),
        positionApi.getAll(),
        timeStationApi.getAll(),
        scheduleApi.getAll(),
        roleApi.getAll(),
        roleApi.getPermissions(),
        trainingTypeApi.getAll().catch(() => []),
        companySettingsApi.get().catch(() => null),
        publicHolidayApi.getAll().catch(() => []),
        breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] })),
        systemSettingsApi.getAll().catch(() => [])
      ]);
      
      setLocations(locationsData || []);
      setPositions(positionsData || []);
      setTimeStations(timeStationsData || []);
      // Ensure locationIds is parsed correctly for each schedule
      const schedulesWithLocationIds = (schedulesData || []).map(schedule => {
        // Parse locationIds and ensure it's an array of numbers
        let locationIds: number[] = [];
        if (schedule.locationIds && Array.isArray(schedule.locationIds)) {
          locationIds = schedule.locationIds.map(id => Number(id)).filter(id => !isNaN(id));
        }
        // Fallback for PascalCase (shouldn't happen now, but keeping for safety)
        else if ((schedule as any).LocationIds && Array.isArray((schedule as any).LocationIds)) {
          locationIds = (schedule as any).LocationIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
        }
        
        return {
          ...schedule,
          locationIds: locationIds
        };
      });
      console.log('Fetched schedules with locationIds:', schedulesWithLocationIds);
      schedulesWithLocationIds.forEach((s, idx) => {
        console.log(`Schedule ${idx + 1} (${s.name}): locationIds =`, s.locationIds, 'type:', typeof s.locationIds, 'isArray:', Array.isArray(s.locationIds));
      });
      setSchedules(schedulesWithLocationIds);
      setRoles(rolesData || []);
      setPermissions(permissionsData || []);
      setTrainingTypes(trainingTypesData || []);
      setCompanySettings(companySettingsData || null);
      setPublicHolidays(publicHolidaysData || []);
      setBreakTimeSettings(breakTimeData || { id: undefined, isPaid: false, rules: [] });
      setSystemSettings(systemSettingsData || []);
      
      // Fetch all shift templates in a single call (without locationId to get all)
      try {
        const allTemplates = await shiftTemplateApi.getAll(undefined, true);
        setShiftTemplates((allTemplates || []).map(normalizeShiftTemplate));
      } catch (error) {
        console.error('Error fetching shift templates:', error);
        setShiftTemplates([]);
      }
      
      // Load company settings form
      if (companySettingsData) {
        setCompanySettingsForm({
          companyName: companySettingsData.companyName || '',
          addressLine1: companySettingsData.addressLine1 || '',
          addressLine2: companySettingsData.addressLine2 || '',
          addressLine3: companySettingsData.addressLine3 || '',
          postZipCode: companySettingsData.postZipCode || '',
          country: companySettingsData.country || 'Ireland',
          currency: companySettingsData.currency || 'EUR €',
          timeZone: companySettingsData.timeZone || '(GMT+01:00) Dublin',
          dateFormat: companySettingsData.dateFormat || 'dd/mm/yyyy',
          endOfDayTime: companySettingsData.endOfDayTime || '00:00',
          otRate1: companySettingsData.otRate1,
          otRate2: companySettingsData.otRate2,
          leaveYearStartMonth: companySettingsData.leaveYearStartMonth ?? 1,
          leaveYearStartDay: companySettingsData.leaveYearStartDay ?? 1,
          leaveYearType: companySettingsData.leaveYearType || 'Calendar',
          anniversaryFallbackUsesFiscalBoundary: companySettingsData.anniversaryFallbackUsesFiscalBoundary ?? false,
          carryForwardAllowed: companySettingsData.carryForwardAllowed ?? true,
          carryForwardMaxDays: companySettingsData.carryForwardMaxDays ?? 5,
          carryForwardExpiryMonth: companySettingsData.carryForwardExpiryMonth ?? 3,
          carryForwardExpiryDay: companySettingsData.carryForwardExpiryDay ?? 31,
          carryForwardRequiresApproval: companySettingsData.carryForwardRequiresApproval ?? true,
          sicknessProtectedCarryForward: companySettingsData.sicknessProtectedCarryForward ?? true,
          sicknessCarryForwardExpiryMonths: companySettingsData.sicknessCarryForwardExpiryMonths ?? 15,
          accrualsBasedOnHours: companySettingsData.accrualsBasedOnHours ?? false,

          fullTimeStandardHoursPerWeek: companySettingsData.fullTimeStandardHoursPerWeek ?? 40,

          includeAgencyWorkersInPayroll: companySettingsData.includeAgencyWorkersInPayroll ?? false,

          allowMobileClockInOutWithoutFaceVerification: companySettingsData.allowMobileClockInOutWithoutFaceVerification ?? false,

          notificationRetentionDays: companySettingsData.notificationRetentionDays ?? 90,

          leavePayReferencePeriodWeeks: companySettingsData.leavePayReferencePeriodWeeks ?? 13,

          payrollLeaveLegacyCutoverDate: companySettingsData.payrollLeaveLegacyCutoverDate ?? null,

          clearPayrollLeaveLegacyCutoverDate: false,
          payrollPhase2PhPremiumMultiplyRecordAndPolicy: companySettingsData.payrollPhase2PhPremiumMultiplyRecordAndPolicy ?? false,
          payrollPhase2OtRateIncludesCalendarAndNightPremiums: companySettingsData.payrollPhase2OtRateIncludesCalendarAndNightPremiums ?? false,
          payrollPhase2PaidLeaveUseAverageEarnings: companySettingsData.payrollPhase2PaidLeaveUseAverageEarnings ?? false,
          payrollCycleWeeklyEnabled: companySettingsData.payrollCycleWeeklyEnabled ?? true,
          payrollCycleFortnightlyEnabled: companySettingsData.payrollCycleFortnightlyEnabled ?? true,
          payrollCycleHourly2928Enabled: companySettingsData.payrollCycleHourly2928Enabled ?? true,
          payrollCycleSalariedMonthlyEnabled: companySettingsData.payrollCycleSalariedMonthlyEnabled ?? true,
          payrollPhNotEligibleWorkedRateMode: companySettingsData.payrollPhNotEligibleWorkedRateMode ?? 0,
          payrollUnpaidLeaveSameDayWorkedRule: companySettingsData.payrollUnpaidLeaveSameDayWorkedRule ?? 0,
          payrollUseAdditivePremiums: companySettingsData.payrollUseAdditivePremiums ?? true,
          sundayPremiumPerHour: companySettingsData.sundayPremiumPerHour ?? 0.5
        });
      }
      
      // Load time card settings from system settings
      if (systemSettingsData && systemSettingsData.length > 0) {
        const timeDisplayFormat = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_display_format')?.value || 'punch_times';
        const separateSundayHours = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_separate_sunday')?.value === 'true';
        const includePositions = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_include_positions')?.value === 'true';
        const clockInEarlierThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_early')?.value || 'schedule_time';
        const clockInLaterThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_late')?.value || 'punch_time';
        const clockInLateGraceMinutes = parseInt(systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_in_late_grace_minutes')?.value || '0', 10) || 0;
        const clockOutEarlierThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_out_early')?.value || 'punch_time';
        const clockOutLaterThan = systemSettingsData.find((s: SystemSetting) => s.key === 'timecard_clock_out_late')?.value || 'punch_time';
        
        setTimeCardSettings({
          timeDisplayFormat,
          separateSundayHours,
          includePositions,
          clockInEarlierThan,
          clockInLaterThan,
          clockInLateGraceMinutes,
          clockOutEarlierThan,
          clockOutLaterThan
        });
      }
      
      // Fetch shift templates for all active locations
      await refreshShiftTemplates();
      
      applyBreakPolicyFromApi(breakTimeData);
      
      // Load week start day
      const weekStartSetting = (systemSettingsData || []).find((s: SystemSetting) => s.key === 'week_start_day');
      if (weekStartSetting) {
        setWeekStartDay(weekStartSetting.value);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set empty arrays on error to prevent undefined issues
      setLocations([]);
      setPositions([]);
      setTimeStations([]);
      setSchedules([]);
      setRoles([]);
      setPermissions([]);
      setTrainingTypes([]);
      setCompanySettings(null);
      setPublicHolidays([]);
      setBreakTimeSettings({ id: undefined, isPaid: false, rules: [] });
      setSystemSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshAuditLogConfigs = async () => {
    const auditConfigs = await auditLogConfigApi.getAll().catch(() => []);
    const mappedConfigs = (auditConfigs || []).map((config) => ({
      id: String(config.id),
      entity: config.entity as AuditLogEntity,
      actions: (config.actions || []) as AuditLogAction[],
      fields: config.fields || [],
      isActive: config.isActive,
    }));
    setAuditLogConfigs(mappedConfigs);
    setAuditLogForm({
      entities: mappedConfigs.map((config) => config.entity),
    });
    loadedSectionsRef.current.add('audit-logs');
  };

  // Location handlers
  const handleAddLocation = () => {
    setEditingLocation(null);
    setLocationForm({ 
      name: '', 
      address: '', 
      timezone: 'Europe/Dublin',
      latitude: undefined,
      longitude: undefined,
      nightStart: '',
      nightEnd: '',
    });
    setShowLocationModal(true);
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name,
      address: location.address,
      timezone: location.timezone,
      latitude: location.latitude,
      longitude: location.longitude,
      nightStart: location.nightStart ?? '',
      nightEnd: location.nightEnd ?? '',
    });
    setShowLocationModal(true);
  };

  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await locationApi.update(editingLocation.id, locationForm, editingLocation.isActive);
      } else {
        await locationApi.create({
          ...locationForm,
          nightStart: locationForm.nightStart?.trim() || undefined,
          nightEnd: locationForm.nightEnd?.trim() || undefined,
        });
      }
      setShowLocationModal(false);
      await refreshLocations();
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const handleDeleteLocation = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }
    try {
      await locationApi.delete(id);
      const locationsData = await locationApi.fetchAllFresh();
      setLocations(locationsData);
      loadedSectionsRef.current.add('locations');
      await reloadGlobalLocations();
      toast.success('Location deleted successfully');
    } catch (error: any) {
      console.error('Error deleting location:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        'Failed to delete location';
      toast.error(typeof message === 'string' ? message : 'Failed to delete location');
    }
  };

  const handleDeactivateLocation = (id: number) => {
    const location = locations.find(l => l.id === id);
    setLocationConfirmDialog({
      open: true,
      locationId: id,
      locationName: location?.name || 'this location',
      action: 'deactivate',
    });
  };

  const handleActivateLocation = (id: number) => {
    const location = locations.find(l => l.id === id);
    setLocationConfirmDialog({
      open: true,
      locationId: id,
      locationName: location?.name || 'this location',
      action: 'activate',
    });
  };

  const handleConfirmLocationAction = async () => {
    if (!locationConfirmDialog.locationId || !locationConfirmDialog.action) return;

    try {
      if (locationConfirmDialog.action === 'activate') {
        await locationApi.activate(locationConfirmDialog.locationId);
        toast.success(`Location "${locationConfirmDialog.locationName}" has been activated successfully`);
      } else {
        await locationApi.deactivate(locationConfirmDialog.locationId);
        toast.success(`Location "${locationConfirmDialog.locationName}" has been deactivated successfully`);
      }
      await refreshLocations();
      setLocationConfirmDialog({ open: false, locationId: null, locationName: '', action: null });
    } catch (error) {
      console.error(`Error ${locationConfirmDialog.action === 'activate' ? 'activating' : 'deactivating'} location:`, error);
      toast.error(`Failed to ${locationConfirmDialog.action === 'activate' ? 'activate' : 'deactivate'} location`);
    }
  };

  // Position handlers
  const handleAddPosition = () => {
    setEditingPosition(null);
    setPositionForm({ title: '', description: '', color: '#3B82F6' });
    setShowPositionModal(true);
  };

  const handleEditPosition = (position: Position) => {
    setEditingPosition(position);
    setPositionForm({
      title: position.title,
      description: position.description || '',
      color: position.color
    });
    setShowPositionModal(true);
  };

  const handlePositionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPosition) {
        await positionApi.update(editingPosition.id, positionForm);
      } else {
        await positionApi.create(positionForm);
      }
      setShowPositionModal(false);
      await refreshPositions();
    } catch (error) {
      console.error('Error saving position:', error);
    }
  };

  const handleDeletePosition = async (id: number) => {
    if (window.window.confirm('Are you sure you want to delete this position?')) {
      try {
        await positionApi.delete(id);
        await refreshPositions();
      } catch (error) {
        console.error('Error deleting position:', error);
      }
    }
  };

  const handleDeactivatePosition = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this position?')) {
      try {
        await positionApi.deactivate(id);
        await refreshPositions();
      } catch (error) {
        console.error('Error deactivating position:', error);
      }
    }
  };

  const handleActivatePosition = async (id: number) => {
    try {
      await positionApi.activate(id);
      await refreshPositions();
    } catch (error) {
      console.error('Error activating position:', error);
    }
  };

  // Time Station handlers
  const handleAddTimeStation = () => {
    setEditingTimeStation(null);
    setIsWifiRestricted(true);
    setTimeStationForm({
      name: '',
      deviceId: '',
      description: '',
      ipAddress: '',
      locationId: locations.length > 0 ? locations[0].id : 1,
      status: 'Active',
      allowPhotoCapture: true,
      locationTracking: false,
      allowPinClockIn: false,
      allowedWifis: [{ ssid: '', bssid: '' }],
      allowedIpRanges: [{ ipRange: '' }]
    });
    setShowTimeStationModal(true);
  };

  const handleEditTimeStation = (timeStation: TimeStation) => {
    setEditingTimeStation(timeStation);
    // Determine status: use status field if available, otherwise derive from isActive
    const status = timeStation.status || (timeStation.isActive ? 'Active' : 'Inactive');
    // Determine if WiFi is restricted based on whether allowedWifis exist and have values
    const hasWifiRestrictions = timeStation.allowedWifis && timeStation.allowedWifis.length > 0 && 
      timeStation.allowedWifis.some(w => w.ssid && w.ssid.trim());
    setIsWifiRestricted(hasWifiRestrictions !== false);
    setTimeStationForm({
      name: timeStation.name,
      deviceId: timeStation.deviceId,
      description: timeStation.description || '',
      ipAddress: timeStation.ipAddress || '',
      locationId: timeStation.locationId,
      status: status,
      allowPhotoCapture: timeStation.allowPhotoCapture,
      locationTracking: timeStation.locationTracking,
      allowPinClockIn: timeStation.allowPinClockIn ?? false,
      allowedWifis: timeStation.allowedWifis && timeStation.allowedWifis.length > 0 
        ? timeStation.allowedWifis 
        : [{ ssid: '', bssid: '' }],
      allowedIpRanges: timeStation.allowedIpRanges && timeStation.allowedIpRanges.length > 0
        ? timeStation.allowedIpRanges
        : [{ ipRange: '' }]
    });
    setShowTimeStationModal(true);
  };

  const handleTimeStationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Filter out empty WiFi entries, or set to empty array if WiFi restriction is disabled
      // Filter out empty IP range entries
      const formData = {
        ...timeStationForm,
        allowedWifis: isWifiRestricted 
          ? (timeStationForm.allowedWifis?.filter(w => w.ssid.trim()).map(w => ({
              ssid: w.ssid,
              bssid: w.bssid?.trim() || undefined
            })) || [])
          : [],
        allowedIpRanges: timeStationForm.allowedIpRanges?.filter(r => r.ipRange && r.ipRange.trim()).map(r => ({
          ipRange: r.ipRange.trim()
        })) || []
      };
      
      if (editingTimeStation) {
        await timeStationApi.update(editingTimeStation.id, formData);
      } else {
        await timeStationApi.create(formData);
      }
      setShowTimeStationModal(false);
      await refreshTimeStations();
    } catch (error) {
      console.error('Error saving time station:', error);
    }
  };

  const handleDeleteTimeStation = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this time station? This action cannot be undone.')) {
      try {
        await timeStationApi.delete(id);
        await refreshTimeStations();
      } catch (error: any) {
        console.error('Error deleting time station:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete time station';
        alert(`Error: ${errorMessage}`);
      }
    }
  };

  const handleDeactivateTimeStation = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this time station?')) {
      try {
        await timeStationApi.deactivate(id);
        await refreshTimeStations();
      } catch (error) {
        console.error('Error deactivating time station:', error);
      }
    }
  };

  const handleActivateTimeStation = async (id: number) => {
    try {
      await timeStationApi.activate(id);
      await refreshTimeStations();
    } catch (error) {
      console.error('Error activating time station:', error);
    }
  };

  const handleApproveTimeStation = async (id: number) => {
    try {
      await timeStationApi.approve(id);
      await refreshTimeStations();
    } catch (error) {
      console.error('Error approving time station:', error);
      alert('Failed to approve time station. Please try again.');
    }
  };

  // Schedule handlers
  const handleAddSchedule = () => {
    setEditingSchedule(null);
    setScheduleForm({ name: '', description: '', locationIds: [] });
    setShowScheduleModal(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    console.log('Editing schedule (full object):', JSON.stringify(schedule, null, 2));
    console.log('Schedule locationIds:', schedule.locationIds);
    console.log('Schedule locationIds type:', typeof schedule.locationIds);
    console.log('Schedule locationIds isArray:', Array.isArray(schedule.locationIds));
    
    // Handle both camelCase and PascalCase, and ensure it's an array of numbers
    let parsedLocationIds: number[] = [];
    if (schedule.locationIds && Array.isArray(schedule.locationIds)) {
      parsedLocationIds = schedule.locationIds.map(id => Number(id)).filter(id => !isNaN(id));
    } else if ((schedule as any).LocationIds && Array.isArray((schedule as any).LocationIds)) {
      parsedLocationIds = (schedule as any).LocationIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
    }
    
    console.log('Parsed locationIds:', parsedLocationIds);
    
    setScheduleForm({
      name: schedule.name,
      description: schedule.description || '',
      locationIds: parsedLocationIds
    });
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that at least one location is selected
    if (!scheduleForm.locationIds || scheduleForm.locationIds.length === 0) {
      alert('Please select at least one location.');
      return;
    }
    
    console.log('Submitting schedule form:', scheduleForm);
    console.log('Location IDs being sent:', scheduleForm.locationIds);
    
    try {
      const savedSchedule = editingSchedule 
        ? await scheduleApi.update(editingSchedule.id, scheduleForm)
        : await scheduleApi.create(scheduleForm);
      
      console.log('Saved schedule response:', savedSchedule);
      console.log('Saved schedule locationIds:', savedSchedule.locationIds);
      
      setShowScheduleModal(false);
      await refreshSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule');
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        await scheduleApi.delete(id);
        await refreshSchedules();
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const handleDeactivateSchedule = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this schedule?')) {
      try {
        await scheduleApi.deactivate(id);
        await refreshSchedules();
      } catch (error) {
        console.error('Error deactivating schedule:', error);
      }
    }
  };

  const handleActivateSchedule = async (id: number) => {
    try {
      await scheduleApi.activate(id);
      await refreshSchedules();
    } catch (error) {
      console.error('Error activating schedule:', error);
    }
  };

  // Payroll Department handlers
  const handleAddPayrollDepartment = () => {
    setEditingPayrollDepartment(null);
    setPayrollDepartmentForm({ name: '', description: '', code: '', isActive: true });
    setShowPayrollDepartmentModal(true);
  };

  const handleEditPayrollDepartment = (department: PayrollDepartment) => {
    setEditingPayrollDepartment(department);
    setPayrollDepartmentForm({
      name: department.name,
      description: department.description || '',
      code: department.code || '',
      isActive: department.isActive
    });
    setShowPayrollDepartmentModal(true);
  };

  const handlePayrollDepartmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!payrollDepartmentForm.name.trim()) {
      alert('Please enter a name for the payroll department.');
      return;
    }
    
    try {
      if (editingPayrollDepartment) {
        await payrollDepartmentApi.update(editingPayrollDepartment.id, payrollDepartmentForm);
      } else {
        await payrollDepartmentApi.create(payrollDepartmentForm);
      }
      
      setShowPayrollDepartmentModal(false);
      await refreshPayrollDepartments();
    } catch (error: any) {
      console.error('Error saving payroll department:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save payroll department';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleDeletePayrollDepartment = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this payroll department?')) {
      try {
        await payrollDepartmentApi.delete(id);
        await refreshPayrollDepartments();
      } catch (error) {
        console.error('Error deleting payroll department:', error);
      }
    }
  };

  // Shift Template helpers
  const formatTime12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const calculateHours = (startTime: string, endTime: string, breakMins: number = 0): number => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let startTotal = startHour * 60 + startMin;
    let endTotal = endHour * 60 + endMin;
    
    // Handle overnight shifts
    if (endTotal < startTotal) {
      endTotal += 24 * 60;
    }
    
    const totalMinutes = endTotal - startTotal - breakMins;
    return parseFloat((totalMinutes / 60).toFixed(2));
  };

  // Calculate default break minutes based on shift hours and break time rules
  const calculateDefaultBreakMinutes = (startTime: string, endTime: string): number => {
    // First calculate hours without break
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let startTotal = startHour * 60 + startMin;
    let endTotal = endHour * 60 + endMin;
    
    // Handle overnight shifts
    if (endTotal < startTotal) {
      endTotal += 24 * 60;
    }
    
    const totalMinutes = endTotal - startTotal;
    const totalHours = totalMinutes / 60;
    
    // Find matching break time rule
    if (breakTimeRules && breakTimeRules.length > 0) {
      const matchingRule = breakTimeRules.find(rule => 
        totalHours >= rule.minHours && totalHours < rule.maxHours
      );
      
      if (matchingRule) {
        return matchingRule.breakMinutes;
      }
    }
    
    // Default to 0 if no matching rule found
    return 0;
  };

  // fetchShiftTemplates is now replaced by refreshShiftTemplates

  // Shift Template handlers
  const handleAddShiftTemplate = () => {
    const activeLocations = locations.filter(loc => loc.isActive);
    if (activeLocations.length === 0) {
      alert('Please add at least one active location first.');
      return;
    }
    setEditingShiftTemplate(null);
    setBreakMinsManuallySet(false);
    // Default breakTimePaid to Attendance Settings value
    const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
    
    setShiftTemplateForm({
      name: '',
      startTime: '',
      endTime: '',
      hours: 0,
      unpaidBreakMins: 0,
      breakTimePaid: defaultBreakTimePaid,
      breakUseGlobalSettings: true,
      totalHours: 0,
      color: '#3b82f6',
      displayOption: 'time',
      locationIds: [],
    });
    setShowShiftTemplateModal(true);
  };

  const handleEditShiftTemplate = (template: ShiftTemplate) => {
    setEditingShiftTemplate(template);
    setBreakMinsManuallySet(true); // When editing, break minutes are already set, so don't auto-calculate
    // Handle both camelCase and ensure it's an array of numbers
    let parsedLocationIds: number[] = [];
    if (template.locationIds && Array.isArray(template.locationIds)) {
      parsedLocationIds = template.locationIds.map(id => Number(id)).filter(id => !isNaN(id));
    }
    
    setShiftTemplateForm({
      name: template.name,
      startTime: template.startTime,
      endTime: template.endTime,
      hours: template.hours,
      unpaidBreakMins: template.unpaidBreakMins || 0,
      breakTimePaid: template.breakTimePaid ?? (breakTimeSettings?.isPaid ?? false),
      breakUseGlobalSettings: template.breakUseGlobalSettings !== false,
      totalHours: template.totalHours || template.hours,
      color: template.color,
      displayOption: template.displayOption,
      locationIds: parsedLocationIds,
    });
    setShowShiftTemplateModal(true);
  };

  const handleDeleteShiftTemplate = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this shift template?')) {
      try {
        await shiftTemplateApi.delete(id);
        await refreshShiftTemplates();
      } catch (error) {
        console.error('Error deleting shift template:', error);
        alert('Failed to delete shift template');
      }
    }
  };

  const handleSaveShiftTemplate = async () => {
    try {
      const effectiveBreakMins = shiftTemplateForm.breakUseGlobalSettings ? 0 : (shiftTemplateForm.unpaidBreakMins || 0);
      const calculatedHours = calculateHours(shiftTemplateForm.startTime, shiftTemplateForm.endTime, effectiveBreakMins);
      
      if (!shiftTemplateForm.locationIds || shiftTemplateForm.locationIds.length === 0) {
        alert('Please select at least one location.');
        return;
      }

      const templateData: CreateShiftTemplateRequest = {
        ...shiftTemplateForm,
        hours: calculatedHours,
        totalHours: calculatedHours,
        locationIds: shiftTemplateForm.locationIds,
      };

      if (editingShiftTemplate) {
        await shiftTemplateApi.update(editingShiftTemplate.id, templateData);
      } else {
        await shiftTemplateApi.create(templateData);
      }
      
      await refreshShiftTemplates();
      setShowShiftTemplateModal(false);
      setEditingShiftTemplate(null);
    } catch (error) {
      console.error('Error saving shift template:', error);
      alert('Failed to save shift template');
    }
  };

  const handleShiftTemplateTimeChange = (field: 'startTime' | 'endTime', value: string) => {
    const newStartTime = field === 'startTime' ? value : shiftTemplateForm.startTime;
    const newEndTime = field === 'endTime' ? value : shiftTemplateForm.endTime;
    
    // Only auto-calculate break minutes if:
    // 1. Both times are filled
    // 2. Break minutes hasn't been manually set (for new templates)
    // 3. Not editing an existing template (breakMinsManuallySet is false)
    const isOverrideBreak = !shiftTemplateForm.breakUseGlobalSettings;
    let newBreakMins = shiftTemplateForm.unpaidBreakMins || 0;
    
    if (isOverrideBreak && newStartTime && newEndTime && !breakMinsManuallySet && !editingShiftTemplate) {
      newBreakMins = calculateDefaultBreakMinutes(newStartTime, newEndTime);
    }
    
    const effectiveBreakMins = isOverrideBreak ? newBreakMins : 0;
    const calculatedHours = calculateHours(newStartTime, newEndTime, effectiveBreakMins);
    
    setShiftTemplateForm({
      ...shiftTemplateForm,
      [field]: value,
      unpaidBreakMins: newBreakMins,
      hours: calculatedHours,
      totalHours: calculatedHours,
    });
  };

  const handleShiftTemplateBreakChange = (value: number) => {
    // Mark break minutes as manually set when user changes it
    setBreakMinsManuallySet(true);
    const calculatedHours = calculateHours(shiftTemplateForm.startTime, shiftTemplateForm.endTime, value);
    setShiftTemplateForm({
      ...shiftTemplateForm,
      unpaidBreakMins: value,
      hours: calculatedHours,
      totalHours: calculatedHours,
    });
  };

  // Role handlers
  const handleAddRole = () => {
    setEditingRole(null);
    setRoleForm({
      name: '',
      description: '',
      isActive: true,
      displayOrder: 0,
      permissionIds: [],
      mfaRequirement: 'None',
      mfaAllowAuthenticator: false,
      mfaAllowEmail: false,
      mfaAllowSms: false,
      mfaDefaultMethod: null,
    });
    setShowRoleModal(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      isActive: role.isActive,
      displayOrder: role.displayOrder,
      permissionIds: role.permissions.map(p => p.id),
      mfaRequirement: role.mfaRequirement || 'None',
      mfaAllowAuthenticator: Boolean(role.mfaAllowAuthenticator),
      mfaAllowEmail: Boolean(role.mfaAllowEmail),
      mfaAllowSms: Boolean(role.mfaAllowSms),
      mfaDefaultMethod: role.mfaDefaultMethod ?? null,
    });
    setShowRoleModal(true);
  };

  const getRoleSelectedMfaMethods = (form: CreateRoleRequest): MfaMethod[] => {
    const methods: MfaMethod[] = [];
    if (form.mfaAllowAuthenticator && tenantMfaAllowAuthenticator) methods.push('Authenticator');
    if (form.mfaAllowEmail && tenantMfaAllowEmail) methods.push('Email');
    if (form.mfaAllowSms && tenantMfaAllowSms) methods.push('Sms');
    return methods;
  };

  const handleRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (tenantMfaEnabled && roleForm.mfaRequirement && roleForm.mfaRequirement !== 'None') {
        const selected = getRoleSelectedMfaMethods(roleForm);
        if (selected.length === 0) {
          alert('Select at least one MFA method allowed for this role.');
          return;
        }
        if (selected.length >= 2) {
          if (!roleForm.mfaDefaultMethod || !selected.includes(roleForm.mfaDefaultMethod)) {
            alert('Select a default MFA method from the allowed methods.');
            return;
          }
        }
      }
      const payload: CreateRoleRequest = {
        ...roleForm,
        mfaAllowAuthenticator: tenantMfaEnabled && tenantMfaAllowAuthenticator ? Boolean(roleForm.mfaAllowAuthenticator) : false,
        mfaAllowEmail: tenantMfaEnabled && tenantMfaAllowEmail ? Boolean(roleForm.mfaAllowEmail) : false,
        mfaAllowSms: tenantMfaEnabled && tenantMfaAllowSms ? Boolean(roleForm.mfaAllowSms) : false,
        mfaDefaultMethod:
          tenantMfaEnabled && roleForm.mfaRequirement !== 'None'
            ? (getRoleSelectedMfaMethods(roleForm).length === 1
                ? getRoleSelectedMfaMethods(roleForm)[0]
                : roleForm.mfaDefaultMethod ?? null)
            : null,
      };
      if (editingRole) {
        await roleApi.update(editingRole.id, payload);
      } else {
        await roleApi.create(payload);
      }
      setShowRoleModal(false);
      await refreshRoles();
      window.dispatchEvent(new Event('staffly:refresh-permissions'));
    } catch (error) {
      console.error('Error saving role:', error);
      alert('Failed to save role. Please try again.');
    }
  };

  const handleDeleteRole = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await roleApi.delete(id);
        await refreshRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    }
  };

  const handleDeactivateRole = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this role?')) {
      try {
        await roleApi.deactivate(id);
        await refreshRoles();
      } catch (error) {
        console.error('Error deactivating role:', error);
      }
    }
  };

  const handleActivateRole = async (id: number) => {
    try {
      await roleApi.activate(id);
      await refreshRoles();
    } catch (error) {
      console.error('Error activating role:', error);
    }
  };

  const handlePermissionToggle = (permissionId: number) => {
    const currentIds = roleForm.permissionIds || [];
    if (currentIds.includes(permissionId)) {
      setRoleForm({
        ...roleForm,
        permissionIds: currentIds.filter(id => id !== permissionId)
      });
    } else {
      setRoleForm({
        ...roleForm,
        permissionIds: [...currentIds, permissionId]
      });
    }
  };

  // Attendance Settings handlers
  const addBreakTimeRule = () => {
    const lastRule = breakTimeRules[breakTimeRules.length - 1];
    const newRule: BreakTimeRule = {
      minHours: lastRule ? lastRule.maxHours : 0,
      maxHours: lastRule ? lastRule.maxHours + 2 : 2,
      breakMinutes: 15,
    };
    setBreakTimeRules([...breakTimeRules, newRule]);
  };

  const removeBreakTimeRule = (index: number) => {
    if (breakTimeRules.length > 1) {
      setBreakTimeRules(breakTimeRules.filter((_, i) => i !== index));
    }
  };

  const updateBreakTimeRule = (index: number, field: keyof BreakTimeRule, value: number) => {
    const updatedRules = [...breakTimeRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setBreakTimeRules(updatedRules);
  };

  const handleSaveBreakTimeSettings = async () => {
    setSavingBreakTime(true);
    try {
      const settingsData = buildBreakTimeSettingsPayload();
      console.log('Saving break time settings:', settingsData);
      const result = await breakTimeSettingsApi.create(settingsData);
      console.log('Break time settings saved successfully:', result);
      
      // Update local state immediately with the saved value
      if (result) {
        applyBreakPolicyFromApi(result);
      }
      
      toast.success('Break time settings saved successfully');
      // Refresh data to get the updated settings
      await refreshAttendanceSettings();
    } catch (error: any) {
      console.error('Error saving break time settings:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save break time settings. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSavingBreakTime(false);
    }
  };

  const handleWeekStartDayChange = async (value: string) => {
    setWeekStartDay(value);
    try {
      const result = await systemSettingsApi.create({ key: 'week_start_day', value });
      console.log('Week start day saved successfully:', result);
      // Refresh system settings to get updated value
      const updatedSettings = await systemSettingsApi.getAll();
      setSystemSettings(updatedSettings);
    } catch (error: any) {
      console.error('Error saving week start day:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save week start day. Please try again.';
      toast.error(errorMessage);
      // Revert the state on error
      const weekStartSetting = systemSettings.find((s: SystemSetting) => s.key === 'week_start_day');
      if (weekStartSetting) {
        setWeekStartDay(weekStartSetting.value);
      }
    }
  };

  // Training Type handlers
  const handleAddTrainingType = () => {
    setEditingTrainingType(null);
    setTrainingTypeForm({
      name: '',
      description: '',
      frequency: 'One-Time',
      recurringValue: undefined,
      recurringUnit: undefined,
      isActive: true
    });
    setShowTrainingTypeModal(true);
  };

  const handleEditTrainingType = (trainingType: TrainingType) => {
    setEditingTrainingType(trainingType);
    setTrainingTypeForm({
      name: trainingType.name,
      description: trainingType.description || '',
      frequency: trainingType.frequency,
      recurringValue: trainingType.recurringValue,
      recurringUnit: trainingType.recurringUnit,
      isActive: trainingType.isActive
    });
    setShowTrainingTypeModal(true);
  };

  const handleTrainingTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTrainingType) {
        await trainingTypeApi.update(editingTrainingType.id, trainingTypeForm);
      } else {
        await trainingTypeApi.create(trainingTypeForm);
      }
      setShowTrainingTypeModal(false);
      await refreshTrainingTypes();
      toast.success(editingTrainingType ? 'Training type updated successfully' : 'Training type created successfully');
    } catch (error: any) {
      console.error('Error saving training type:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save training type. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleDeleteTrainingType = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this training type?')) {
      try {
        await trainingTypeApi.delete(id);
        await refreshTrainingTypes();
      } catch (error) {
        console.error('Error deleting training type:', error);
      }
    }
  };

  const handleDeactivateTrainingType = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this training type?')) {
      try {
        await trainingTypeApi.deactivate(id);
        await refreshTrainingTypes();
      } catch (error) {
        console.error('Error deactivating training type:', error);
      }
    }
  };

  const handleActivateTrainingType = async (id: number) => {
    try {
      await trainingTypeApi.activate(id);
      await refreshTrainingTypes();
    } catch (error) {
      console.error('Error activating training type:', error);
    }
  };

  const formatTrainingTypeFrequency = (trainingType: TrainingType) => {
    if (trainingType.frequency === 'One-Time') return 'One-Time';
    if (trainingType.frequency === 'Recurring' && trainingType.recurringValue && trainingType.recurringUnit) {
      return `Every ${trainingType.recurringValue} ${trainingType.recurringUnit}`;
    }
    return trainingType.frequency;
  };

  // Time Card Settings handlers
  const handleSaveTimeCardSettings = async () => {
    setSavingTimeCardSettings(true);
    try {
      const settingsToSave = [
        { key: 'timecard_display_format', value: timeCardSettings.timeDisplayFormat },
        { key: 'timecard_separate_sunday', value: timeCardSettings.separateSundayHours.toString() },
        { key: 'timecard_include_positions', value: timeCardSettings.includePositions.toString() },
        { key: 'timecard_clock_in_early', value: timeCardSettings.clockInEarlierThan },
        { key: 'timecard_clock_in_late', value: timeCardSettings.clockInLaterThan },
        { key: 'timecard_clock_in_late_grace_minutes', value: String(timeCardSettings.clockInLateGraceMinutes ?? 0) },
        { key: 'timecard_clock_out_early', value: timeCardSettings.clockOutEarlierThan },
        { key: 'timecard_clock_out_late', value: timeCardSettings.clockOutLaterThan }
      ];

      for (const setting of settingsToSave) {
        await systemSettingsApi.create(setting);
      }

      toast.success('Time card settings saved successfully');
      await refreshTimeCardSettings();
    } catch (error: any) {
      console.error('Error saving time card settings:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save time card settings. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSavingTimeCardSettings(false);
    }
  };

  // Audit Log handlers
  const resetAuditLogForm = () => {
    setAuditLogForm({ entities: [] });
  };

  const getAuditFieldOptions = (entity: AuditLogEntity) => auditLogEntityFields[entity] || [];
  const getAuditActionOptions = (entity: AuditLogEntity) => auditLogEntityActions[entity] || [];

  const getAuditFieldLabel = (entity: AuditLogEntity, fieldId: string) => {
    return getAuditFieldOptions(entity).find((option) => option.id === fieldId)?.label || fieldId;
  };

  const getAuditFieldCaptures = (entity: AuditLogEntity, fieldId: string) => {
    return getAuditFieldOptions(entity).find((option) => option.id === fieldId)?.captures || [];
  };

  const handleAuditLogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (auditLogForm.entities.length === 0) {
      toast.error('Select at least one entity.');
      return;
    }
    const selectedEntities = new Set(auditLogForm.entities);
    const existingEntities = new Set(auditLogConfigs.map((config) => config.entity));

    const entitiesToRemove = auditLogConfigs
      .filter((config) => !selectedEntities.has(config.entity))
      .map((config) => config.id);

    const entitiesToAdd = auditLogForm.entities.filter((entity) => !existingEntities.has(entity));

    for (const id of entitiesToRemove) {
      await auditLogConfigApi.delete(Number(id));
    }

    for (const entity of entitiesToAdd) {
      const actions = [...getAuditActionOptions(entity)].sort();
      const fields = getAuditFieldOptions(entity).map((field) => field.id);
      await auditLogConfigApi.create({
        entity,
        isActive: true,
        actions,
        fields: fields.map((fieldKey) => ({
          fieldKey,
          fieldLabel: getAuditFieldLabel(entity, fieldKey),
          captures: getAuditFieldCaptures(entity, fieldKey).join(','),
        })),
      });
    }

    await refreshAuditLogConfigs();
    toast.success('Audit log configuration updated successfully.');
  };

  // Company Settings handlers
  const handleSaveCompanySettings = async () => {
    // Client-side validation
    if (!companySettingsForm.companyName || companySettingsForm.companyName.trim() === '') {
      toast.error('Company name is required.');
      return;
    }

    setSavingCompanySettings(true);
    try {
      if (companySettings?.id) {
        await companySettingsApi.update(companySettings.id, companySettingsForm);
      } else {
        await companySettingsApi.create(companySettingsForm);
      }
      toast.success('Company settings saved successfully');
      await refreshCompanySettings();
    } catch (error: any) {
      console.error('Error saving company settings:', error);
      
      // Handle different error response formats
      let errorMessage = 'Failed to save company settings. Please try again.';
      
      if (error?.response?.data) {
        // Check if it's a string
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
        // Check if it's an object with a message property
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        // Check if it's an object with errors (validation errors)
        else if (error.response.data.errors) {
          const errors = error.response.data.errors;
          const errorMessages = Object.keys(errors).map(key => 
            Array.isArray(errors[key]) ? errors[key].join(', ') : errors[key]
          );
          errorMessage = errorMessages.join('\n');
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSavingCompanySettings(false);
    }
  };

  // Public Holiday handlers
  const handleAddPublicHoliday = () => {
    setEditingPublicHoliday(null);
    setPublicHolidayForm({
      name: '',
      date: '',
      year: new Date().getFullYear(),
      country: 'Ireland',
      isActive: true
    });
    setShowPublicHolidayModal(true);
  };

  const handleEditPublicHoliday = (holiday: PublicHoliday) => {
    setEditingPublicHoliday(holiday);
    const dateObj = new Date(holiday.date);
    setPublicHolidayForm({
      name: holiday.name || holiday.holidayName || '',
      date: holiday.date || holiday.holidayDate || '',
      year: holiday.year || dateObj.getFullYear(),
      country: holiday.country,
      isActive: holiday.isActive
    });
    setShowPublicHolidayModal(true);
  };

  const handlePublicHolidaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPublicHoliday) {
        await publicHolidayApi.update(editingPublicHoliday.id, publicHolidayForm);
      } else {
        await publicHolidayApi.create(publicHolidayForm);
      }
      setShowPublicHolidayModal(false);
      await refreshPublicHolidays();
      toast.success(editingPublicHoliday ? 'Public holiday updated successfully' : 'Public holiday created successfully');
    } catch (error: any) {
      console.error('Error saving public holiday:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save public holiday. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleDeletePublicHoliday = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this public holiday?')) {
      try {
        await publicHolidayApi.delete(id);
        await refreshPublicHolidays();
      } catch (error) {
        console.error('Error deleting public holiday:', error);
      }
    }
  };

  const handleDeactivatePublicHoliday = async (id: number) => {
    try {
      await publicHolidayApi.deactivate(id);
      await refreshPublicHolidays();
    } catch (error) {
      console.error('Error deactivating public holiday:', error);
    }
  };

  const handleActivatePublicHoliday = async (id: number) => {
    try {
      await publicHolidayApi.activate(id);
      await refreshPublicHolidays();
    } catch (error) {
      console.error('Error activating public holiday:', error);
    }
  };

  // Public Holiday Policy handlers
  const handleAddPublicHolidayPolicy = () => {
    setEditingPublicHolidayPolicy(null);
    setPublicHolidayPolicyForm({
      version: 1,
      effectiveFrom: '',
      effectiveTo: null,
      eligibilityRuleType: 'Statutory40Hours',
      requiredHoursLastWeeks: 40,
      weeksLookback: 5,
      defaultEntitlementType: 'PaidOff',
      workOnHolidayEntitlementType: undefined,
      holidayPremiumRate: 1.0,
      dayInLieuExpiryDays: 30,
      dayInLieuAutoCredit: false,
      creditToAnnualLeave: false
    });
    setShowPublicHolidayPolicyModal(true);
  };

  const handleEditPublicHolidayPolicy = (policy: PublicHolidayPolicy) => {
    setEditingPublicHolidayPolicy(policy);
    setPublicHolidayPolicyForm({
      version: policy.version,
      effectiveFrom: policy.effectiveFrom,
      effectiveTo: policy.effectiveTo,
      eligibilityRuleType: policy.eligibilityRuleType,
      requiredHoursLastWeeks: policy.requiredHoursLastWeeks,
      weeksLookback: policy.weeksLookback,
      defaultEntitlementType: policy.defaultEntitlementType,
      workOnHolidayEntitlementType: policy.workOnHolidayEntitlementType,
      holidayPremiumRate: policy.holidayPremiumRate,
      dayInLieuExpiryDays: policy.dayInLieuExpiryDays,
      dayInLieuAutoCredit: policy.dayInLieuAutoCredit,
      creditToAnnualLeave: policy.creditToAnnualLeave
    });
    setShowPublicHolidayPolicyModal(true);
  };

  const validatePolicyDates = (newPolicy: CreatePublicHolidayPolicyRequest, editingId?: number): string | null => {
    const newFrom = new Date(newPolicy.effectiveFrom);
    const newTo = newPolicy.effectiveTo ? new Date(newPolicy.effectiveTo) : null;

    // Check if EffectiveFrom is before EffectiveTo (if both are set)
    if (newTo && newFrom >= newTo) {
      return 'Effective From date must be before Effective To date';
    }

    // Check for overlapping dates with existing policies
    for (const existingPolicy of publicHolidayPolicies) {
      // Skip the policy being edited
      if (editingId && existingPolicy.policyId === editingId) {
        continue;
      }

      const existingFrom = new Date(existingPolicy.effectiveFrom);
      const existingTo = existingPolicy.effectiveTo ? new Date(existingPolicy.effectiveTo) : null;

      // Two date ranges overlap if: start1 < end2 AND start2 < end1
      // For null (active/infinite), treat as a very far future date
      const newToDate = newTo || new Date('9999-12-31');
      const existingToDate = existingTo || new Date('9999-12-31');

      // Check for overlap: newFrom < existingTo AND existingFrom < newTo
      const overlaps = newFrom < existingToDate && existingFrom < newToDate;

      if (overlaps) {
        return `This policy overlaps with an existing policy (Version ${existingPolicy.version}, ${existingPolicy.effectiveFrom} to ${existingPolicy.effectiveTo || 'Active'})`;
      }
    }

    // If setting a new active policy (EffectiveTo = null), ensure no other active policy exists
    if (newTo === null) {
      for (const existingPolicy of publicHolidayPolicies) {
        if (existingPolicy.effectiveTo === null && (!editingId || existingPolicy.policyId !== editingId)) {
          return 'There is already an active policy. Please set an Effective To date for the existing active policy first.';
        }
      }
    }

    return null;
  };

  const handlePublicHolidayPolicySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validationError = validatePolicyDates(publicHolidayPolicyForm, editingPublicHolidayPolicy?.policyId);
      if (validationError) {
        alert(validationError);
        return;
      }

      if (editingPublicHolidayPolicy) {
        await publicHolidayPolicyApi.update(editingPublicHolidayPolicy.policyId, publicHolidayPolicyForm);
      } else {
        await publicHolidayPolicyApi.create(publicHolidayPolicyForm);
      }
      setShowPublicHolidayPolicyModal(false);
      await refreshPublicHolidayPolicies();
    } catch (error: any) {
      console.error('Error saving public holiday policy:', error);
      alert(error.response?.data?.message || 'Failed to save public holiday policy');
    }
  };

  const handleDeletePublicHolidayPolicy = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) {
      return;
    }
    try {
      await publicHolidayPolicyApi.delete(id);
      await refreshPublicHolidayPolicies();
    } catch (error) {
      console.error('Error deleting public holiday policy:', error);
      alert('Failed to delete public holiday policy');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'positions', label: 'Positions', icon: Briefcase },
    { id: 'schedules', label: 'Schedules', icon: Calendar },
    { id: 'payroll-departments', label: 'Payroll Departments', icon: Currency },
    { id: 'shift-templates', label: 'Shift Templates', icon: Clock },
    { id: 'time-stations', label: 'Time Stations', icon: Monitor },
    { id: 'roles', label: 'Role Management', icon: Shield },
    { id: 'attendance-settings', label: 'Attendance Settings', icon: Clock },
    { id: 'time-cards', label: 'Time Cards', icon: Clock },
    { id: 'audit-logs', label: 'Audit Logs', icon: Database },
    { id: 'feature-access', label: 'Feature Access', icon: SlidersHorizontal },
    ...(healthcareNurseBandingPayrollEnabled
      ? [{ id: 'nurse-banding' as const, label: 'Position Banding', icon: Currency }]
      : []),
    { id: 'training-types', label: 'Training Types', icon: GraduationCap },
    { id: 'company-settings', label: 'Company Settings', icon: Building2 },
    { id: 'public-holidays', label: 'Public Holidays', icon: Calendar },
    { id: 'face-sync-status', label: 'Face Sync Status', icon: User },
    ...(hasSuperAdminRole ? [{ id: 'uploads-migration', label: 'Uploads → S3 Migration', icon: Cloud }] : []),
  ];

  const renderContent = () => {
    if (sectionLoading[activeSection]) {
      return (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
          <p className="text-neutral-600">Loading...</p>
        </div>
      );
    }

    // Filter data based on active status and search
    const filteredLocations = locations.filter((location: any) => {
      const isActive = location.isActive === true || location.isActive === null || location.isActive === undefined;
      const isInactive = location.isActive === false;
      
      // Filter by active status
      let matchesFilter = true;
      if (locationFilter === 'active') {
        matchesFilter = isActive;
      } else if (locationFilter === 'inactive') {
        matchesFilter = isInactive;
      }
      
      // Filter by search term
      if (matchesFilter && locationSearch.trim()) {
        const searchLower = locationSearch.toLowerCase();
        matchesFilter = 
          location.name?.toLowerCase().includes(searchLower) ||
          location.address?.toLowerCase().includes(searchLower) ||
          location.timezone?.toLowerCase().includes(searchLower);
      }
      
      return matchesFilter;
    }).sort((a, b) => {
      // Sort: Active locations first, then inactive
      const aIsActive = a.isActive === true || a.isActive === null || a.isActive === undefined;
      const bIsActive = b.isActive === true || b.isActive === null || b.isActive === undefined;
      
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      
      // If both have same status, sort by name
      return (a.name || '').localeCompare(b.name || '');
    });

    const filteredPositions = positions.filter((position: any) => {
      const isActive = position.isActive === true || position.isActive === null || position.isActive === undefined;
      const isInactive = position.isActive === false;
      
      // Filter by active status
      let matchesFilter = true;
      if (positionFilter === 'active') {
        matchesFilter = isActive;
      } else if (positionFilter === 'inactive') {
        matchesFilter = isInactive;
      }
      
      // Filter by search term
      if (matchesFilter && positionSearch.trim()) {
        const searchLower = positionSearch.toLowerCase();
        matchesFilter = 
          position.title?.toLowerCase().includes(searchLower) ||
          position.description?.toLowerCase().includes(searchLower);
      }
      
      return matchesFilter;
    });

    const filteredTimeStations = timeStations.filter((timeStation: any) => {
      const isActive = timeStation.isActive === true || timeStation.isActive === null || timeStation.isActive === undefined;
      const isInactive = timeStation.isActive === false;
      const isPending = timeStation.status === 'Pending' || (!timeStation.isActive && timeStation.status === 'Pending');
      
      // Filter by status
      let matchesFilter = true;
      if (timeStationFilter === 'all') {
        matchesFilter = true; // Show all
      } else if (timeStationFilter === 'active') {
        matchesFilter = isActive && timeStation.status !== 'Pending';
      } else if (timeStationFilter === 'inactive') {
        matchesFilter = isInactive && timeStation.status !== 'Pending';
      } else if (timeStationFilter === 'pending') {
        matchesFilter = isPending || timeStation.status === 'Pending';
      }
      
      // Filter by search term
      if (matchesFilter && timeStationSearch.trim()) {
        const searchLower = timeStationSearch.toLowerCase();
        matchesFilter = 
          timeStation.name?.toLowerCase().includes(searchLower) ||
          timeStation.deviceId?.toLowerCase().includes(searchLower) ||
          timeStation.description?.toLowerCase().includes(searchLower) ||
          timeStation.ipAddress?.toLowerCase().includes(searchLower) ||
          timeStation.location?.name?.toLowerCase().includes(searchLower);
      }
      
      return matchesFilter;
    });

    // Calculate counts for each filter
    const locationCounts = {
      total: locations.length,
      active: locations.filter(l => l.isActive === true || l.isActive === null || l.isActive === undefined).length,
      inactive: locations.filter(l => l.isActive === false).length,
      filtered: filteredLocations.length
    };

    const positionCounts = {
      total: positions.length,
      active: positions.filter(p => p.isActive === true || p.isActive === null || p.isActive === undefined).length,
      inactive: positions.filter(p => p.isActive === false).length,
      filtered: filteredPositions.length
    };

    const timeStationCounts = {
      total: timeStations.length,
      active: timeStations.filter(t => (t.isActive === true || t.isActive === null || t.isActive === undefined) && t.status !== 'Pending').length,
      inactive: timeStations.filter(t => t.isActive === false && t.status !== 'Pending').length,
      pending: timeStations.filter(t => t.status === 'Pending' || (!t.isActive && t.status === 'Pending')).length,
      filtered: filteredTimeStations.length
    };

    const filteredSchedules = schedules.filter((schedule: any) => {
      const isActive = schedule.isActive === true || schedule.isActive === null || schedule.isActive === undefined;
      const isInactive = schedule.isActive === false;
      
      // Filter by active status
      let matchesFilter = true;
      if (scheduleFilter === 'active') {
        matchesFilter = isActive;
      } else if (scheduleFilter === 'inactive') {
        matchesFilter = isInactive;
      }
      
      // Filter by location selection
      if (matchesFilter && scheduleLocationFilter.length > 0) {
        const scheduleLocationIds = schedule.locationIds || [];
        // Check if schedule has at least one of the selected locations
        matchesFilter = scheduleLocationIds.some((locationId: number) => 
          scheduleLocationFilter.includes(locationId)
        );
      }
      
      // Filter by search term
      if (matchesFilter && scheduleSearch.trim()) {
        const searchLower = scheduleSearch.toLowerCase();
        matchesFilter = 
          schedule.name?.toLowerCase().includes(searchLower) ||
          schedule.description?.toLowerCase().includes(searchLower);
      }
      
      return matchesFilter;
    });

    const scheduleCounts = {
      total: schedules.length,
      active: schedules.filter(s => s.isActive === true || s.isActive === null || s.isActive === undefined).length,
      inactive: schedules.filter(s => s.isActive === false).length,
      filtered: filteredSchedules.length
    };

    const filteredShiftTemplates = sortShiftTemplatesByStartTime(
      shiftTemplates.filter((template: ShiftTemplate) => {
      const isActive = template.isActive !== false;
      const isInactive = template.isActive === false;

      // Filter by active status
      let matchesFilter = true;
      if (shiftTemplateFilter === 'active') {
        matchesFilter = isActive;
      } else if (shiftTemplateFilter === 'inactive') {
        matchesFilter = isInactive;
      }

      // Filter by location selection
      if (matchesFilter && shiftTemplateLocationFilter.length > 0) {
        const templateLocationIds = template.locationIds || [];
        matchesFilter = templateLocationIds.some((locationId: number) => 
          shiftTemplateLocationFilter.includes(locationId)
        );
      }
      
      // Filter by search term
      if (matchesFilter && shiftTemplateSearch.trim()) {
        const searchLower = shiftTemplateSearch.toLowerCase();
        matchesFilter = 
          template.name?.toLowerCase().includes(searchLower) ||
          template.startTime.includes(shiftTemplateSearch) ||
          template.endTime.includes(shiftTemplateSearch);
      }
      
      return matchesFilter;
    })
    );

    const shiftTemplateCounts = {
      total: shiftTemplates.length,
      active: shiftTemplates.filter(t => t.isActive !== false).length,
      inactive: shiftTemplates.filter(t => t.isActive === false).length,
      filtered: filteredShiftTemplates.length
    };

    const filteredRoles = roles.filter((role: any) => {
      const isActive = role.isActive === true || role.isActive === null || role.isActive === undefined;
      const isInactive = role.isActive === false;
      
      // Filter by active status
      let matchesFilter = true;
      if (roleFilter === 'active') {
        matchesFilter = isActive;
      } else if (roleFilter === 'inactive') {
        matchesFilter = isInactive;
      }
      
      // Filter by search term
      if (matchesFilter && roleSearch.trim()) {
        const searchLower = roleSearch.toLowerCase();
        matchesFilter = 
          role.name?.toLowerCase().includes(searchLower) ||
          role.description?.toLowerCase().includes(searchLower) ||
          role.permissions?.some((p: Permission) => 
            p.name?.toLowerCase().includes(searchLower) || 
            p.code?.toLowerCase().includes(searchLower)
          );
      }
      
      return matchesFilter;
    });

    const roleCounts = {
      total: roles.length,
      active: roles.filter(r => r.isActive === true || r.isActive === null || r.isActive === undefined).length,
      inactive: roles.filter(r => r.isActive === false).length,
      filtered: filteredRoles.length
    };

    const filteredTrainingTypes = trainingTypes.filter((trainingType: any) => {
      const isActive = trainingType.isActive === true || trainingType.isActive === null || trainingType.isActive === undefined;
      const isInactive = trainingType.isActive === false;
      
      // Filter by active status
      let matchesFilter = true;
      if (trainingTypeFilter === 'active') {
        matchesFilter = isActive;
      } else if (trainingTypeFilter === 'inactive') {
        matchesFilter = isInactive;
      }
      
      // Filter by search term
      if (matchesFilter && trainingTypeSearch.trim()) {
        const searchLower = trainingTypeSearch.toLowerCase();
        matchesFilter = 
          trainingType.name?.toLowerCase().includes(searchLower) ||
          trainingType.description?.toLowerCase().includes(searchLower) ||
          trainingType.frequency?.toLowerCase().includes(searchLower);
      }
      
      return matchesFilter;
    });

    const trainingTypeCounts = {
      total: trainingTypes.length,
      active: trainingTypes.filter(t => t.isActive === true || t.isActive === null || t.isActive === undefined).length,
      inactive: trainingTypes.filter(t => t.isActive === false).length,
      filtered: filteredTrainingTypes.length
    };

    const filteredPublicHolidays = publicHolidays.filter((holiday: any) => {
      const isActive = holiday.isActive === true || holiday.isActive === null || holiday.isActive === undefined;
      const isInactive = holiday.isActive === false;
      
      // Filter by active status
      let matchesFilter = true;
      if (publicHolidayFilter === 'active') {
        matchesFilter = isActive;
      } else if (publicHolidayFilter === 'inactive') {
        matchesFilter = isInactive;
      }
      
      // Filter by search term
      if (matchesFilter && publicHolidaySearch.trim()) {
        const searchLower = publicHolidaySearch.toLowerCase();
        matchesFilter = 
          holiday.name?.toLowerCase().includes(searchLower) ||
          holiday.country?.toLowerCase().includes(searchLower) ||
          holiday.date?.toLowerCase().includes(searchLower);
      }
      
      return matchesFilter;
    });

    const publicHolidayCounts = {
      total: publicHolidays.length,
      active: publicHolidays.filter(h => h.isActive === true || h.isActive === null || h.isActive === undefined).length,
      inactive: publicHolidays.filter(h => h.isActive === false).length,
      filtered: filteredPublicHolidays.length
    };

    switch (activeSection) {
      case 'locations':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
                <h2 className="text-xl md:text-2xl font-bold">Locations</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {locationSearch.trim() 
                    ? `Showing ${locationCounts.filtered} of ${locationCounts.total} locations`
                    : `Manage workplace locations (${locationCounts.filtered} of ${locationCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select 
                    value={locationFilter} 
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="flex-1 sm:w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({locationCounts.active})</option>
                    <option value="inactive">Inactive ({locationCounts.inactive})</option>
                    <option value="all">All ({locationCounts.total})</option>
                  </select>
                </div>
                <button
                  onClick={handleAddLocation}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Location
                </button>
              </div>
              </div>

            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search locations by name, address, or timezone..."
                  value={locationSearch}
                  onChange={(e) => {
                    setLocationSearch(e.target.value);
                    setLocationPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {filteredLocations.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <MapPin className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No locations found</h3>
                <p className="text-neutral-600 mb-4">
                  {locationSearch.trim() 
                    ? 'No locations match your search criteria. Try adjusting your search or filters.'
                    : locationFilter === 'active' 
                      ? 'No active locations found.' 
                      : locationFilter === 'inactive' 
                        ? 'No inactive locations found.' 
                        : 'Get started by creating your first location.'}
                </p>
                <button
                  onClick={handleAddLocation}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Location
                </button>
              </div>
            ) : (
              <>
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                <div className="overflow-y-auto flex-1 min-h-0">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b border-neutral-200">
                        <th className="text-left p-4 font-medium text-neutral-700">Name</th>
                        <th className="text-left p-4 font-medium text-neutral-700">Address</th>
                        <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                    {(() => {
                      // filteredLocations is already sorted (active first, then by name)
                      const sortedLocations = filteredLocations.filter((location: any) => location.id !== null && location.id !== undefined);
                      const totalPages = Math.ceil(sortedLocations.length / itemsPerPage);
                      const startIndex = (locationPage - 1) * itemsPerPage;
                      const paginatedLocations = sortedLocations.slice(startIndex, startIndex + itemsPerPage);
                      
                      return paginatedLocations.map((location: any) => (
                        <tr key={location.id} className="border-b border-neutral-100 last:border-b-0">
                          <td className="p-4 font-medium">
                            <div className="flex items-center gap-2">
                              {location.name}
                              {!location.isActive && (
                                <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                              )}
                            </div>
                            </td>
                          <td className="p-4 text-neutral-600">{location.address || '-'}</td>
                          <td className="p-4 text-right">
                            <div className="w-48 flex items-center h-8 relative ml-auto">
                              {/* Edit button - aligned left */}
                              <button
                                onClick={() => handleEditLocation(location)}
                                className="p-2 h-8 w-8 flex items-center justify-center text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors absolute left-0"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              
                              {/* Activate/Deactivate button - aligned center */}
                              <div className="flex-1 flex items-center justify-center">
                                {location.isActive ? (
                                  <button
                                    onClick={() => handleDeactivateLocation(location.id)}
                                    className="px-3 h-8 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors flex items-center"
                                  >
                                    Deactivate
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleActivateLocation(location.id)}
                                    className="px-3 h-8 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors flex items-center"
                                  >
                                    Activate
                                  </button>
                                )}
                              </div>
                              
                              {/* Delete button - aligned right */}
                              <button
                                onClick={() => handleDeleteLocation(location.id)}
                                className="p-2 h-8 w-8 flex items-center justify-center text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors absolute right-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          </tr>
                        ));
                    })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {(() => {
                  const sortedLocations = filteredLocations.filter((location: any) => location.id !== null && location.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                  const totalPages = Math.ceil(sortedLocations.length / itemsPerPage);
                  const startIndex = (locationPage - 1) * itemsPerPage;
                  
                  if (totalPages <= 1) return null;
                  
                  return (
                    <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                      <div className="text-sm text-neutral-700">
                        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedLocations.length)} of {sortedLocations.length} locations
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setLocationPage(prev => Math.max(1, prev - 1))}
                          disabled={locationPage === 1}
                          className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-neutral-700">
                          Page {locationPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setLocationPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={locationPage >= totalPages}
                          className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden grid gap-4">
              {filteredLocations.filter((location: any) => location.id !== null && location.id !== undefined).map((location: any) => (
                <div key={location.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium">{location.name}</h3>
                        {!location.isActive && (
                          <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                        )}
                      </div>
                      {location.address && (
                        <p className="text-sm text-neutral-600 mt-1">{location.address}</p>
                      )}
                    </div>
                    <div className="w-48 flex items-center h-8 relative">
                      {/* Edit button - aligned left */}
                      <button
                        onClick={() => handleEditLocation(location)}
                        className="p-2 h-8 w-8 flex items-center justify-center text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors absolute left-0"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      
                      {/* Activate/Deactivate button - aligned center */}
                      <div className="flex-1 flex items-center justify-center">
                        {location.isActive ? (
                          <button
                            onClick={() => handleDeactivateLocation(location.id)}
                            className="px-3 h-8 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors flex items-center"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateLocation(location.id)}
                            className="px-3 h-8 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors flex items-center"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                      
                      {/* Delete button - aligned right */}
                      <button
                        onClick={() => handleDeleteLocation(location.id)}
                        className="p-2 h-8 w-8 flex items-center justify-center text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors absolute right-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
              </>
            )}
          </div>
        );

      case 'positions':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
                <h2 className="text-xl md:text-2xl font-bold">Positions</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {positionSearch.trim() 
                    ? `Showing ${positionCounts.filtered} of ${positionCounts.total} positions`
                    : `Manage job positions and roles (${positionCounts.filtered} of ${positionCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select 
                    value={positionFilter} 
                    onChange={(e) => setPositionFilter(e.target.value)}
                    className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({positionCounts.active})</option>
                    <option value="inactive">Inactive ({positionCounts.inactive})</option>
                    <option value="all">All ({positionCounts.total})</option>
                  </select>
                </div>
                <button
                  onClick={handleAddPosition}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Position
                </button>
              </div>
              </div>

            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search positions by title or description..."
                  value={positionSearch}
                  onChange={(e) => {
                    setPositionSearch(e.target.value);
                    setPositionPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                <div className="overflow-y-auto flex-1 min-h-0">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b border-neutral-200">
                        <th className="text-left p-4 font-medium text-neutral-700">Position</th>
                        <th className="text-left p-4 font-medium text-neutral-700">Description</th>
                        <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                    {(() => {
                      const sortedPositions = filteredPositions.filter((position: any) => position.id !== null && position.id !== undefined).sort((a: any, b: any) => a.title.localeCompare(b.title));
                      const totalPages = Math.ceil(sortedPositions.length / itemsPerPage);
                      const startIndex = (positionPage - 1) * itemsPerPage;
                      const paginatedPositions = sortedPositions.slice(startIndex, startIndex + itemsPerPage);
                      
                      return paginatedPositions.map((position: any) => (
                        <tr key={position.id} className="border-b border-neutral-100 last:border-b-0">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: position.color || '#3b82f6' }}
                              />
                              <span className="font-medium">{position.title}</span>
                              {!position.isActive && (
                                <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                              )}
                              </div>
                            </td>
                          <td className="p-4 text-neutral-600">{position.description || '-'}</td>
                          <td className="p-4">
                            <div className="flex gap-1 justify-end">
                                <button
                                  onClick={() => handleEditPosition(position)}
                                className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              {position.isActive ? (
                                <>
                                  <button
                                    onClick={() => handleDeactivatePosition(position.id)}
                                    className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                                  >
                                    Deactivate
                                </button>
                                <button
                                  onClick={() => handleDeletePosition(position.id)}
                                    className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleActivatePosition(position.id)}
                                  className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                >
                                  Activate
                                </button>
                              )}
                              </div>
                            </td>
                          </tr>
                        ));
                    })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {(() => {
                  const sortedPositions = filteredPositions.filter((position: any) => position.id !== null && position.id !== undefined).sort((a: any, b: any) => a.title.localeCompare(b.title));
                  const totalPages = Math.ceil(sortedPositions.length / itemsPerPage);
                  const startIndex = (positionPage - 1) * itemsPerPage;
                  
                  if (totalPages <= 1) return null;
                  
                  return (
                    <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                      <div className="text-sm text-neutral-700">
                        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedPositions.length)} of {sortedPositions.length} positions
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPositionPage(prev => Math.max(1, prev - 1))}
                          disabled={positionPage === 1}
                          className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-neutral-700">
                          Page {positionPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setPositionPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={positionPage >= totalPages}
                          className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden grid gap-4">
              {filteredPositions.filter((position: any) => position.id !== null && position.id !== undefined).sort((a: any, b: any) => a.title.localeCompare(b.title)).map((position: any) => (
                <div key={position.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: position.color || '#3b82f6' }}
                        />
                        <h3 className="text-lg font-medium">{position.title}</h3>
                        {!position.isActive && (
                          <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                        )}
                      </div>
                      {position.description && (
                        <p className="text-sm text-neutral-600 mt-1">{position.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEditPosition(position)}
                        className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {position.isActive ? (
                        <>
                          <button
                            onClick={() => handleDeactivatePosition(position.id)}
                            className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                          >
                            Deactivate
                          </button>
                          <button
                            onClick={() => handleDeletePosition(position.id)}
                            className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button
                                onClick={() => handleActivatePosition(position.id)}
                          className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'schedules':
        return (
          <div>
            <div className="flex gap-1 mb-6 border-b border-neutral-200">
              <button
                type="button"
                onClick={() => setSchedulesSubTab('schedules')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  schedulesSubTab === 'schedules'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Schedules
              </button>
              <button
                type="button"
                onClick={() => setSchedulesSubTab('shift-groups')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  schedulesSubTab === 'shift-groups'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Shift Groups
              </button>
            </div>
            {schedulesSubTab === 'shift-groups' ? (
              <ShiftGroupsSettingsPanel locations={locations} />
            ) : (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div>
                <h2 className="text-xl md:text-2xl font-bold">Schedules</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {(scheduleSearch.trim() || scheduleLocationFilter.length > 0)
                    ? `Showing ${scheduleCounts.filtered} of ${scheduleCounts.total} schedules`
                    : `Manage work schedules (${scheduleCounts.filtered} of ${scheduleCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select 
                    value={scheduleFilter} 
                    onChange={(e) => setScheduleFilter(e.target.value)}
                    className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({scheduleCounts.active})</option>
                    <option value="inactive">Inactive ({scheduleCounts.inactive})</option>
                    <option value="all">All ({scheduleCounts.total})</option>
                  </select>
                </div>
                <button
                  onClick={handleAddSchedule}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                Add Schedule
              </button>
              </div>
            </div>
            
            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search schedules by name or description..."
                  value={scheduleSearch}
                  onChange={(e) => {
                    setScheduleSearch(e.target.value);
                    setSchedulePage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Location Filter */}
            <div className="mb-4">
              <BasketSelect
                label="Filter by Location"
                availableItems={locations.filter(loc => loc.isActive).map(loc => ({
                  id: loc.id,
                  label: loc.name
                }))}
                selectedIds={scheduleLocationFilter}
                onChange={(ids) => {
                  setScheduleLocationFilter(ids.map(id => typeof id === 'string' ? parseInt(id, 10) : id));
                }}
                placeholder="Select locations to filter schedules..."
              />
              {scheduleLocationFilter.length > 0 && (
                <button
                  onClick={() => setScheduleLocationFilter([])}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  Clear filter
                </button>
              )}
            </div>

            {filteredSchedules.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <Calendar className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No schedules found</h3>
                <p className="text-neutral-600 mb-4">
                  {scheduleSearch.trim() || scheduleLocationFilter.length > 0
                    ? 'No schedules match your search criteria or location filter. Try adjusting your search or filters.'
                    : scheduleFilter === 'active' 
                      ? 'No active schedules found.' 
                      : scheduleFilter === 'inactive' 
                        ? 'No inactive schedules found.' 
                        : 'Get started by creating your first schedule.'}
                </p>
                <button
                  onClick={handleAddSchedule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Schedule
                </button>
            </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block">
                  <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="border-b border-neutral-200">
                            <th className="text-left p-4 font-medium text-neutral-700">Name</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Description</th>
                            <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const sortedSchedules = filteredSchedules.filter((schedule: any) => schedule.id !== null && schedule.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                            const totalPages = Math.ceil(sortedSchedules.length / itemsPerPage);
                            const startIndex = (schedulePage - 1) * itemsPerPage;
                            const paginatedSchedules = sortedSchedules.slice(startIndex, startIndex + itemsPerPage);
                            
                            return paginatedSchedules.map((schedule: any) => (
                              <tr key={schedule.id} className="border-b border-neutral-100 last:border-b-0">
                                <td className="p-4 font-medium">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-blue-600" />
                                    {schedule.name}
                                    {!schedule.isActive && (
                                      <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-4 text-neutral-600">
                                  {schedule.description || 'No description'}
                                </td>
                                <td className="p-4">
                                  <div className="flex gap-1 justify-end">
                                    <button
                                      onClick={() => handleEditSchedule(schedule)}
                                      className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    {schedule.isActive ? (
                                      <>
                                        <button
                                          onClick={() => handleDeactivateSchedule(schedule.id)}
                                          className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                                        >
                                          Deactivate
                                        </button>
                                        <button
                                          onClick={() => handleDeleteSchedule(schedule.id)}
                                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => handleActivateSchedule(schedule.id)}
                                        className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                      >
                                        Activate
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {(() => {
                      const sortedSchedules = filteredSchedules.filter((schedule: any) => schedule.id !== null && schedule.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                      const totalPages = Math.ceil(sortedSchedules.length / itemsPerPage);
                      const startIndex = (schedulePage - 1) * itemsPerPage;
                      
                      if (totalPages <= 1) return null;
                      
                      return (
                        <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                          <div className="text-sm text-neutral-700">
                            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedSchedules.length)} of {sortedSchedules.length} schedules
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSchedulePage(prev => Math.max(1, prev - 1))}
                              disabled={schedulePage === 1}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-neutral-700">
                              Page {schedulePage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setSchedulePage(prev => Math.min(totalPages, prev + 1))}
                              disabled={schedulePage >= totalPages}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden grid gap-4">
                  {filteredSchedules.filter((schedule: any) => schedule.id !== null && schedule.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name)).map((schedule: any) => (
                    <div key={schedule.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="h-4 w-4 text-blue-600" />
                            <h3 className="text-lg font-medium">{schedule.name}</h3>
                            {!schedule.isActive && (
                              <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                            )}
              </div>
                          <p className="text-sm text-neutral-600">
                            {schedule.description || 'No description'}
                          </p>
                        </div>
                        <div className="flex gap-1">
              <button
                            onClick={() => handleEditSchedule(schedule)}
                            className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                            <Edit className="h-4 w-4" />
              </button>
                          {schedule.isActive ? (
                            <>
                              <button
                                onClick={() => handleDeactivateSchedule(schedule.id)}
                                className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                              >
                                Deactivate
                              </button>
                              <button
                                onClick={() => handleDeleteSchedule(schedule.id)}
                                className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleActivateSchedule(schedule.id)}
                              className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                            >
                              Activate
                            </button>
                          )}
            </div>
            </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
            )}
          </div>
        );

      case 'payroll-departments':
        const filteredPayrollDepartments = payrollDepartments.filter((dept: PayrollDepartment) => {
          const matchesSearch = !payrollDepartmentSearch.trim() || 
            dept.name.toLowerCase().includes(payrollDepartmentSearch.toLowerCase()) ||
            (dept.description && dept.description.toLowerCase().includes(payrollDepartmentSearch.toLowerCase())) ||
            (dept.code && dept.code.toLowerCase().includes(payrollDepartmentSearch.toLowerCase()));
          
          const matchesFilter = payrollDepartmentFilter === 'all' ||
            (payrollDepartmentFilter === 'active' && dept.isActive) ||
            (payrollDepartmentFilter === 'inactive' && !dept.isActive);
          
          return matchesSearch && matchesFilter;
        });

        const payrollDepartmentCounts = {
          total: payrollDepartments.length,
          active: payrollDepartments.filter((d: PayrollDepartment) => d.isActive).length,
          inactive: payrollDepartments.filter((d: PayrollDepartment) => !d.isActive).length,
          filtered: filteredPayrollDepartments.length
        };

        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Payroll Departments</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {payrollDepartmentSearch.trim()
                    ? `Showing ${payrollDepartmentCounts.filtered} of ${payrollDepartmentCounts.total} departments`
                    : `Manage payroll departments (${payrollDepartmentCounts.filtered} of ${payrollDepartmentCounts.total} showing)`}
                </p>
              </div>
              <button
                onClick={handleAddPayrollDepartment}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Payroll Department
              </button>
            </div>
            
            {/* Search and Filter */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search departments by name, description, or code..."
                  value={payrollDepartmentSearch}
                  onChange={(e) => {
                    setPayrollDepartmentSearch(e.target.value);
                    setPayrollDepartmentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setPayrollDepartmentFilter('all');
                    setPayrollDepartmentPage(1);
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    payrollDepartmentFilter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  All ({payrollDepartmentCounts.total})
                </button>
                <button
                  onClick={() => {
                    setPayrollDepartmentFilter('active');
                    setPayrollDepartmentPage(1);
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    payrollDepartmentFilter === 'active'
                      ? 'bg-green-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  Active ({payrollDepartmentCounts.active})
                </button>
                <button
                  onClick={() => {
                    setPayrollDepartmentFilter('inactive');
                    setPayrollDepartmentPage(1);
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    payrollDepartmentFilter === 'inactive'
                      ? 'bg-orange-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  Inactive ({payrollDepartmentCounts.inactive})
                </button>
              </div>
            </div>

            {filteredPayrollDepartments.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <Currency className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No payroll departments found</h3>
                <p className="text-neutral-600 mb-4">
                  {payrollDepartmentSearch.trim()
                    ? 'No departments match your search criteria. Try adjusting your search.'
                    : 'Get started by creating your first payroll department.'}
                </p>
                <button
                  onClick={handleAddPayrollDepartment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Payroll Department
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block">
                  <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="border-b border-neutral-200">
                            <th className="p-4 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                            <th className="p-4 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Code</th>
                            <th className="p-4 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Description</th>
                            <th className="p-4 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                            <th className="p-4 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                          {(() => {
                            const sortedDepartments = filteredPayrollDepartments
                              .filter((d: PayrollDepartment) => d.id !== null && d.id !== undefined)
                              .sort((a: PayrollDepartment, b: PayrollDepartment) => a.name.localeCompare(b.name));
                            const totalPages = Math.ceil(sortedDepartments.length / itemsPerPage);
                            const startIndex = (payrollDepartmentPage - 1) * itemsPerPage;
                            const paginatedDepartments = sortedDepartments.slice(startIndex, startIndex + itemsPerPage);
                            
                            return paginatedDepartments.map((dept: PayrollDepartment) => (
                              <tr key={dept.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                                <td className="p-4 font-medium">{dept.name}</td>
                                <td className="p-4 text-neutral-600">{dept.code || '-'}</td>
                                <td className="p-4 text-neutral-600">{dept.description || '-'}</td>
                                <td className="p-4">
                                  {dept.isActive ? (
                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Active</span>
                                  ) : (
                                    <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                                  )}
                                </td>
                                <td className="p-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => handleEditPayrollDepartment(dept)}
                                      className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    {dept.isActive && (
                                      <button
                                        onClick={() => handleDeletePayrollDepartment(dept.id)}
                                        className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {(() => {
                      const sortedDepartments = filteredPayrollDepartments
                        .filter((d: PayrollDepartment) => d.id !== null && d.id !== undefined)
                        .sort((a: PayrollDepartment, b: PayrollDepartment) => a.name.localeCompare(b.name));
                      const totalPages = Math.ceil(sortedDepartments.length / itemsPerPage);
                      const startIndex = (payrollDepartmentPage - 1) * itemsPerPage;
                      
                      if (totalPages <= 1) return null;
                      
                      return (
                        <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                          <div className="text-sm text-neutral-700">
                            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedDepartments.length)} of {sortedDepartments.length} departments
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setPayrollDepartmentPage(prev => Math.max(1, prev - 1))}
                              disabled={payrollDepartmentPage === 1}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-neutral-700">
                              Page {payrollDepartmentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setPayrollDepartmentPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={payrollDepartmentPage >= totalPages}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden grid gap-4">
                  {filteredPayrollDepartments
                    .filter((d: PayrollDepartment) => d.id !== null && d.id !== undefined)
                    .sort((a: PayrollDepartment, b: PayrollDepartment) => a.name.localeCompare(b.name))
                    .map((dept: PayrollDepartment) => (
                    <div key={dept.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-medium">{dept.name}</h3>
                            {dept.isActive ? (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Active</span>
                            ) : (
                              <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                            )}
                          </div>
                          {dept.code && (
                            <p className="text-sm text-neutral-600 mt-1">Code: {dept.code}</p>
                          )}
                          {dept.description && (
                            <p className="text-sm text-neutral-600 mt-1">{dept.description}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditPayrollDepartment(dept)}
                            className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {dept.isActive && (
                            <button
                              onClick={() => handleDeletePayrollDepartment(dept.id)}
                              className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case 'shift-templates':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Shift Templates</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {(shiftTemplateSearch.trim() || shiftTemplateLocationFilter.length > 0 || shiftTemplateFilter !== 'all')
                    ? `Showing ${shiftTemplateCounts.filtered} of ${shiftTemplateCounts.total} templates`
                    : `Manage reusable shift templates (${shiftTemplateCounts.filtered} of ${shiftTemplateCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select
                    value={shiftTemplateFilter}
                    onChange={(e) => {
                      setShiftTemplateFilter(e.target.value);
                      setShiftTemplatePage(1);
                    }}
                    className="w-36 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({shiftTemplateCounts.active})</option>
                    <option value="inactive">Inactive ({shiftTemplateCounts.inactive})</option>
                    <option value="all">All ({shiftTemplateCounts.total})</option>
                  </select>
                </div>
                <button
                  onClick={handleAddShiftTemplate}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Template
                </button>
              </div>
            </div>
            
            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search templates by name or time..."
                  value={shiftTemplateSearch}
                  onChange={(e) => {
                    setShiftTemplateSearch(e.target.value);
                    setShiftTemplatePage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Location Filter */}
            <div className="mb-4">
              <BasketSelect
                label="Filter by Location"
                availableItems={locations.filter(loc => loc.isActive).map(loc => ({
                  id: loc.id,
                  label: loc.name
                }))}
                selectedIds={shiftTemplateLocationFilter}
                onChange={(ids) => {
                  setShiftTemplateLocationFilter(ids.map(id => typeof id === 'string' ? parseInt(id, 10) : id));
                }}
                placeholder="Select locations to filter templates..."
              />
              {shiftTemplateLocationFilter.length > 0 && (
                <button
                  onClick={() => setShiftTemplateLocationFilter([])}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  Clear filter
                </button>
              )}
            </div>

            {filteredShiftTemplates.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <Clock className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No templates found</h3>
                <p className="text-neutral-600 mb-4">
                  {(shiftTemplateSearch.trim() || shiftTemplateLocationFilter.length > 0 || shiftTemplateFilter !== 'all')
                    ? 'No templates match your search criteria or filters. Try adjusting your search or filters.'
                    : 'Get started by creating your first shift template.'}
                </p>
                <button
                  onClick={handleAddShiftTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Template
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block">
                  <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="border-b border-neutral-200">
                            <th className="text-left p-4 font-medium text-neutral-700">Template</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Time Range</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Break (mins)</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Total Hours</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Status</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Location</th>
                            <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const totalPages = Math.ceil(filteredShiftTemplates.length / itemsPerPage);
                            const startIndex = (shiftTemplatePage - 1) * itemsPerPage;
                            const paginatedTemplates = filteredShiftTemplates.slice(startIndex, startIndex + itemsPerPage);
                            
                            return paginatedTemplates.map((template) => {
                              const displayColor = resolveShiftTemplateColor(template, shiftTemplateColorMap);
                              return (
                              <tr
                                key={template.id}
                                className={`border-b border-neutral-100 last:border-b-0 ${template.isActive === false ? 'opacity-60' : ''}`}
                                style={{
                                  backgroundColor: shiftTemplateLightBackground(displayColor),
                                  borderLeft: `4px solid ${displayColor}`,
                                }}
                              >
                                <td className="p-4">
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-4 h-4 rounded-full"
                                      style={{ backgroundColor: displayColor }}
                                    />
                                    <span className="font-medium">{template.name || `${template.startTime} - ${template.endTime}`}</span>
                                  </div>
                                </td>
                                <td className="p-4 text-neutral-600">
                                  {formatTime12Hour(template.startTime)} - {formatTime12Hour(template.endTime)}
                                </td>
                                <td className="p-4 text-neutral-600">
                                  {template.breakUseGlobalSettings !== false ? 'Global' : (template.unpaidBreakMins || 0)}
                                </td>
                                <td className="p-4 text-neutral-600">
                                  {(template.totalHours || template.hours).toFixed(2)}
                                </td>
                                <td className="p-4">
                                  {template.isActive !== false ? (
                                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded bg-neutral-100 text-neutral-600">
                                      Inactive
                                    </span>
                                  )}
                                </td>
                                <td className="p-4 text-neutral-600">
                                  {template.locationIds && template.locationIds.length > 0
                                    ? template.locationIds.map(locId => locations.find(loc => loc.id === locId)?.name).filter(Boolean).join(', ') || 'Unknown'
                                    : 'No locations'}
                                </td>
                                <td className="p-4">
                                  <div className="flex gap-1 justify-end">
                                    <button
                                      onClick={() => handleEditShiftTemplate(template)}
                                      className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteShiftTemplate(template.id)}
                                      className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {(() => {
                      const totalPages = Math.ceil(filteredShiftTemplates.length / itemsPerPage);
                      const startIndex = (shiftTemplatePage - 1) * itemsPerPage;
                      
                      if (totalPages <= 1) return null;
                      
                      return (
                        <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                          <div className="text-sm text-neutral-700">
                            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredShiftTemplates.length)} of {filteredShiftTemplates.length} templates
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setShiftTemplatePage(prev => Math.max(1, prev - 1))}
                              disabled={shiftTemplatePage === 1}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-neutral-700">
                              Page {shiftTemplatePage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setShiftTemplatePage(prev => Math.min(totalPages, prev + 1))}
                              disabled={shiftTemplatePage >= totalPages}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden grid gap-4">
                  {filteredShiftTemplates.map((template) => {
                    const displayColor = resolveShiftTemplateColor(template, shiftTemplateColorMap);
                    return (
                    <div
                      key={template.id}
                      className={`border rounded-lg p-4 ${template.isActive === false ? 'opacity-60' : ''}`}
                      style={{
                        backgroundColor: shiftTemplateLightBackground(displayColor),
                        borderColor: displayColor,
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: displayColor }}
                            />
                            <h3 className="text-lg font-medium">{template.name || `${template.startTime} - ${template.endTime}`}</h3>
                            {template.isActive !== false ? (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-neutral-100 text-neutral-600">
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-neutral-600 mb-1">
                            {formatTime12Hour(template.startTime)} - {formatTime12Hour(template.endTime)}
                          </p>
                          <p className="text-sm text-neutral-600">
                            Break: {template.breakUseGlobalSettings !== false ? 'Global settings' : `${template.unpaidBreakMins || 0} mins`} • Hours: {(template.totalHours || template.hours).toFixed(2)}
                          </p>
                          <p className="text-sm text-neutral-500 mt-1">
                            Locations: {template.locationIds && template.locationIds.length > 0
                              ? template.locationIds.map(locId => locations.find(loc => loc.id === locId)?.name).filter(Boolean).join(', ') || 'Unknown'
                              : 'No locations'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditShiftTemplate(template)}
                            className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteShiftTemplate(template.id)}
                            className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Modal */}
            {showShiftTemplateModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                  <h2 className="text-xl font-bold mb-4">
                    {editingShiftTemplate ? 'Edit Template' : 'Add Template'}
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Template Name
                      </label>
                      <input
                        type="text"
                        value={shiftTemplateForm.name}
                        onChange={(e) => setShiftTemplateForm({ ...shiftTemplateForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Morning Shift"
                      />
                    </div>

                    <div>
                      <BasketSelect
                        label="Locations"
                        availableItems={locations.filter(loc => loc.isActive).map(loc => ({
                          id: loc.id,
                          label: loc.name
                        }))}
                        selectedIds={shiftTemplateForm.locationIds || []}
                        onChange={(ids) => {
                          setShiftTemplateForm({ 
                            ...shiftTemplateForm, 
                            locationIds: ids.map(id => typeof id === 'string' ? parseInt(id, 10) : id)
                          });
                        }}
                        placeholder="Select locations..."
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={shiftTemplateForm.startTime}
                          onChange={(e) => handleShiftTemplateTimeChange('startTime', e.target.value)}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={shiftTemplateForm.endTime}
                          onChange={(e) => handleShiftTemplateTimeChange('endTime', e.target.value)}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Break Policy
                      </label>
                      <div className="flex flex-wrap gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="templateBreakPolicy"
                            checked={shiftTemplateForm.breakUseGlobalSettings !== false}
                            onChange={() => {
                              const spanHours = calculateHours(shiftTemplateForm.startTime, shiftTemplateForm.endTime, 0);
                              setShiftTemplateForm({
                                ...shiftTemplateForm,
                                breakUseGlobalSettings: true,
                                hours: spanHours,
                                totalHours: spanHours,
                              });
                            }}
                            className="text-blue-600"
                          />
                          <span className="text-sm">Global Settings</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="templateBreakPolicy"
                            checked={shiftTemplateForm.breakUseGlobalSettings === false}
                            onChange={() => {
                              setBreakMinsManuallySet(false);
                              const newBreakMins = shiftTemplateForm.startTime && shiftTemplateForm.endTime
                                ? calculateDefaultBreakMinutes(shiftTemplateForm.startTime, shiftTemplateForm.endTime)
                                : 0;
                              const newHours = calculateHours(shiftTemplateForm.startTime, shiftTemplateForm.endTime, newBreakMins);
                              setShiftTemplateForm({
                                ...shiftTemplateForm,
                                breakUseGlobalSettings: false,
                                unpaidBreakMins: newBreakMins,
                                breakTimePaid: breakTimeSettings?.isPaid ?? false,
                                hours: newHours,
                                totalHours: newHours,
                              });
                            }}
                            className="text-blue-600"
                          />
                          <span className="text-sm">Override</span>
                        </label>
                      </div>
                      {shiftTemplateForm.breakUseGlobalSettings !== false ? (
                        <>
                          <p className="text-xs text-neutral-500 mb-2">
                            From Settings → Break Policy (preview for this shift length)
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-1">
                                Break (minutes)
                              </label>
                              <input
                                type="text"
                                value={
                                  shiftTemplateForm.startTime && shiftTemplateForm.endTime
                                    ? calculateDefaultBreakMinutes(shiftTemplateForm.startTime, shiftTemplateForm.endTime)
                                    : '—'
                                }
                                disabled
                                className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-50 text-neutral-700"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-1">
                                Break Time Paid
                              </label>
                              <input
                                type="text"
                                value={breakTimeSettings?.isPaid ? 'Paid' : 'Unpaid'}
                                disabled
                                className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-50 text-neutral-700"
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Break (minutes)
                            </label>
                            <input
                              type="number"
                              value={shiftTemplateForm.unpaidBreakMins || 0}
                              onChange={(e) => handleShiftTemplateBreakChange(parseInt(e.target.value) || 0)}
                              min="0"
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              Break Time Paid
                            </label>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setShiftTemplateForm({ ...shiftTemplateForm, breakTimePaid: !shiftTemplateForm.breakTimePaid })}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  shiftTemplateForm.breakTimePaid ? 'bg-blue-600' : 'bg-neutral-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    shiftTemplateForm.breakTimePaid ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                              <span className="text-sm text-neutral-600">
                                {shiftTemplateForm.breakTimePaid ? 'Paid' : 'Unpaid'}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Color
                      </label>
                      <div className="flex gap-2">
                        {['#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#f97316', '#10b981'].map(color => (
                          <button
                            key={color}
                            onClick={() => setShiftTemplateForm({ ...shiftTemplateForm, color })}
                            className={`w-8 h-8 rounded-full border-2 ${
                              shiftTemplateForm.color === color ? 'border-neutral-900' : 'border-neutral-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        <input
                          type="color"
                          value={shiftTemplateForm.color}
                          onChange={(e) => setShiftTemplateForm({ ...shiftTemplateForm, color: e.target.value })}
                          className="w-8 h-8 rounded border border-neutral-300 cursor-pointer"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Total Hours (calculated)
                      </label>
                      <input
                        type="text"
                        value={(shiftTemplateForm.totalHours || shiftTemplateForm.hours).toFixed(2)}
                        disabled
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-50 text-neutral-600"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowShiftTemplateModal(false);
                        setEditingShiftTemplate(null);
                      }}
                      className="px-4 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveShiftTemplate}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'time-stations':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Time Stations</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {timeStationSearch.trim() 
                    ? `Showing ${timeStationCounts.filtered} of ${timeStationCounts.total} time stations`
                    : `Manage iPad time stations for employee clock in/out (${timeStationCounts.filtered} of ${timeStationCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select 
                    value={timeStationFilter} 
                    onChange={(e) => setTimeStationFilter(e.target.value)}
                    className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({timeStationCounts.active})</option>
                    <option value="inactive">Inactive ({timeStationCounts.inactive})</option>
                    <option value="pending">Pending ({timeStationCounts.pending})</option>
                    <option value="all">All ({timeStationCounts.total})</option>
                  </select>
                  {timeStationCounts.pending > 0 && (
                    <button
                      onClick={() => setTimeStationFilter('pending')}
                      className="relative px-3 py-2 bg-orange-100 text-orange-700 text-sm font-medium rounded-md hover:bg-orange-200 transition-colors flex items-center gap-2"
                    >
                      <span>Pending</span>
                      <span className="bg-orange-600 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                        {timeStationCounts.pending}
                      </span>
                    </button>
                  )}
              </div>
              <button
                  onClick={handleAddTimeStation}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Time Station
              </button>
            </div>
            </div>

            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search time stations by name, device ID, IP address, or location..."
                  value={timeStationSearch}
                  onChange={(e) => setTimeStationSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
          </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                <div className="overflow-y-auto flex-1 min-h-0">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-neutral-50 z-10">
                      <tr className="border-b border-neutral-200">
                        <th className="text-left p-4 font-medium text-neutral-700">Name</th>
                        <th className="text-left p-4 font-medium text-neutral-700">Device ID</th>
                        <th className="text-left p-4 font-medium text-neutral-700">Location</th>
                        <th className="text-left p-4 font-medium text-neutral-700">IP Address</th>
                        <th className="text-left p-4 font-medium text-neutral-700">Features</th>
                        <th className="text-left p-4 font-medium text-neutral-700">Status</th>
                        <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const sortedTimeStations = filteredTimeStations.filter((timeStation: any) => timeStation.id !== null && timeStation.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                        const totalPages = Math.ceil(sortedTimeStations.length / itemsPerPage);
                        const startIndex = (timeStationPage - 1) * itemsPerPage;
                        const paginatedTimeStations = sortedTimeStations.slice(startIndex, startIndex + itemsPerPage);
                        
                        return paginatedTimeStations.map((timeStation: any) => (
                          <tr key={timeStation.id} className="border-b border-neutral-100 last:border-b-0">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <Monitor className="h-4 w-4" />
                                <div>
                                  <div className="font-medium">{timeStation.name}</div>
                                  {timeStation.description && (
                                    <div className="text-sm text-neutral-600">{timeStation.description}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <code className="bg-neutral-100 px-2 py-1 rounded text-sm">{timeStation.deviceId}</code>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {timeStation.location?.name || 'Unknown'}
                              </div>
                            </td>
                            <td className="p-4">
                              <code className="bg-neutral-100 px-2 py-1 rounded text-sm">{timeStation.ipAddress || 'N/A'}</code>
                            </td>
                            <td className="p-4">
                              <div className="flex gap-1">
                                {timeStation.allowPhotoCapture && (
                                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">Photo</span>
                                )}
                                {timeStation.locationTracking && (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">GPS</span>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              {timeStation.status === 'Pending' ? (
                                <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">
                                  Pending
                                </span>
                              ) : (
                                <span className={`px-2 py-1 text-xs rounded ${timeStation.isActive ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'}`}>
                                  {timeStation.isActive ? 'Active' : 'Inactive'}
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={() => handleEditTimeStation(timeStation)}
                                  className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                {timeStation.status === 'Pending' ? (
                                  <>
                                    <button
                                      onClick={() => handleApproveTimeStation(timeStation.id)}
                                      className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTimeStation(timeStation.id)}
                                      className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                ) : timeStation.isActive ? (
                                  <>
                                    <button
                                      onClick={() => handleDeactivateTimeStation(timeStation.id)}
                                      className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                                    >
                                      Deactivate
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTimeStation(timeStation.id)}
                                      className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleActivateTimeStation(timeStation.id)}
                                      className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                    >
                                      Activate
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTimeStation(timeStation.id)}
                                      className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {(() => {
                  const sortedTimeStations = filteredTimeStations.filter((timeStation: any) => timeStation.id !== null && timeStation.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                  const totalPages = Math.ceil(sortedTimeStations.length / itemsPerPage);
                  const startIndex = (timeStationPage - 1) * itemsPerPage;
                  
                  if (totalPages <= 1) return null;
                  
                  return (
                    <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                      <div className="text-sm text-neutral-700">
                        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedTimeStations.length)} of {sortedTimeStations.length} time stations
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setTimeStationPage(prev => Math.max(1, prev - 1))}
                          disabled={timeStationPage === 1}
                          className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-neutral-700">
                          Page {timeStationPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setTimeStationPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={timeStationPage >= totalPages}
                          className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden grid gap-4">
              {filteredTimeStations.filter((timeStation: any) => timeStation.id !== null && timeStation.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name)).map((timeStation: any) => (
                <div key={timeStation.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4" />
                      <h3 className="text-lg font-medium">{timeStation.name}</h3>
                    </div>
                    {timeStation.status === 'Pending' ? (
                      <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">
                        Pending
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-xs rounded ${timeStation.isActive ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'}`}>
                        {timeStation.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </div>
                  
                  {timeStation.description && (
                    <p className="text-sm text-neutral-600 mb-3">{timeStation.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
              <div>
                      <div className="text-xs text-neutral-500 mb-1">Device ID</div>
                      <code className="bg-neutral-100 px-2 py-1 rounded text-xs">{timeStation.deviceId}</code>
              </div>
                    <div>
                      <div className="text-xs text-neutral-500 mb-1">Location</div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span className="text-xs">{timeStation.location?.name || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                  
                  {timeStation.ipAddress && (
                    <div className="mb-3">
                      <div className="text-xs text-neutral-500 mb-1">IP Address</div>
                      <code className="bg-neutral-100 px-2 py-1 rounded text-xs">{timeStation.ipAddress}</code>
                    </div>
                  )}

                  <div className="flex gap-1 mb-3">
                    {timeStation.allowPhotoCapture && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">Photo Required</span>
                    )}
                    {timeStation.locationTracking && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">GPS Tracking</span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditTimeStation(timeStation)}
                      className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </button>
                    {timeStation.status === 'Pending' ? (
                      <>
                        <button
                          onClick={() => handleApproveTimeStation(timeStation.id)}
                          className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleDeleteTimeStation(timeStation.id)}
                          className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </>
                    ) : (
                      <>
                        {timeStation.isActive ? (
                          <button
                            onClick={() => handleDeactivateTimeStation(timeStation.id)}
                            className="flex-1 px-3 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivateTimeStation(timeStation.id)}
                            className="flex-1 px-3 py-2 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                          >
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteTimeStation(timeStation.id)}
                          className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
            </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredTimeStations.length === 0 && (
              <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center">
                <Monitor className="h-12 w-12 mx-auto mb-4 text-neutral-400" />
                <h3 className="text-lg font-medium mb-2">No Time Stations</h3>
                <p className="text-neutral-600 mb-4">
                  {timeStationSearch.trim() 
                    ? 'No time stations match your search criteria. Try adjusting your search or filters.'
                    : 'Create your first time station to enable iPad-based employee clock in/out.'}
                </p>
                <button
                  onClick={handleAddTimeStation}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Time Station
                </button>
              </div>
            )}
          </div>
        );

      case 'attendance-settings':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-bold">Attendance Settings</h2>
              <p className="text-sm md:text-base text-neutral-600">Configure attendance rules, break time settings, and week start day</p>
            </div>

            <div className="space-y-6">
              {/* Week Start Day Setting */}
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Week Calendar Settings</h3>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Week Start Day
                    </label>
                    <p className="text-sm text-neutral-600">
                      Choose which day of the week should be the first day in all week calendars
                    </p>
                  </div>
                  <div className="w-full md:w-48">
                    <select
                      value={weekStartDay}
                      onChange={(e) => handleWeekStartDayChange(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Break policy — company default */}
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Break Policy</h3>
                <p className="text-sm text-neutral-600 mb-4">Global break rules for all locations. Sync applies policy to timecard Paid / Unpaid break columns.</p>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 pb-6 border-b border-neutral-100">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Default for unscheduled rows
                    </label>
                    <p className="text-sm text-neutral-600">
                      Used when a timecard row has no shift break pay override
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${!isPaid ? 'font-medium' : 'text-neutral-500'}`}>Unpaid</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPaid}
                        onChange={(e) => setIsPaid(e.target.checked)}
                        disabled={savingBreakTime}
                        className="sr-only peer disabled:opacity-50"
                      />
                      <div className={`w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${savingBreakTime ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                    </label>
                    <span className={`text-sm ${isPaid ? 'font-medium' : 'text-neutral-500'}`}>Paid</span>
                  </div>
                </div>

                <div className="mb-6 pb-6 border-b border-neutral-100">
                  <h4 className="text-sm font-semibold text-neutral-800 mb-3">Unpaid break policy</h4>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={unpaidPunchRequired}
                        onChange={(e) => setUnpaidPunchRequired(e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-medium text-neutral-700">Break punches required</span>
                        <span className="block text-xs text-neutral-500">Unpaid break = punched gaps only (no rule minimum)</span>
                      </span>
                    </label>
                    {!unpaidPunchRequired && (
                      <>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={unpaidForceMinFromRules}
                            onChange={(e) => setUnpaidForceMinFromRules(e.target.checked)}
                            className="mt-1"
                          />
                          <span>
                            <span className="block text-sm font-medium text-neutral-700">Force minimum break from rules when no punch</span>
                            <span className="block text-xs text-neutral-500">Apply rule-table minimum even if Actual break = 0</span>
                          </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={unpaidUseShiftBreakAsFloor}
                            onChange={(e) => setUnpaidUseShiftBreakAsFloor(e.target.checked)}
                            className="mt-1"
                          />
                          <span>
                            <span className="block text-sm font-medium text-neutral-700">Use shift scheduled break as floor (full shift)</span>
                            <span className="block text-xs text-neutral-500">On full shift only: Unpaid break = max(rule, shift break)</span>
                          </span>
                        </label>
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-2">
                  <h4 className="text-sm font-semibold text-neutral-800 mb-1">Paid break policy</h4>
                  <p className="text-xs text-neutral-500 mb-3">Allowance from rules; excess punched break goes to Unpaid break on sync.</p>
                </div>
              </div>

              {/* Break Time Rules */}
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold">Break Time Rules</h3>
                  <button
                    onClick={addBreakTimeRule}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 w-full sm:w-auto"
                  >
                    <Plus className="h-4 w-4" />
                    Add Rule
                  </button>
                </div>
                <p className="text-sm text-neutral-600 mb-4">
                  Define break time allowances based on hours worked. Rules apply to all locations and are calculated automatically based on employee shift duration.
                </p>
                
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto mb-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="text-left p-3 font-medium text-neutral-700">Min Hours</th>
                        <th className="text-left p-3 font-medium text-neutral-700">Max Hours</th>
                        <th className="text-left p-3 font-medium text-neutral-700">Break Minutes</th>
                        <th className="text-right p-3 font-medium text-neutral-700 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakTimeRules.map((rule, index) => (
                        <tr key={index} className="border-b border-neutral-100 last:border-b-0">
                          <td className="p-3">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={rule.minHours}
                              onChange={(e) => updateBreakTimeRule(index, 'minHours', parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="p-3">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={rule.maxHours}
                              onChange={(e) => updateBreakTimeRule(index, 'maxHours', parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="p-3">
                            <input
                              type="number"
                              min="0"
                              step="5"
                              value={rule.breakMinutes}
                              onChange={(e) => updateBreakTimeRule(index, 'breakMinutes', parseInt(e.target.value) || 0)}
                              className="w-24 px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="p-3">
                            <button
                              onClick={() => removeBreakTimeRule(index)}
                              disabled={breakTimeRules.length <= 1}
                              className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3 mb-4">
                  {breakTimeRules.map((rule, index) => (
                    <div key={index} className="border-2 border-neutral-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold">Rule {index + 1}</span>
                        <button
                          onClick={() => removeBreakTimeRule(index)}
                          disabled={breakTimeRules.length <= 1}
                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
              <div>
                          <label className="block text-xs text-neutral-600 mb-1">Min Hours</label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            value={rule.minHours}
                            onChange={(e) => updateBreakTimeRule(index, 'minHours', parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
              </div>
                        <div>
                          <label className="block text-xs text-neutral-600 mb-1">Max Hours</label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            value={rule.maxHours}
                            onChange={(e) => updateBreakTimeRule(index, 'maxHours', parseFloat(e.target.value) || 0)}
                            className="w-full px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
            </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-neutral-600 mb-1">Break Minutes</label>
                          <input
                            type="number"
                            min="0"
                            step="5"
                            value={rule.breakMinutes}
                            onChange={(e) => updateBreakTimeRule(index, 'breakMinutes', parseInt(e.target.value) || 0)}
                            className="w-full px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-neutral-200">
                  <button
                    onClick={handleSaveBreakTimeSettings}
                    disabled={savingBreakTime}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4" />
                    {savingBreakTime ? 'Saving...' : 'Save Break Time Settings'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'time-cards':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Time Card Settings</h2>
                <p className="text-sm md:text-base text-neutral-600">Configure how employee timecards are displayed and calculated</p>
              </div>
            </div>

            <div className="space-y-4 md:space-y-6">
              {/* Employee Timecards Section */}
              <div className="bg-white border border-neutral-200 rounded-lg">
                <div className="p-4 md:p-6 border-b border-neutral-200">
                  <h3 className="text-lg md:text-xl font-semibold">Employee Timecards</h3>
                </div>
                <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                  {/* Note: Week Start Day is now managed in Attendance Settings as a universal setting */}
                  
                  {/* Time Display Format */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Time Display Format</label>
                      <p className="text-xs md:text-sm text-neutral-600">How should times be shown on timecards?</p>
                    </div>
                    <div>
                      <select
                        value={timeCardSettings.timeDisplayFormat}
                        onChange={(e) => setTimeCardSettings({ ...timeCardSettings, timeDisplayFormat: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="punch_times">Punch Times</option>
                        <option value="schedule_times">Schedule Times</option>
                      </select>
                    </div>
                  </div>

                  {/* Sunday Hours */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Sunday Hours Tracking</label>
                      <p className="text-xs md:text-sm text-neutral-600">Track Sunday hours separately from regular hours?</p>
                    </div>
                    <div className="flex gap-4 md:gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="sundayHours"
                          value="yes"
                          checked={timeCardSettings.separateSundayHours}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, separateSundayHours: true })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="sundayHours"
                          value="no"
                          checked={!timeCardSettings.separateSundayHours}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, separateSundayHours: false })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">No</span>
                      </label>
                    </div>
                  </div>

                  {/* Include Positions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Employee Positions</label>
                      <p className="text-xs md:text-sm text-neutral-600">Include employee positions on timecards?</p>
                    </div>
                    <div className="flex gap-4 md:gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="includePositions"
                          value="yes"
                          checked={timeCardSettings.includePositions}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, includePositions: true })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="includePositions"
                          value="no"
                          checked={!timeCardSettings.includePositions}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, includePositions: false })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clock In Times Section */}
              <div className="bg-white border border-neutral-200 rounded-lg">
                <div className="p-4 md:p-6 border-b border-neutral-200">
                  <h3 className="text-lg md:text-xl font-semibold">Clock In Times</h3>
                </div>
                <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                  {/* Clock In Earlier */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Clock In Earlier Than Schedule</label>
                      <p className="text-xs md:text-sm text-neutral-600">When employee clocks in before schedule time</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockInEarly"
                          value="schedule_time"
                          checked={timeCardSettings.clockInEarlierThan === 'schedule_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockInEarlierThan: 'schedule_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Schedule Time</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockInEarly"
                          value="punch_time"
                          checked={timeCardSettings.clockInEarlierThan === 'punch_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockInEarlierThan: 'punch_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Punch Time</span>
                      </label>
                    </div>
                  </div>

                  {/* Clock In Later */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Clock In Later Than Schedule</label>
                      <p className="text-xs md:text-sm text-neutral-600">When employee clocks in after schedule time</p>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 md:gap-6 sm:items-center">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockInLate"
                          value="schedule_time"
                          checked={timeCardSettings.clockInLaterThan === 'schedule_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockInLaterThan: 'schedule_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Schedule Time</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockInLate"
                          value="punch_time"
                          checked={timeCardSettings.clockInLaterThan === 'punch_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockInLaterThan: 'punch_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Punch Time</span>
                      </label>
                      {timeCardSettings.clockInLaterThan === 'punch_time' && (
                        <label className="flex items-center space-x-2">
                          <span className="text-sm whitespace-nowrap">Grace time (mins)</span>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={timeCardSettings.clockInLateGraceMinutes ?? 0}
                            onChange={(e) => {
                              const parsed = parseInt(e.target.value, 10);
                              setTimeCardSettings({
                                ...timeCardSettings,
                                clockInLateGraceMinutes: Number.isNaN(parsed) || parsed < 0 ? 0 : parsed,
                              });
                            }}
                            className="w-20 px-2 py-1 border border-neutral-300 rounded text-sm"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Clock Out Times Section */}
              <div className="bg-white border border-neutral-200 rounded-lg">
                <div className="p-4 md:p-6 border-b border-neutral-200">
                  <h3 className="text-lg md:text-xl font-semibold">Clock Out Times</h3>
                </div>
                <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                  {/* Clock Out Earlier */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Clock Out Earlier Than Schedule</label>
                      <p className="text-xs md:text-sm text-neutral-600">When employee clocks out before schedule time</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockOutEarly"
                          value="schedule_time"
                          checked={timeCardSettings.clockOutEarlierThan === 'schedule_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockOutEarlierThan: 'schedule_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Schedule Time</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockOutEarly"
                          value="punch_time"
                          checked={timeCardSettings.clockOutEarlierThan === 'punch_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockOutEarlierThan: 'punch_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Punch Time</span>
                      </label>
                    </div>
                  </div>

                  {/* Clock Out Later */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                    <div>
                      <label className="text-sm md:text-base font-medium block mb-1">Clock Out Later Than Schedule</label>
                      <p className="text-xs md:text-sm text-neutral-600">When employee clocks out after schedule time</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockOutLate"
                          value="schedule_time"
                          checked={timeCardSettings.clockOutLaterThan === 'schedule_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockOutLaterThan: 'schedule_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Schedule Time</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="clockOutLate"
                          value="punch_time"
                          checked={timeCardSettings.clockOutLaterThan === 'punch_time'}
                          onChange={() => setTimeCardSettings({ ...timeCardSettings, clockOutLaterThan: 'punch_time' })}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Punch Time</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveTimeCardSettings}
                  disabled={savingTimeCardSettings}
                  className="px-6 md:px-8 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  <Save className="h-4 w-4" />
                  {savingTimeCardSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'audit-logs':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Audit Logs</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  Configure which entity actions and fields are captured in audit logs
                </p>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg p-4 md:p-6 mb-6 space-y-4">
              <form onSubmit={handleAuditLogSubmit} className="space-y-4">
                <BasketSelect
                  label="Entities"
                  availableItems={auditLogEntities.map((entity) => ({ id: entity, label: entity }))}
                  selectedIds={auditLogForm.entities}
                  onChange={(ids) =>
                    setAuditLogForm({
                      entities: ids.filter((id) => auditLogEntities.includes(id as AuditLogEntity)) as AuditLogEntity[],
                    })
                  }
                  required={true}
                  placeholder="Select entities to capture..."
                />

                <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                  Selecting entities will capture all actions and fields for those entities.
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetAuditLogForm}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Save Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        );

      case 'feature-access':
        if (!hasPermission('settings')) {
          return (
            <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
              You need the Settings permission to manage company feature access.
            </div>
          );
        }
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-bold">Feature Access</h2>
              <p className="text-sm md:text-base text-neutral-600 mt-1">
                Enable optional company features. Some toggles also require role permissions (for example Security
                Command and Control Log). Healthcare options are intended for clinical / locum staffing tenants.
                Attendance-related features apply to mobile and web punch-in flows.
              </p>
            </div>
            <div className="bg-white border border-neutral-200 rounded-lg p-4 md:p-6 max-w-xl space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-neutral-900">Security Command and Control Log</div>
                  <p className="text-sm text-neutral-600 mt-1">
                    Off by default. When on, users with the matching permission can open the log from the profile menu
                    (under Incident Admin). Assign <span className="font-mono text-xs">security_command_control_log</span> in Role Management.
                  </p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300"
                    checked={featureAccessScclEnabled}
                    onChange={(e) => setFeatureAccessScclEnabled(e.target.checked)}
                    disabled={sectionLoading['feature-access'] || featureAccessSaving}
                  />
                  <span className="text-sm text-neutral-700">Enabled</span>
                </label>
              </div>
              <div className="border-t border-neutral-100 pt-6 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-neutral-900">Mandatory Schedule Input</div>
                  <p className="text-sm text-neutral-600 mt-1">
                    Off by default. Attendance punch-in does not collect a schedule today. When enabled, employees
                    assigned to more than one schedule in their profile must choose which schedule they are clocking in
                    for (mobile and web). Employees with only one assigned schedule are not prompted—their schedule is
                    applied automatically. On timecards, the schedule still comes from a matched scheduled shift when one
                    exists; otherwise managers select the schedule manually during review.
                  </p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300"
                    checked={featureAccessMandatoryScheduleInputEnabled}
                    onChange={(e) => setFeatureAccessMandatoryScheduleInputEnabled(e.target.checked)}
                    disabled={sectionLoading['feature-access'] || featureAccessSaving}
                  />
                  <span className="text-sm text-neutral-700">Enabled</span>
                </label>
              </div>
              <div className="border-t border-neutral-100 pt-6 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-neutral-900">Healthcare Compliance &amp; Renewals</div>
                  <p className="text-sm text-neutral-600 mt-1">
                    Off by default. When on, enables healthcare compliance workflows: medical indemnity and professional
                    license tracking, renewal reminders to doctors and staff (web, mobile, and email), and compliance
                    alerts on the dashboard. Uses existing Employee Licenses with license types such as Medical Indemnity.
                  </p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300"
                    checked={featureAccessHealthcareComplianceEnabled}
                    onChange={(e) => setFeatureAccessHealthcareComplianceEnabled(e.target.checked)}
                    disabled={sectionLoading['feature-access'] || featureAccessSaving}
                  />
                  <span className="text-sm text-neutral-700">Enabled</span>
                </label>
              </div>
              <div className="border-t border-neutral-100 pt-6 flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-neutral-900">Healthcare Position Banding Payroll</div>
                  <p className="text-sm text-neutral-600 mt-1">
                    Off by default. When on, enables position BY (band year) payroll using company rate tables
                    (base rates by year and day type, plus schedule additional rates). Configure rate tables under
                    Settings → Position Banding after enabling.
                  </p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300"
                    checked={featureAccessHealthcareNurseBandingPayrollEnabled}
                    onChange={(e) => setFeatureAccessHealthcareNurseBandingPayrollEnabled(e.target.checked)}
                    disabled={sectionLoading['feature-access'] || featureAccessSaving}
                  />
                  <span className="text-sm text-neutral-700">Enabled</span>
                </label>
              </div>
              <div className="border-t border-neutral-100 pt-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-neutral-900">Multi-Factor Authentication (MFA)</div>
                    <p className="text-sm text-neutral-600 mt-1">
                      Off by default. When on, role MFA settings apply and users may be required to verify with an
                      authenticator app, email, or SMS depending on company and role configuration.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutral-300"
                      checked={featureAccessMfaEnabled}
                      onChange={(e) => setFeatureAccessMfaEnabled(e.target.checked)}
                      disabled={sectionLoading['feature-access'] || featureAccessSaving}
                    />
                    <span className="text-sm text-neutral-700">Enabled</span>
                  </label>
                </div>
                {featureAccessMfaEnabled && (
                  <div className="pl-1 space-y-2">
                    <p className="text-sm font-medium text-neutral-800">Allowed methods (at least one required)</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-neutral-300"
                        checked={featureAccessMfaAllowAuthenticator}
                        onChange={(e) => setFeatureAccessMfaAllowAuthenticator(e.target.checked)}
                        disabled={sectionLoading['feature-access'] || featureAccessSaving}
                      />
                      <span className="text-sm text-neutral-700">Authenticator app</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-neutral-300"
                        checked={featureAccessMfaAllowEmail}
                        onChange={(e) => setFeatureAccessMfaAllowEmail(e.target.checked)}
                        disabled={sectionLoading['feature-access'] || featureAccessSaving}
                      />
                      <span className="text-sm text-neutral-700">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-neutral-300"
                        checked={featureAccessMfaAllowSms}
                        onChange={(e) => setFeatureAccessMfaAllowSms(e.target.checked)}
                        disabled={sectionLoading['feature-access'] || featureAccessSaving}
                      />
                      <span className="text-sm text-neutral-700">SMS</span>
                    </label>
                  </div>
                )}
                <div className="pt-2">
                  <div className="font-medium text-neutral-900 mb-1">JWT session expiry</div>
                  <p className="text-sm text-neutral-600 mb-2">
                    How long a signed-in session remains valid before the user must sign in again.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      className="w-24 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={featureAccessJwtExpiryValue}
                      onChange={(e) => setFeatureAccessJwtExpiryValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      disabled={sectionLoading['feature-access'] || featureAccessSaving}
                    />
                    <select
                      className="px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={featureAccessJwtExpiryUnit}
                      onChange={(e) => setFeatureAccessJwtExpiryUnit(e.target.value as 'Hours' | 'Days')}
                      disabled={sectionLoading['feature-access'] || featureAccessSaving}
                    >
                      <option value="Hours">Hours</option>
                      <option value="Days">Days</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end border-t border-neutral-100 pt-4">
                <button
                  type="button"
                  disabled={featureAccessSaving || sectionLoading['feature-access']}
                  onClick={async () => {
                    if (
                      featureAccessMfaEnabled &&
                      !featureAccessMfaAllowAuthenticator &&
                      !featureAccessMfaAllowEmail &&
                      !featureAccessMfaAllowSms
                    ) {
                      toast.error('When MFA is enabled, select at least one allowed method.');
                      return;
                    }
                    try {
                      setFeatureAccessSaving(true);
                      await companySettingsApi.updateFeatureAccess({
                        securityCommandControlLogEnabled: featureAccessScclEnabled,
                        mandatoryScheduleInputEnabled: featureAccessMandatoryScheduleInputEnabled,
                        healthcareComplianceEnabled: featureAccessHealthcareComplianceEnabled,
                        healthcareNurseBandingPayrollEnabled: featureAccessHealthcareNurseBandingPayrollEnabled,
                        mfaEnabled: featureAccessMfaEnabled,
                        mfaAllowAuthenticator: featureAccessMfaAllowAuthenticator,
                        mfaAllowEmail: featureAccessMfaAllowEmail,
                        mfaAllowSms: featureAccessMfaAllowSms,
                        jwtExpiryValue: featureAccessJwtExpiryValue,
                        jwtExpiryUnit: featureAccessJwtExpiryUnit,
                      });
                      setTenantMfaEnabled(featureAccessMfaEnabled);
                      setTenantMfaAllowAuthenticator(featureAccessMfaAllowAuthenticator);
                      setTenantMfaAllowEmail(featureAccessMfaAllowEmail);
                      setTenantMfaAllowSms(featureAccessMfaAllowSms);
                      toast.success('Feature access saved.');
                      window.dispatchEvent(new Event('staffly:refresh-permissions'));
                    } catch (err: any) {
                      toast.error(err?.response?.data?.message || err?.message || 'Failed to save.');
                    } finally {
                      setFeatureAccessSaving(false);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {featureAccessSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'nurse-banding':
        return <NurseBandingSettingsPanel />;

      case 'roles':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Role Management</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {roleSearch.trim() 
                    ? `Showing ${roleCounts.filtered} of ${roleCounts.total} roles`
                    : `Manage user roles and permissions (${roleCounts.filtered} of ${roleCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select 
                    value={roleFilter} 
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({roleCounts.active})</option>
                    <option value="inactive">Inactive ({roleCounts.inactive})</option>
                    <option value="all">All ({roleCounts.total})</option>
                  </select>
              </div>
              <button
                  onClick={handleAddRole}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                  Add Role
                </button>
              </div>
            </div>

            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search roles by name, description, or permissions..."
                  value={roleSearch}
                  onChange={(e) => {
                    setRoleSearch(e.target.value);
                    setRolePage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {filteredRoles.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <Shield className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No roles found</h3>
                <p className="text-neutral-600 mb-4">
                  {roleSearch.trim() 
                    ? 'No roles match your search criteria. Try adjusting your search or filters.'
                    : roleFilter === 'active' 
                      ? 'No active roles found.' 
                      : roleFilter === 'inactive' 
                        ? 'No inactive roles found.' 
                        : 'Get started by creating your first role.'}
                </p>
                <button
                  onClick={handleAddRole}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Role
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block">
                  <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="border-b border-neutral-200">
                            <th className="text-left p-4 font-medium text-neutral-700">Role Name</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Description</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Permissions</th>
                            <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const sortedRoles = filteredRoles.filter((role: any) => role.id !== null && role.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                            const totalPages = Math.ceil(sortedRoles.length / itemsPerPage);
                            const startIndex = (rolePage - 1) * itemsPerPage;
                            const paginatedRoles = sortedRoles.slice(startIndex, startIndex + itemsPerPage);
                            
                            return paginatedRoles.map((role: any) => (
                              <tr key={role.id} className="border-b border-neutral-100 last:border-b-0">
                                <td className="p-4 font-medium">
                                  <div className="flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-blue-600" />
                                    {role.name}
                                    {!role.isActive && (
                                      <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-4 text-neutral-600">
                                  {role.description || 'No description'}
                                </td>
                                <td className="p-4">
                                  <div className="flex flex-wrap gap-1">
                                    {role.permissions && role.permissions.length > 0 ? (
                                      role.permissions.map((permission: Permission) => (
                                        <span
                                          key={permission.id}
                                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                                        >
                                          {permission.name}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-xs text-neutral-400">No permissions</span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-4">
                                  <div className="flex gap-1 justify-end">
                                    <button
                                      onClick={() => handleEditRole(role)}
                                      className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    {role.isActive ? (
                                      <>
                                        <button
                                          onClick={() => handleDeactivateRole(role.id)}
                                          className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                                        >
                                          Deactivate
                                        </button>
                                        <button
                                          onClick={() => handleDeleteRole(role.id)}
                                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => handleActivateRole(role.id)}
                                        className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                      >
                                        Activate
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {(() => {
                      const sortedRoles = filteredRoles.filter((role: any) => role.id !== null && role.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name));
                      const totalPages = Math.ceil(sortedRoles.length / itemsPerPage);
                      const startIndex = (rolePage - 1) * itemsPerPage;
                      
                      if (totalPages <= 1) return null;
                      
                      return (
                        <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                          <div className="text-sm text-neutral-700">
                            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedRoles.length)} of {sortedRoles.length} roles
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setRolePage(prev => Math.max(1, prev - 1))}
                              disabled={rolePage === 1}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-neutral-700">
                              Page {rolePage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setRolePage(prev => Math.min(totalPages, prev + 1))}
                              disabled={rolePage >= totalPages}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden grid gap-4">
                  {filteredRoles.filter((role: any) => role.id !== null && role.id !== undefined).sort((a: any, b: any) => a.name.localeCompare(b.name)).map((role: any) => (
                    <div key={role.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="h-4 w-4 text-blue-600" />
                            <h3 className="text-lg font-medium">{role.name}</h3>
                            {!role.isActive && (
                              <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-neutral-600 mb-3">
                            {role.description || 'No description'}
                          </p>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {role.permissions && role.permissions.length > 0 ? (
                              role.permissions.map((permission: Permission) => (
                                <span
                                  key={permission.id}
                                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                                >
                                  {permission.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-neutral-400">No permissions</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditRole(role)}
                            className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {role.isActive ? (
                            <>
                              <button
                                onClick={() => handleDeactivateRole(role.id)}
                                className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                              >
                                Deactivate
                              </button>
                              <button
                                onClick={() => handleDeleteRole(role.id)}
                                className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleActivateRole(role.id)}
                              className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case 'training-types':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Training Types</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {trainingTypeSearch.trim() 
                    ? `Showing ${trainingTypeCounts.filtered} of ${trainingTypeCounts.total} training types`
                    : `Manage training types (${trainingTypeCounts.filtered} of ${trainingTypeCounts.total} showing)`}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-neutral-600" />
                  <select 
                    value={trainingTypeFilter} 
                    onChange={(e) => setTrainingTypeFilter(e.target.value)}
                    className="flex-1 sm:w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active ({trainingTypeCounts.active})</option>
                    <option value="inactive">Inactive ({trainingTypeCounts.inactive})</option>
                    <option value="all">All ({trainingTypeCounts.total})</option>
                  </select>
                </div>
                <button
                  onClick={handleAddTrainingType}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                Add Training Type
              </button>
            </div>
            </div>

            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search training types by name, description, or frequency..."
                  value={trainingTypeSearch}
                  onChange={(e) => {
                    setTrainingTypeSearch(e.target.value);
                    setTrainingTypePage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {filteredTrainingTypes.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <GraduationCap className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No training types found</h3>
                <p className="text-neutral-600 mb-4">
                  {trainingTypeSearch.trim() 
                    ? 'No training types match your search criteria. Try adjusting your search or filters.'
                    : trainingTypeFilter === 'active' 
                      ? 'No active training types found.' 
                      : trainingTypeFilter === 'inactive' 
                        ? 'No inactive training types found.' 
                        : 'Get started by creating your first training type.'}
                </p>
                <button
                  onClick={handleAddTrainingType}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Training Type
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block">
                  <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="border-b border-neutral-200">
                            <th className="text-left p-4 font-medium text-neutral-700">Name</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Description</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Frequency</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Status</th>
                            <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const totalPages = Math.ceil(filteredTrainingTypes.length / itemsPerPage);
                            const startIndex = (trainingTypePage - 1) * itemsPerPage;
                            const paginatedTrainingTypes = filteredTrainingTypes.slice(startIndex, startIndex + itemsPerPage);
                            
                            return paginatedTrainingTypes.map((trainingType: TrainingType) => (
                              <tr key={trainingType.id} className="border-b border-neutral-200 hover:bg-neutral-50">
                                <td className="p-4 font-medium">{trainingType.name}</td>
                                <td className="p-4 text-neutral-600">{trainingType.description || '-'}</td>
                                <td className="p-4 text-neutral-600">{formatTrainingTypeFrequency(trainingType)}</td>
                                <td className="p-4">
                                  {trainingType.isActive ? (
                                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                                  ) : (
                                    <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                                  )}
                                </td>
                                <td className="p-4">
                                  <div className="flex gap-1 justify-end">
                                    <button
                                      onClick={() => handleEditTrainingType(trainingType)}
                                      className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    {trainingType.isActive ? (
                                      <>
                                        <button
                                          onClick={() => handleDeactivateTrainingType(trainingType.id)}
                                          className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                                        >
                                          Deactivate
                                        </button>
                                        <button
                                          onClick={() => handleDeleteTrainingType(trainingType.id)}
                                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => handleActivateTrainingType(trainingType.id)}
                                        className="px-3 py-1 text-sm text-green-600 hover:text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                      >
                                        Activate
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {(() => {
                      const totalPages = Math.ceil(filteredTrainingTypes.length / itemsPerPage);
                      const startIndex = (trainingTypePage - 1) * itemsPerPage;
                      
                      if (totalPages <= 1) return null;
                      
                      return (
                        <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between flex-shrink-0">
                          <div className="text-sm text-neutral-700">
                            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTrainingTypes.length)} of {filteredTrainingTypes.length} training types
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setTrainingTypePage(prev => Math.max(1, prev - 1))}
                              disabled={trainingTypePage === 1}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-sm text-neutral-700">
                              Page {trainingTypePage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setTrainingTypePage(prev => Math.min(totalPages, prev + 1))}
                              disabled={trainingTypePage >= totalPages}
                              className="px-3 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredTrainingTypes.map((trainingType: TrainingType) => (
                    <div key={trainingType.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-neutral-900">{trainingType.name}</h3>
                          {trainingType.description && (
                            <p className="text-sm text-neutral-600 mt-1">{trainingType.description}</p>
                          )}
                        </div>
                        {trainingType.isActive ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-600 mb-3">
                        Frequency: {formatTrainingTypeFrequency(trainingType)}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleEditTrainingType(trainingType)}
                          className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {trainingType.isActive ? (
                          <>
                            <button
                              onClick={() => handleDeactivateTrainingType(trainingType.id)}
                              className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                            >
                              Deactivate
                            </button>
                            <button
                              onClick={() => handleDeleteTrainingType(trainingType.id)}
                              className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleActivateTrainingType(trainingType.id)}
                            className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Training Type Modal */}
            {showTrainingTypeModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-4">
                      {editingTrainingType ? 'Edit Training Type' : 'Add Training Type'}
                    </h3>
                    <form onSubmit={handleTrainingTypeSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
                        <input
                          type="text"
                          value={trainingTypeForm.name}
                          onChange={(e) => setTrainingTypeForm({ ...trainingTypeForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                        <textarea
                          value={trainingTypeForm.description || ''}
                          onChange={(e) => setTrainingTypeForm({ ...trainingTypeForm, description: e.target.value })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Frequency *</label>
                        <select
                          value={trainingTypeForm.frequency}
                          onChange={(e) => setTrainingTypeForm({ 
                            ...trainingTypeForm, 
                            frequency: e.target.value as 'One-Time' | 'Recurring',
                            recurringValue: undefined,
                            recurringUnit: undefined
                          })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="One-Time">One-Time</option>
                          <option value="Recurring">Recurring</option>
                        </select>
                      </div>
                      {trainingTypeForm.frequency === 'Recurring' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Every</label>
                            <input
                              type="number"
                              min="1"
                              value={trainingTypeForm.recurringValue || ''}
                              onChange={(e) => setTrainingTypeForm({ ...trainingTypeForm, recurringValue: parseInt(e.target.value) || undefined })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required={trainingTypeForm.frequency === 'Recurring'}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Unit</label>
                            <select
                              value={trainingTypeForm.recurringUnit || ''}
                              onChange={(e) => setTrainingTypeForm({ ...trainingTypeForm, recurringUnit: e.target.value as 'days' | 'weeks' | 'months' })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required={trainingTypeForm.frequency === 'Recurring'}
                            >
                              <option value="">Select unit</option>
                              <option value="days">Days</option>
                              <option value="weeks">Weeks</option>
                              <option value="months">Months</option>
                            </select>
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowTrainingTypeModal(false)}
                          className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          {editingTrainingType ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'company-settings':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Company Settings</h2>
                <p className="text-sm md:text-base text-neutral-600">Configure company information and preferences</p>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-lg">
              <div className="p-4 md:p-6 space-y-6">
                {/* Company Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Company Name *</label>
                      <input
                        type="text"
                        value={companySettingsForm.companyName}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, companyName: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Address Fields */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Address Line 1</label>
                      <input
                        type="text"
                        value={companySettingsForm.addressLine1 || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, addressLine1: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Address Line 2</label>
                      <input
                        type="text"
                        value={companySettingsForm.addressLine2 || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, addressLine2: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Address Line 3</label>
                      <input
                        type="text"
                        value={companySettingsForm.addressLine3 || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, addressLine3: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Post/ZIP Code</label>
                      <input
                        type="text"
                        value={companySettingsForm.postZipCode || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, postZipCode: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Country *</label>
                      <select
                        value={companySettingsForm.country}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, country: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Ireland">Ireland</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="Australia">Australia</option>
                        <option value="Germany">Germany</option>
                        <option value="France">France</option>
                        <option value="Spain">Spain</option>
                        <option value="Italy">Italy</option>
                        <option value="Netherlands">Netherlands</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Currency *</label>
                      <select
                        value={companySettingsForm.currency}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, currency: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="EUR €">EUR €</option>
                        <option value="USD $">USD $</option>
                        <option value="GBP £">GBP £</option>
                        <option value="CAD $">CAD $</option>
                        <option value="AUD $">AUD $</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Time Zone *</label>
                      <select
                        value={companySettingsForm.timeZone}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, timeZone: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="(GMT+01:00) Dublin">(GMT+01:00) Dublin</option>
                        <option value="(GMT+00:00) London">(GMT+00:00) London</option>
                        <option value="(GMT-05:00) New York">(GMT-05:00) New York</option>
                        <option value="(GMT-08:00) Los Angeles">(GMT-08:00) Los Angeles</option>
                        <option value="(GMT+09:00) Tokyo">(GMT+09:00) Tokyo</option>
                        <option value="(GMT+10:00) Sydney">(GMT+10:00) Sydney</option>
                        <option value="(GMT+00:00) UTC">(GMT+00:00) UTC</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Date Format *</label>
                      <select
                        value={companySettingsForm.dateFormat}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, dateFormat: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="dd/mm/yyyy">dd/mm/yyyy</option>
                        <option value="mm/dd/yyyy">mm/dd/yyyy</option>
                        <option value="yyyy-mm-dd">yyyy-mm-dd</option>
                        <option value="dd-mm-yyyy">dd-mm-yyyy</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">End of Day Time *</label>
                      <select
                        value={companySettingsForm.endOfDayTime}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, endOfDayTime: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="00:00">00:00</option>
                        <option value="01:00">01:00</option>
                        <option value="02:00">02:00</option>
                        <option value="03:00">03:00</option>
                        <option value="04:00">04:00</option>
                        <option value="05:00">05:00</option>
                        <option value="06:00">06:00</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between border border-neutral-200 rounded-md px-3 py-2">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Allow mobile ClockIN/Out without face verification
                        </label>
                        <div className="text-sm text-neutral-600">
                          Mobile attendance will skip face verification but still capture a photo
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={companySettingsForm.allowMobileClockInOutWithoutFaceVerification === true}
                          onChange={(e) =>
                            setCompanySettingsForm({
                              ...companySettingsForm,
                              allowMobileClockInOutWithoutFaceVerification: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Notification Retention */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Notification Retention (days)</label>
                      <input
                        type="number"
                        min={7}
                        max={365}
                        value={companySettingsForm.notificationRetentionDays ?? 90}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, notificationRetentionDays: parseInt(e.target.value) || 90 })}
                        className="w-32 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-neutral-500 mt-1">Notifications older than this will be automatically deleted (default: 90 days)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Night Start</label>
                      <select
                        value={companySettingsForm.nightStart || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, nightStart: e.target.value || undefined })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select time</option>
                        <option value="18:00">18:00</option>
                        <option value="19:00">19:00</option>
                        <option value="20:00">20:00</option>
                        <option value="21:00">21:00</option>
                        <option value="22:00">22:00</option>
                        <option value="23:00">23:00</option>
                        <option value="00:00">00:00</option>
                        <option value="01:00">01:00</option>
                        <option value="02:00">02:00</option>
                        <option value="03:00">03:00</option>
                        <option value="04:00">04:00</option>
                        <option value="05:00">05:00</option>
                        <option value="06:00">06:00</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Night End</label>
                      <select
                        value={companySettingsForm.nightEnd || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, nightEnd: e.target.value || undefined })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select time</option>
                        <option value="18:00">18:00</option>
                        <option value="19:00">19:00</option>
                        <option value="20:00">20:00</option>
                        <option value="21:00">21:00</option>
                        <option value="22:00">22:00</option>
                        <option value="23:00">23:00</option>
                        <option value="00:00">00:00</option>
                        <option value="01:00">01:00</option>
                        <option value="02:00">02:00</option>
                        <option value="03:00">03:00</option>
                        <option value="04:00">04:00</option>
                        <option value="05:00">05:00</option>
                        <option value="06:00">06:00</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Overtime Hours */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Overtime Hours</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">OT Hours 1</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={companySettingsForm.otRate1 || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, otRate1: e.target.value ? parseFloat(e.target.value) : undefined })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-neutral-600 mt-1">OT Hours 1 is applied once an employee goes over this many hours in a week</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">OT Hours 2</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={companySettingsForm.otRate2 || ''}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, otRate2: e.target.value ? parseFloat(e.target.value) : undefined })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-neutral-600 mt-1">OT Hours 2 is applied once an employee goes over this many hours in a week</p>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="text-md font-semibold mb-3 text-neutral-700">Rate Multipliers</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT1 Rate Multiplier</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={companySettingsForm.ot1RateMultiplier ?? 1.0}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, ot1RateMultiplier: e.target.value ? parseFloat(e.target.value) : 1.0 })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-neutral-600 mt-1">Multiplier for OT1 rate (default: 1.0)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT2 Rate Multiplier</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={companySettingsForm.ot2RateMultiplier ?? 1.0}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, ot2RateMultiplier: e.target.value ? parseFloat(e.target.value) : 1.0 })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-neutral-600 mt-1">Multiplier for OT2 rate (default: 1.0)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Sunday Rate Multiplier</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={companySettingsForm.sundayRateMultiplier ?? 1.0}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, sundayRateMultiplier: e.target.value ? parseFloat(e.target.value) : 1.0 })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-neutral-600 mt-1">Multiplier for Sunday rate (default: 1.0)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Holiday Rate Multiplier</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={companySettingsForm.bankHolidayRateMultiplier ?? 1.0}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, bankHolidayRateMultiplier: e.target.value ? parseFloat(e.target.value) : 1.0 })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-neutral-600 mt-1">Multiplier for bank holiday rate (default: 1.0)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Night Shift Rate Multiplier</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={companySettingsForm.nightShiftRateMultiplier ?? 1.0}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, nightShiftRateMultiplier: e.target.value ? parseFloat(e.target.value) : 1.0 })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-neutral-600 mt-1">Multiplier for night shift rate (default: 1.0)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-neutral-200">
                  <h3 className="text-lg font-semibold mb-2">Payroll Cycles</h3>
                  <p className="text-xs text-neutral-600 mb-4 max-w-3xl">
                    Choose which pay-period types appear on the Payroll screen. Only selected cycles are shown when creating a draft run.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2 max-w-3xl">
                    {(
                      [
                        { key: 'payrollCycleWeeklyEnabled' as const, label: 'Weekly', hint: '7-day week periods' },
                        { key: 'payrollCycleFortnightlyEnabled' as const, label: 'Bi-weekly', hint: '14-day periods' },
                        { key: 'payrollCycleHourly2928Enabled' as const, label: 'Hourly 29–28', hint: '29th of one month through 28th of the next' },
                        { key: 'payrollCycleSalariedMonthlyEnabled' as const, label: 'Salaried Monthly', hint: 'Calendar month for salaried staff' },
                      ] as const
                    ).map(({ key, label, hint }) => (
                      <label
                        key={key}
                        className="flex items-start gap-2.5 rounded-md border border-neutral-200 bg-neutral-50/80 px-3 py-2.5 cursor-pointer hover:bg-neutral-50"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border-neutral-300 text-blue-600"
                          checked={companySettingsForm[key] ?? true}
                          onChange={(e) =>
                            setCompanySettingsForm({ ...companySettingsForm, [key]: e.target.checked })
                          }
                        />
                        <span>
                          <span className="block text-sm font-medium text-neutral-900">{label}</span>
                          <span className="block text-xs text-neutral-600 mt-0.5">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-neutral-200">
                  <h3 className="text-lg font-semibold mb-2">Payroll inclusion &amp; leave hours</h3>
                  <p className="text-xs text-neutral-600 mb-4 max-w-3xl">
                    Company defaults used by leave pro-rata, payroll eligibility, and migration cutover.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Full-time standard hours / week</label>
                      <input
                        type="number"
                        min={1}
                        max={168}
                        step={0.5}
                        value={companySettingsForm.fullTimeStandardHoursPerWeek ?? 40}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setCompanySettingsForm({
                            ...companySettingsForm,
                            fullTimeStandardHoursPerWeek: Number.isFinite(v) ? Math.min(168, Math.max(1, v)) : 40,
                          });
                        }}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-neutral-600 mt-1">Used for part-time leave pro-rata (hours ↔ days).</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Leave legacy cutover date</label>
                      <input
                        type="date"
                        value={companySettingsForm.payrollLeaveLegacyCutoverDate ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCompanySettingsForm({
                            ...companySettingsForm,
                            payrollLeaveLegacyCutoverDate: v || null,
                            clearPayrollLeaveLegacyCutoverDate: !v,
                          });
                        }}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-neutral-600 mt-1">
                        Leave on/before this date is excluded from paid/unpaid payroll. Clear to disable.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mb-6">
                    <input
                      type="checkbox"
                      id="includeAgencyWorkersInPayroll"
                      checked={companySettingsForm.includeAgencyWorkersInPayroll ?? false}
                      onChange={(e) =>
                        setCompanySettingsForm({
                          ...companySettingsForm,
                          includeAgencyWorkersInPayroll: e.target.checked,
                        })
                      }
                      className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="includeAgencyWorkersInPayroll" className="text-sm text-neutral-700">
                      Include agency workers in payroll runs
                    </label>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-neutral-200">
                  <h3 className="text-lg font-semibold mb-2">Payroll engine (Phase 2)</h3>
                  <p className="text-xs text-neutral-600 mb-4 max-w-3xl">
                    Optional gross-pay rules. Defaults are off (v1 behaviour). Average paid leave uses finalized payroll runs stored per employee when a run is finalized.
                    When average paid leave is on, statutory BH (1/5 of 13-week average) also uses this lookback.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Leave pay reference period (weeks)</label>
                      <input
                        type="number"
                        min={1}
                        max={104}
                        value={companySettingsForm.leavePayReferencePeriodWeeks ?? 13}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setCompanySettingsForm({
                            ...companySettingsForm,
                            leavePayReferencePeriodWeeks: Number.isFinite(v) ? Math.min(104, Math.max(1, v)) : 13
                          });
                        }}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-neutral-600 mt-1">Lookback for average earnings paid leave (1–104). Used when average paid leave is enabled.</p>
                    </div>
                  </div>
                  <div className="border border-neutral-200 rounded-lg p-4 space-y-3 bg-neutral-50/80">
                    <h4 className="text-sm font-semibold text-neutral-800">Sunday &amp; bank holiday premiums (additive)</h4>
                    <p className="text-xs text-neutral-600">
                      When enabled, hourly staff get regular pay at base rate plus separate lines: Sunday premium (€/hr) and bank holiday extra (100% of hourly rate on worked PH hours when eligible).
                    </p>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="payrollUseAdditivePremiums"
                        checked={companySettingsForm.payrollUseAdditivePremiums ?? true}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, payrollUseAdditivePremiums: e.target.checked })}
                        className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="payrollUseAdditivePremiums" className="text-sm text-neutral-700">
                        Use separate premium pay lines (recommended). When off, Sunday/BH use pay-record multipliers blended into the hourly rate.
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Sunday premium (€ per hour, hourly staff)</label>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={companySettingsForm.sundayPremiumPerHour ?? 0.5}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setCompanySettingsForm({
                            ...companySettingsForm,
                            sundayPremiumPerHour: Number.isFinite(v) ? Math.max(0, v) : 0.5
                          });
                        }}
                        disabled={!(companySettingsForm.payrollUseAdditivePremiums ?? true)}
                        className="w-full max-w-xs px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="p2PhMultiply"
                        checked={companySettingsForm.payrollPhase2PhPremiumMultiplyRecordAndPolicy ?? false}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, payrollPhase2PhPremiumMultiplyRecordAndPolicy: e.target.checked })}
                        className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="p2PhMultiply" className="text-sm text-neutral-700">
                        Public holiday worked: multiply <span className="font-medium">pay-record</span> and <span className="font-medium">policy</span> premiums (instead of max).
                      </label>
                    </div>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="p2OtPremiums"
                        checked={companySettingsForm.payrollPhase2OtRateIncludesCalendarAndNightPremiums ?? false}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, payrollPhase2OtRateIncludesCalendarAndNightPremiums: e.target.checked })}
                        className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="p2OtPremiums" className="text-sm text-neutral-700">
                        Overtime rate includes calendar (e.g. Sunday/PH) and night premiums on top of OT multipliers.
                      </label>
                    </div>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="p2AvgLeave"
                        checked={companySettingsForm.payrollPhase2PaidLeaveUseAverageEarnings ?? false}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, payrollPhase2PaidLeaveUseAverageEarnings: e.target.checked })}
                        className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="p2AvgLeave" className="text-sm text-neutral-700">
                        Paid leave cash for hourly / daily / project uses average weekly earnings from finalized runs (falls back to contractual HB with a warning if history is missing).
                        Also used for statutory BH “1/5 of average weekly” when that leave-policy mode is selected.
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">PH not eligible — worked time cash</label>
                      <select
                        value={companySettingsForm.payrollPhNotEligibleWorkedRateMode ?? 0}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setCompanySettingsForm({
                            ...companySettingsForm,
                            payrollPhNotEligibleWorkedRateMode: Number.isFinite(v) ? Math.min(2, Math.max(0, v)) : 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={0}>Legacy (Sunday/PH premiums as today)</option>
                        <option value={1}>Plain base rate on PH-not-eligible days</option>
                        <option value={2}>Pay-record bank-holiday multiplier only</option>
                      </select>
                      <p className="text-xs text-neutral-600 mt-1">When a public holiday is not eligible for premium pay, how worked segments are valued (PD-024).</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Same day: unpaid leave + paid time</label>
                      <select
                        value={companySettingsForm.payrollUnpaidLeaveSameDayWorkedRule ?? 0}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setCompanySettingsForm({
                            ...companySettingsForm,
                            payrollUnpaidLeaveSameDayWorkedRule: Number.isFinite(v) ? Math.min(2, Math.max(0, v)) : 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={0}>Deduct full standard day</option>
                        <option value={1}>Deduct unworked standard hours only</option>
                        <option value={2}>Waive deduction if paid hours cover standard</option>
                      </select>
                      <p className="text-xs text-neutral-600 mt-1">When approved unpaid leave overlaps a day with paid clock time.</p>
                    </div>
                  </div>
                </div>

                {/* Leave Management Configuration */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Leave Management</h3>
                  
                  {/* Leave Year Configuration */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-neutral-800 mb-3">Leave Year</h4>
                    <p className="text-xs text-neutral-600 mb-3 max-w-3xl">
                      Controls how <span className="font-medium">leave-year labels</span> are chosen for balances, holiday requests, and payroll (Calendar = Jan–Dec;
                      Anniversary = rolling year from each employee&apos;s hire date; Fiscal = company month/day below, overridden by leave policy when set).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">Leave Year Type</label>
                        <select
                          value={companySettingsForm.leaveYearType || 'Calendar'}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, leaveYearType: e.target.value })}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Calendar">Calendar Year</option>
                          <option value="Anniversary">Anniversary (Hire Date)</option>
                          <option value="Fiscal">Fiscal Year</option>
                        </select>
                      </div>
                      {(companySettingsForm.leaveYearType === 'Fiscal' || companySettingsForm.leaveYearType === 'Anniversary') && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              {companySettingsForm.leaveYearType === 'Fiscal' ? 'Fiscal year starts (month)' : 'Fiscal fallback — start month'}
                            </label>
                            <select
                              value={companySettingsForm.leaveYearStartMonth ?? 1}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, leaveYearStartMonth: parseInt(e.target.value) })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                  {new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' })}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              {companySettingsForm.leaveYearType === 'Fiscal' ? 'Fiscal year starts (day)' : 'Fiscal fallback — start day'}
                            </label>
                            <select
                              value={companySettingsForm.leaveYearStartDay ?? 1}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, leaveYearStartDay: parseInt(e.target.value) })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {Array.from({ length: 31 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                    {companySettingsForm.leaveYearType === 'Anniversary' && (
                      <div className="mt-3 flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="anniversaryFiscalFallback"
                          checked={companySettingsForm.anniversaryFallbackUsesFiscalBoundary ?? false}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, anniversaryFallbackUsesFiscalBoundary: e.target.checked })}
                          className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="anniversaryFiscalFallback" className="text-sm text-neutral-700">
                          If an employee has <span className="font-medium">no hire date</span>, use the fiscal month/day above for their leave-year label (otherwise use calendar year).
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Accrual units */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-neutral-800 mb-3">Accrual Units</h4>
                    <p className="text-xs text-neutral-600 mb-3 max-w-3xl">
                      Choose whether leave accruals are calculated and shown in <span className="font-medium">days</span> or{' '}
                      <span className="font-medium">hours</span>. Hours use full-time standard hours per week ÷ 5 as one leave day
                      (e.g. 40h week = 8h per day).
                    </p>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="accrualsBasedOnHours"
                        checked={companySettingsForm.accrualsBasedOnHours ?? false}
                        onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, accrualsBasedOnHours: e.target.checked })}
                        className="mt-1 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="accrualsBasedOnHours" className="text-sm text-neutral-700">
                        Accruals are based on <span className="font-medium">hours</span> (unchecked = days)
                      </label>
                    </div>
                  </div>

                  {/* Carry Forward Configuration */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-neutral-800 mb-3">Carry Forward</h4>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={companySettingsForm.carryForwardAllowed ?? true}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, carryForwardAllowed: e.target.checked })}
                          className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="text-sm font-medium text-neutral-700">Allow Carry Forward</label>
                      </div>
                      {companySettingsForm.carryForwardAllowed && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-neutral-200">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Max Carry Forward Days</label>
                            <input
                              type="number"
                              min="0"
                              value={companySettingsForm.carryForwardMaxDays ?? 5}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, carryForwardMaxDays: parseInt(e.target.value) || 0 })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry Month</label>
                            <select
                              value={companySettingsForm.carryForwardExpiryMonth ?? 3}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, carryForwardExpiryMonth: parseInt(e.target.value) })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                  {new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' })}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Expiry Day</label>
                            <input
                              type="number"
                              min="1"
                              max="31"
                              value={companySettingsForm.carryForwardExpiryDay ?? 31}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, carryForwardExpiryDay: parseInt(e.target.value) || 1 })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-6">
                            <input
                              type="checkbox"
                              checked={companySettingsForm.carryForwardRequiresApproval ?? true}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, carryForwardRequiresApproval: e.target.checked })}
                              className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label className="text-sm font-medium text-neutral-700">Requires Approval</label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sickness Carry Forward */}
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-800 mb-3">Sickness Carry Forward</h4>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={companySettingsForm.sicknessProtectedCarryForward ?? true}
                          onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, sicknessProtectedCarryForward: e.target.checked })}
                          className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="text-sm font-medium text-neutral-700">Protected Carry Forward</label>
                      </div>
                      {companySettingsForm.sicknessProtectedCarryForward && (
                        <div className="pl-6 border-l-2 border-neutral-200">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Carry Forward Expiry (Months)</label>
                            <input
                              type="number"
                              min="0"
                              value={companySettingsForm.sicknessCarryForwardExpiryMonths ?? 15}
                              onChange={(e) => setCompanySettingsForm({ ...companySettingsForm, sicknessCarryForwardExpiryMonths: parseInt(e.target.value) || 0 })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-neutral-600 mt-1">Number of months after leave year end before carried forward sickness days expire</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-neutral-200">
                  <button
                    onClick={handleSaveCompanySettings}
                    disabled={savingCompanySettings}
                    className="px-6 md:px-8 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="h-4 w-4" />
                    {savingCompanySettings ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'public-holidays':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Public Holidays</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  {publicHolidayTab === 'holidays' 
                    ? (publicHolidaySearch.trim() 
                        ? `Showing ${publicHolidayCounts.filtered} of ${publicHolidayCounts.total} holidays`
                        : `Manage public holidays (${publicHolidayCounts.filtered} of ${publicHolidayCounts.total} showing)`)
                    : `Manage company policies for public holidays`}
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-neutral-200 mb-6">
              <div className="flex space-x-8">
                <button
                  onClick={() => setPublicHolidayTab('holidays')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    publicHolidayTab === 'holidays'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }`}
                >
                  Holidays
                </button>
                <button
                  onClick={() => {
                    setPublicHolidayTab('policy');
                    if (publicHolidayPolicies.length === 0) {
                      refreshPublicHolidayPolicies();
                    }
                  }}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    publicHolidayTab === 'policy'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }`}
                >
                  Company Policy
                </button>
              </div>
            </div>

            {publicHolidayTab === 'holidays' ? (
              <>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-neutral-600" />
                      <select 
                        value={publicHolidayFilter} 
                        onChange={(e) => setPublicHolidayFilter(e.target.value)}
                        className="flex-1 sm:w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active ({publicHolidayCounts.active})</option>
                        <option value="inactive">Inactive ({publicHolidayCounts.inactive})</option>
                        <option value="all">All ({publicHolidayCounts.total})</option>
                      </select>
                    </div>
                    <button
                      onClick={handleAddPublicHoliday}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Holiday
                    </button>
                  </div>
                </div>

            {/* Search Box */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search holidays by name, country, or date..."
                  value={publicHolidaySearch}
                  onChange={(e) => setPublicHolidaySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {filteredPublicHolidays.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <Calendar className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No public holidays found</h3>
                <p className="text-neutral-600 mb-4">
                  {publicHolidaySearch.trim() 
                    ? 'No holidays match your search criteria. Try adjusting your search or filters.'
                    : publicHolidayFilter === 'active' 
                      ? 'No active holidays found.' 
                      : publicHolidayFilter === 'inactive' 
                        ? 'No inactive holidays found.' 
                        : 'Get started by adding your first public holiday.'}
                </p>
                <button
                  onClick={handleAddPublicHoliday}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add Holiday
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block">
                  <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-neutral-50 z-10">
                          <tr className="border-b border-neutral-200">
                            <th className="text-left p-4 font-medium text-neutral-700">Name</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Date</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Country</th>
                            <th className="text-left p-4 font-medium text-neutral-700">Status</th>
                            <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPublicHolidays.map((holiday: PublicHoliday) => (
                          <tr key={holiday.id} className="border-b border-neutral-200 hover:bg-neutral-50">
                            <td className="p-4 font-medium">{holiday.name}</td>
                            <td className="p-4 text-neutral-600">{formatCompanyDate(holiday.date)}</td>
                            <td className="p-4 text-neutral-600">{holiday.country}</td>
                            <td className="p-4">
                              {holiday.isActive ? (
                                <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                              ) : (
                                <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={() => handleEditPublicHoliday(holiday)}
                                  className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                {holiday.isActive ? (
                                  <>
                                    <button
                                      onClick={() => handleDeactivatePublicHoliday(holiday.id)}
                                      className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                                    >
                                      Deactivate
                                    </button>
                                    <button
                                      onClick={() => handleDeletePublicHoliday(holiday.id)}
                                      className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => handleActivatePublicHoliday(holiday.id)}
                                    className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                  >
                                    Activate
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {filteredPublicHolidays.map((holiday: PublicHoliday) => (
                    <div key={holiday.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-neutral-900">{holiday.name}</h3>
                          <p className="text-sm text-neutral-600 mt-1">{formatCompanyDate(holiday.date)}</p>
                          <p className="text-sm text-neutral-600">{holiday.country}</p>
                        </div>
                        {holiday.isActive ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                        )}
                      </div>
                      <div className="flex gap-2 justify-end mt-3">
                        <button
                          onClick={() => handleEditPublicHoliday(holiday)}
                          className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {holiday.isActive ? (
                          <>
                            <button
                              onClick={() => handleDeactivatePublicHoliday(holiday.id)}
                              className="px-3 py-1 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                            >
                              Deactivate
                            </button>
                            <button
                              onClick={() => handleDeletePublicHoliday(holiday.id)}
                              className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleActivatePublicHoliday(holiday.id)}
                            className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

                {/* Public Holiday Modal */}
                {showPublicHolidayModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-4">
                          {editingPublicHoliday ? 'Edit Public Holiday' : 'Add Public Holiday'}
                        </h3>
                        <form onSubmit={handlePublicHolidaySubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Holiday Name *</label>
                            <input
                              type="text"
                              value={publicHolidayForm.name}
                              onChange={(e) => setPublicHolidayForm({ ...publicHolidayForm, name: e.target.value })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Year *</label>
                            <input
                              type="number"
                              min="2000"
                              max="2100"
                              value={publicHolidayForm.year || new Date().getFullYear()}
                              onChange={(e) => {
                                const year = parseInt(e.target.value) || new Date().getFullYear();
                                setPublicHolidayForm({ ...publicHolidayForm, year });
                                // Update date to match year if date is already set
                                if (publicHolidayForm.date) {
                                  const dateObj = new Date(publicHolidayForm.date);
                                  dateObj.setFullYear(year);
                                  setPublicHolidayForm({ ...publicHolidayForm, year, date: dateObj.toISOString().split('T')[0] });
                                }
                              }}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Date *</label>
                            <input
                              type="date"
                              value={publicHolidayForm.date}
                              onChange={(e) => {
                                setPublicHolidayForm({ ...publicHolidayForm, date: e.target.value });
                                // Auto-update year from selected date
                                if (e.target.value) {
                                  const dateObj = new Date(e.target.value);
                                  setPublicHolidayForm({ ...publicHolidayForm, date: e.target.value, year: dateObj.getFullYear() });
                                }
                              }}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">Country *</label>
                            <select
                              value={publicHolidayForm.country}
                              onChange={(e) => setPublicHolidayForm({ ...publicHolidayForm, country: e.target.value })}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="Ireland">Ireland</option>
                              <option value="United Kingdom">United Kingdom</option>
                              <option value="United States">United States</option>
                              <option value="Canada">Canada</option>
                              <option value="Australia">Australia</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="isActive"
                              checked={publicHolidayForm.isActive}
                              onChange={(e) => setPublicHolidayForm({ ...publicHolidayForm, isActive: e.target.checked })}
                              className="h-4 w-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="isActive" className="text-sm font-medium text-neutral-700">
                              Active
                            </label>
                          </div>
                          <div className="flex gap-2 pt-4">
                            <button
                              type="button"
                              onClick={() => setShowPublicHolidayModal(false)}
                              className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                              {editingPublicHoliday ? 'Update' : 'Create'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Policy Tab Content */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                  <button
                    onClick={handleAddPublicHolidayPolicy}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Policy
                  </button>
                </div>

                {publicHolidayPolicies.length === 0 ? (
                  <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                    <Shield className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">No policies found</h3>
                    <p className="text-neutral-600 mb-4">
                      Get started by adding your first public holiday company policy.
                    </p>
                    <button
                      onClick={handleAddPublicHolidayPolicy}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Plus className="h-4 w-4" />
                      Add Policy
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                      <div className="border border-neutral-200 rounded-lg bg-white flex flex-col" style={{ height: '450px' }}>
                        <div className="overflow-y-auto flex-1 min-h-0">
                          <table className="w-full">
                            <thead className="sticky top-0 bg-neutral-50 z-10">
                              <tr className="border-b border-neutral-200">
                                <th className="text-left p-4 font-medium text-neutral-700">Version</th>
                                <th className="text-left p-4 font-medium text-neutral-700">Effective From</th>
                                <th className="text-left p-4 font-medium text-neutral-700">Effective To</th>
                                <th className="text-left p-4 font-medium text-neutral-700">Status</th>
                                <th className="text-left p-4 font-medium text-neutral-700">Entitlement</th>
                                <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {publicHolidayPolicies.map((policy: PublicHolidayPolicy) => (
                                <tr key={policy.policyId} className="border-b border-neutral-200 hover:bg-neutral-50">
                                  <td className="p-4 font-medium">v{policy.version}</td>
                                  <td className="p-4 text-neutral-600">{formatCompanyDate(policy.effectiveFrom)}</td>
                                  <td className="p-4 text-neutral-600">
                                    {policy.effectiveTo ? formatCompanyDate(policy.effectiveTo) : 'Active'}
                                  </td>
                                  <td className="p-4">
                                    {policy.effectiveTo === null ? (
                                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                                    )}
                                  </td>
                                  <td className="p-4 text-neutral-600">{policy.defaultEntitlementType}</td>
                                  <td className="p-4">
                                    <div className="flex gap-1 justify-end">
                                      <button
                                        onClick={() => handleEditPublicHolidayPolicy(policy)}
                                        className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeletePublicHolidayPolicy(policy.policyId)}
                                        className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {publicHolidayPolicies.map((policy: PublicHolidayPolicy) => (
                        <div key={policy.policyId} className="bg-white border border-neutral-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className="font-medium text-neutral-900">Version {policy.version}</h3>
                              <p className="text-sm text-neutral-600 mt-1">
                                From: {formatCompanyDate(policy.effectiveFrom)}
                              </p>
                              <p className="text-sm text-neutral-600">
                                To: {policy.effectiveTo ? formatCompanyDate(policy.effectiveTo) : 'Active'}
                              </p>
                              <p className="text-sm text-neutral-600">Entitlement: {policy.defaultEntitlementType}</p>
                            </div>
                            {policy.effectiveTo === null ? (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                            ) : (
                              <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                            )}
                          </div>
                          <div className="flex gap-2 justify-end mt-3">
                            <button
                              onClick={() => handleEditPublicHolidayPolicy(policy)}
                              className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePublicHolidayPolicy(policy.policyId)}
                              className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Public Holiday Policy Modal */}
                {showPublicHolidayPolicyModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-4">
                          {editingPublicHolidayPolicy ? 'Edit Public Holiday Policy' : 'Add Public Holiday Policy'}
                        </h3>
                        <form onSubmit={handlePublicHolidayPolicySubmit} className="space-y-6">
                          {/* Basic Information */}
                          <div className="border-b border-neutral-200 pb-4">
                            <h4 className="text-sm font-semibold text-neutral-800 mb-3">Basic Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Version *</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={publicHolidayPolicyForm.version}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, version: parseInt(e.target.value) || 1 })}
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Effective From *</label>
                                <input
                                  type="date"
                                  value={publicHolidayPolicyForm.effectiveFrom}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, effectiveFrom: e.target.value })}
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Effective To</label>
                                <input
                                  type="date"
                                  value={publicHolidayPolicyForm.effectiveTo || ''}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, effectiveTo: e.target.value || null })}
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <p className="text-xs text-neutral-600 mt-1">Leave empty for active policy</p>
                              </div>
                            </div>
                          </div>

                          {/* Eligibility Rules */}
                          <div className="border-b border-neutral-200 pb-4">
                            <h4 className="text-sm font-semibold text-neutral-800 mb-3">Eligibility (Statutory Protected)</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Eligibility Rule Type *</label>
                                <select
                                  value={publicHolidayPolicyForm.eligibilityRuleType}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, eligibilityRuleType: e.target.value as any })}
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="Statutory40Hours">Statutory 40 Hours</option>
                                  <option value="NoRestriction">No Restriction</option>
                                  <option value="Custom">Custom</option>
                                </select>
                              </div>
                              {publicHolidayPolicyForm.eligibilityRuleType === 'Custom' && (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Required Hours Last Weeks (max: 40)</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="40"
                                      value={publicHolidayPolicyForm.requiredHoursLastWeeks}
                                      onChange={(e) => {
                                        const value = parseFloat(e.target.value) || 0;
                                        const clampedValue = Math.min(Math.max(value, 0), 40);
                                        setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, requiredHoursLastWeeks: clampedValue });
                                      }}
                                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="text-xs text-neutral-600 mt-1">Maximum value: 40</p>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Weeks Lookback (max: 5)</label>
                                    <input
                                      type="number"
                                      min="1"
                                      max="5"
                                      value={publicHolidayPolicyForm.weeksLookback}
                                      onChange={(e) => {
                                        const value = parseInt(e.target.value) || 1;
                                        const clampedValue = Math.min(Math.max(value, 1), 5);
                                        setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, weeksLookback: clampedValue });
                                      }}
                                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="text-xs text-neutral-600 mt-1">Maximum value: 5</p>
                                  </div>
                                </>
                              )}
                              {publicHolidayPolicyForm.eligibilityRuleType === 'Statutory40Hours' && (
                                <>
                                  <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Required Hours Last Weeks (default: 40)</label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={publicHolidayPolicyForm.requiredHoursLastWeeks}
                                      readOnly
                                      className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-100 cursor-not-allowed"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Weeks Lookback (default: 5)</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={publicHolidayPolicyForm.weeksLookback}
                                      readOnly
                                      className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-100 cursor-not-allowed"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Default Entitlement */}
                          <div className="border-b border-neutral-200 pb-4">
                            <h4 className="text-sm font-semibold text-neutral-800 mb-3">Default Entitlement</h4>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Default Entitlement Type *</label>
                                <select
                                  value={publicHolidayPolicyForm.defaultEntitlementType}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, defaultEntitlementType: e.target.value as any })}
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="PaidOff">Paid Off</option>
                                  <option value="ExtraPay">Extra Pay</option>
                                  <option value="DayInLieu">Day In Lieu</option>
                                  <option value="ExtraLeave">Extra Leave</option>
                                </select>
                              </div>
                              {publicHolidayPolicyForm.defaultEntitlementType === 'PaidOff' && (
                                <div>
                                  <label className="block text-sm font-medium text-neutral-700 mb-1">Work On Holiday Entitlement Type</label>
                                  <select
                                    value={publicHolidayPolicyForm.workOnHolidayEntitlementType || ''}
                                    onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, workOnHolidayEntitlementType: e.target.value ? (e.target.value as any) : undefined })}
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="">Select...</option>
                                    <option value="ExtraPay">Extra Pay</option>
                                    <option value="DayInLieu">Day In Lieu</option>
                                    <option value="ExtraLeave">Extra Leave</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Payroll Rules - Show Holiday Premium Rate conditionally */}
                          {(publicHolidayPolicyForm.defaultEntitlementType === 'ExtraPay' || 
                            (publicHolidayPolicyForm.defaultEntitlementType === 'PaidOff' && publicHolidayPolicyForm.workOnHolidayEntitlementType === 'ExtraPay')) && (
                            <div className="border-b border-neutral-200 pb-4">
                              <h4 className="text-sm font-semibold text-neutral-800 mb-3">Payroll Rules</h4>
                              <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Holiday Premium Rate *</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={publicHolidayPolicyForm.holidayPremiumRate}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, holidayPremiumRate: parseFloat(e.target.value) || 1.0 })}
                                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                                <p className="text-xs text-neutral-600 mt-1">e.g., 1.0, 1.5</p>
                              </div>
                            </div>
                          )}

                          {/* Day-in-lieu Rules - Show conditionally */}
                          {(publicHolidayPolicyForm.defaultEntitlementType === 'DayInLieu' || 
                            (publicHolidayPolicyForm.defaultEntitlementType === 'PaidOff' && publicHolidayPolicyForm.workOnHolidayEntitlementType === 'DayInLieu')) && (
                            <div className="border-b border-neutral-200 pb-4">
                              <h4 className="text-sm font-semibold text-neutral-800 mb-3">Day-in-lieu Rules</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-neutral-700 mb-1">Day In Lieu Expiry Days</label>
                                  <input
                                    type="number"
                                    min="0"
                                    value={publicHolidayPolicyForm.dayInLieuExpiryDays}
                                    onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, dayInLieuExpiryDays: parseInt(e.target.value) || 30 })}
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <p className="text-xs text-neutral-600 mt-1">e.g., 30</p>
                                </div>
                                <div className="flex items-center space-x-2 pt-6">
                                  <input
                                    type="checkbox"
                                    id="dayInLieuAutoCredit"
                                    checked={publicHolidayPolicyForm.dayInLieuAutoCredit}
                                    onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, dayInLieuAutoCredit: e.target.checked })}
                                    className="h-4 w-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                                  />
                                  <label htmlFor="dayInLieuAutoCredit" className="text-sm font-medium text-neutral-700">
                                    Day In Lieu Auto Credit
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Additional Options - Show Credit to Annual Leave conditionally */}
                          {(publicHolidayPolicyForm.defaultEntitlementType === 'ExtraLeave' || 
                            (publicHolidayPolicyForm.defaultEntitlementType === 'PaidOff' && publicHolidayPolicyForm.workOnHolidayEntitlementType === 'ExtraLeave')) && (
                            <div>
                              <h4 className="text-sm font-semibold text-neutral-800 mb-3">Additional Options</h4>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="creditToAnnualLeave"
                                  checked={publicHolidayPolicyForm.creditToAnnualLeave}
                                  onChange={(e) => setPublicHolidayPolicyForm({ ...publicHolidayPolicyForm, creditToAnnualLeave: e.target.checked })}
                                  className="h-4 w-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="creditToAnnualLeave" className="text-sm font-medium text-neutral-700">
                                  Credit To Annual Leave
                                </label>
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2 pt-4">
                            <button
                              type="button"
                              onClick={() => setShowPublicHolidayPolicyModal(false)}
                              className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                              {editingPublicHolidayPolicy ? 'Update' : 'Create'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'uploads-migration':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Uploads → S3 Migration</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  Migrate legacy files from this server&apos;s <code className="px-1 py-0.5 bg-neutral-100 rounded">wwwroot/uploads</code> into S3 for the current tenant.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      setMigratingUploads(true);
                      const ctx = await adminUploadsApi.getContext();
                      setUploadsContext(ctx);
                      toast.success('Loaded upload context.');
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to load context.');
                    } finally {
                      setMigratingUploads(false);
                    }
                  }}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 text-sm font-medium rounded-md hover:bg-neutral-200 transition-colors flex items-center gap-2 disabled:opacity-50"
                  disabled={migratingUploads}
                >
                  <RefreshCw className={`h-4 w-4 ${migratingUploads ? 'animate-spin' : ''}`} />
                  Detect Env/Tenant
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Run migration now? This will upload profile images to S3 and update DB links.')) return;
                    try {
                      setMigratingUploads(true);
                      const ctx = await adminUploadsApi.getContext();
                      setUploadsContext(ctx);
                      const result = await adminUploadsApi.migrateAll({
                        dryRun: false,
                        deleteLocalAfterUpload: false,
                        forceReupload: false,
                        presignMinutes: 60,
                        includeProfileImages: true,
                        includeTaskAttachments: true,
                        includeTaskConversationAttachments: true,
                        includeSubTaskAttachments: true,
                        includeSubTaskConversationAttachments: true,
                        includeSecurityCommandControlLogAttachments: true,
                      });
                      setUploadsMigrationResult(result);
                      toast.success(`Migration finished. Migrated: ${result?.totals?.migrated ?? 0}`);
                    } catch (e: any) {
                      toast.error(e?.response?.data?.message || e?.message || 'Migration failed.');
                    } finally {
                      setMigratingUploads(false);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  disabled={migratingUploads}
                >
                  <Cloud className="h-4 w-4" />
                  {migratingUploads ? 'Migrating…' : 'Migrate Profile Images'}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-neutral-900 mb-2">Detected context</div>
                <pre className="text-xs bg-neutral-50 border border-neutral-200 rounded p-3 overflow-auto">
                  {JSON.stringify(uploadsContext, null, 2)}
                </pre>
              </div>

              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="text-sm font-semibold text-neutral-900 mb-2">Last migration result</div>
                <pre className="text-xs bg-neutral-50 border border-neutral-200 rounded p-3 overflow-auto">
                  {JSON.stringify(uploadsMigrationResult, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        );

      case 'face-sync-status':
        return (
          <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold">Face Sync Status</h2>
                <p className="text-sm md:text-base text-neutral-600">
                  Monitor and manage face recognition synchronization between database and AWS Rekognition.
                  Collection ID is derived from the request URL (e.g. app.staffly.pro → app-staffly-pro).
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={refreshFaceSyncStatus}
                  disabled={sectionLoading['face-sync-status']}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 text-sm font-medium rounded-md hover:bg-neutral-200 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${sectionLoading['face-sync-status'] ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                {faceSyncStatus && faceSyncStatus.orphanedFacesCount > 0 && (
                  <button
                    onClick={handleSyncFaces}
                    disabled={syncingFaces}
                    className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <Trash className="h-4 w-4" />
                    {syncingFaces ? 'Cleaning...' : `Cleanup ${faceSyncStatus.orphanedFacesCount} Orphaned Faces`}
                  </button>
                )}
              </div>
            </div>

            {sectionLoading['face-sync-status'] ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-neutral-600">Loading face sync status...</p>
              </div>
            ) : !faceSyncStatus ? (
              <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
                <AlertTriangle className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">Unable to load sync status</h3>
                <p className="text-neutral-600 mb-4">Failed to fetch face sync status. Please try again.</p>
                <button
                  onClick={refreshFaceSyncStatus}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Rekognition unavailable warning */}
                {faceSyncStatus.rekognitionAvailable === false && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-amber-900">AWS Rekognition Unavailable</h3>
                      <p className="text-sm text-amber-800 mt-1">
                        {faceSyncStatus.rekognitionErrorMessage || 'Unable to connect to AWS Rekognition.'} Check your AWS configuration (credentials, region, and Rekognition collection).
                      </p>
                      <p className="text-sm text-amber-700 mt-2">Showing database face count only.</p>
                    </div>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-neutral-200 rounded-lg p-6 border-l-4 border-blue-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-600 mb-1">Faces in Rekognition</p>
                        <p className="text-3xl font-bold text-neutral-900">
                          {faceSyncStatus.rekognitionAvailable === false ? '—' : faceSyncStatus.rekognitionFaceCount}
                        </p>
                      </div>
                      <Cloud className="w-12 h-12 text-blue-500" />
                    </div>
                  </div>

                  <div className="bg-white border border-neutral-200 rounded-lg p-6 border-l-4 border-green-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-600 mb-1">Faces in Database</p>
                        <p className="text-3xl font-bold text-neutral-900">{faceSyncStatus.databaseFaceCount}</p>
                      </div>
                      <Database className="w-12 h-12 text-green-500" />
                    </div>
                  </div>
                </div>

                {/* Sync Status */}
                <div className={`rounded-lg border p-6 ${
                  faceSyncStatus.rekognitionAvailable === false
                    ? 'bg-amber-50 border-amber-200'
                    : faceSyncStatus.isInSync
                    ? 'bg-green-50 border-green-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    {faceSyncStatus.rekognitionAvailable === false ? (
                      <AlertTriangle className="w-6 h-6 text-amber-600" />
                    ) : faceSyncStatus.isInSync ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    )}
                    <h3 className="text-xl font-bold text-neutral-900">
                      {faceSyncStatus.rekognitionAvailable === false
                        ? 'Rekognition Unavailable'
                        : faceSyncStatus.isInSync
                        ? 'In Sync'
                        : 'Out of Sync'}
                    </h3>
                  </div>
                  <p className="text-neutral-700">
                    {faceSyncStatus.rekognitionAvailable === false
                      ? 'AWS Rekognition could not be reached. Database face count is shown above.'
                      : faceSyncStatus.isInSync
                      ? 'All faces are properly synchronized between Rekognition and the database.'
                      : 'There are discrepancies between Rekognition and the database.'}
                  </p>
                </div>

                {/* Discrepancies - only when Rekognition is available and out of sync */}
                {faceSyncStatus.rekognitionAvailable !== false && !faceSyncStatus.isInSync && (
                  <div className="space-y-4">
                    {faceSyncStatus.orphanedFacesCount > 0 && (
                      <div className="bg-white border border-neutral-200 rounded-lg p-6 border-l-4 border-orange-500">
                        <h3 className="text-lg font-bold text-neutral-900 mb-2">
                          Orphaned Faces: {faceSyncStatus.orphanedFacesCount}
                        </h3>
                        <p className="text-neutral-600 mb-4">
                          These faces exist in Rekognition but are not linked to any active employee in the database.
                        </p>
                        {faceSyncStatus.orphanedFaceIds.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-neutral-700 mb-2">Face IDs:</p>
                            <div className="bg-neutral-50 rounded p-3 max-h-40 overflow-y-auto">
                              {faceSyncStatus.orphanedFaceIds.map((faceId, index) => (
                                <code key={index} className="block text-xs text-neutral-600 font-mono mb-1">
                                  {faceId}
                                </code>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {faceSyncStatus.missingFacesCount > 0 && (
                      <div className="bg-white border border-neutral-200 rounded-lg p-6 border-l-4 border-red-500">
                        <h3 className="text-lg font-bold text-neutral-900 mb-2">
                          Missing Faces: {faceSyncStatus.missingFacesCount}
                        </h3>
                        <p className="text-neutral-600 mb-4">
                          These FaceIds exist in the database but the faces are missing from Rekognition collection.
                        </p>
                        {faceSyncStatus.missingFaceIds.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-neutral-700 mb-2">Face IDs:</p>
                            <div className="bg-neutral-50 rounded p-3 max-h-40 overflow-y-auto">
                              {faceSyncStatus.missingFaceIds.map((faceId, index) => (
                                <code key={index} className="block text-xs text-neutral-600 font-mono mb-1">
                                  {faceId}
                                </code>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Details Table */}
                <div className="bg-white border border-neutral-200 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-neutral-900 mb-4">Details</h3>
                  <div className="space-y-3">
                    {'collectionId' in faceSyncStatus && faceSyncStatus.collectionId && (
                      <div className="flex justify-between items-center py-2 border-b border-neutral-200">
                        <span className="text-neutral-700">Rekognition Collection:</span>
                        <code className="font-mono text-sm font-semibold text-neutral-900 bg-neutral-100 px-2 py-1 rounded">
                          {faceSyncStatus.collectionId}
                        </code>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 border-b border-neutral-200">
                      <span className="text-neutral-700">Rekognition Face Count:</span>
                      <span className="font-semibold text-neutral-900">
                        {faceSyncStatus.rekognitionAvailable === false ? '—' : faceSyncStatus.rekognitionFaceCount}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-neutral-200">
                      <span className="text-neutral-700">Database Face Count:</span>
                      <span className="font-semibold text-neutral-900">{faceSyncStatus.databaseFaceCount}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-neutral-200">
                      <span className="text-neutral-700">Orphaned Faces:</span>
                      <span className="font-semibold text-neutral-900">{faceSyncStatus.orphanedFacesCount}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-neutral-200">
                      <span className="text-neutral-700">Missing Faces:</span>
                      <span className="font-semibold text-neutral-900">{faceSyncStatus.missingFacesCount}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-neutral-700">Sync Status:</span>
                      <span className={`font-semibold ${
                        faceSyncStatus.isInSync ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {faceSyncStatus.isInSync ? 'In Sync' : 'Out of Sync'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full bg-neutral-50 min-h-screen flex flex-col md:flex-row">
      {/* Mobile Header with Menu Button */}
      <div className="md:hidden bg-white border-b border-neutral-200 p-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-neutral-100 rounded-md transition-colors"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div>
            <h1 className="text-lg font-bold">Settings</h1>
            <p className="text-xs text-neutral-600">Manage system configuration</p>
          </div>
        </div>
      </div>

      {/* Sidebar - Hidden on mobile, overlay when open */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50
        w-64 bg-white border-r border-neutral-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        md:translate-x-0
      `}>
        {/* Mobile close button */}
        <div className="md:hidden p-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Settings</h1>
            <p className="text-xs text-neutral-600">Manage system configuration</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 hover:bg-neutral-100 rounded-md transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Desktop header */}
        <div className="hidden md:block p-6 border-b border-neutral-200">
          <h1 className="text-xl font-bold">Settings</h1>
          <p className="text-sm text-neutral-600">Manage system configuration</p>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    const nextId = item.id as SettingsSectionId;
                    if (nextId !== activeSection) {
                      if (!loadedSectionsRef.current.has(nextId)) {
                        setSectionLoading(prev => ({ ...prev, [nextId]: true }));
                      }
                      setSearchParams(
                        (prev) => {
                          const p = new URLSearchParams(prev);
                          p.set('section', nextId);
                          return p;
                        },
                        { replace: true }
                      );
                    }
                    setActiveSection(nextId);
                    setSidebarOpen(false); // Close sidebar on mobile when item is selected
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto w-full">
        <div className="p-4 md:p-8">
          {renderContent()}
        </div>
      </div>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </h3>
            
            <form onSubmit={handleLocationSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={locationForm.name}
                  onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter location name"
                />
              </div>

              {GOOGLE_MAPS_API_KEY ? (
                <div className="space-y-3">
                  {/* Show current address in edit mode */}
                  {editingLocation && editingLocation.address && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Current Address
                      </label>
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-50 text-neutral-700">
                        {editingLocation.address}
                      </div>
                    </div>
                  )}
                  
                  <LocationMapPicker
                    address={locationForm.address}
                    latitude={locationForm.latitude}
                    longitude={locationForm.longitude}
                    onAddressChange={(address) => {
                      console.log('onAddressChange called with:', address);
                      setLocationForm(prev => ({ ...prev, address }));
                    }}
                    onCoordinatesChange={(lat, lng) => {
                      console.log('onCoordinatesChange called with:', lat, lng);
                      setLocationForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
                    }}
                    apiKey={GOOGLE_MAPS_API_KEY}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Address *
                  </label>
                  <textarea
                    required
                    value={locationForm.address}
                    onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    placeholder="Enter address"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Note: Add REACT_APP_GOOGLE_MAPS_API_KEY to enable map picker
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Timezone *
                </label>
                <select
                  required
                  value={locationForm.timezone}
                  onChange={(e) => setLocationForm({ ...locationForm, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Europe/Dublin">Europe/Dublin</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Night start (optional)
                  </label>
                  <input
                    type="text"
                    value={locationForm.nightStart ?? ''}
                    onChange={(e) => setLocationForm({ ...locationForm, nightStart: e.target.value })}
                    placeholder="HH:mm — overrides company default for payroll night band"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Night end (optional)
                  </label>
                  <input
                    type="text"
                    value={locationForm.nightEnd ?? ''}
                    onChange={(e) => setLocationForm({ ...locationForm, nightEnd: e.target.value })}
                    placeholder="HH:mm"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <p className="text-xs text-neutral-500 -mt-2">
                Leave blank to use company settings. Format 22:00–06:00 style window (see payroll docs).
              </p>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowLocationModal(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingLocation ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Position Modal */}
      {showPositionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {editingPosition ? 'Edit Position' : 'Add New Position'}
            </h3>
            
            <form onSubmit={handlePositionSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={positionForm.title}
                  onChange={(e) => setPositionForm({ ...positionForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter position title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description
                </label>
                <textarea
                  value={positionForm.description}
                  onChange={(e) => setPositionForm({ ...positionForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Enter description (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Color *
                </label>
                <div className="flex items-center gap-3">
                <input
                  type="color"
                  required
                  value={positionForm.color}
                  onChange={(e) => setPositionForm({ ...positionForm, color: e.target.value })}
                    className="w-12 h-10 border border-neutral-300 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    value={positionForm.color}
                    onChange={(e) => setPositionForm({ ...positionForm, color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="#3b82f6"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowPositionModal(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingPosition ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Time Station Modal */}
      {showTimeStationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-neutral-200">
              <h3 className="text-lg font-semibold mb-2">
                {editingTimeStation ? 'Edit Time Station' : 'Add Time Station'}
              </h3>
              <p className="text-sm text-neutral-600">
                Configure iPad time station settings for employee attendance tracking.
              </p>
            </div>
            
            <div className="overflow-y-auto flex-1 p-6">
              <form id="timeStationForm" onSubmit={handleTimeStationSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Station Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={timeStationForm.name}
                    onChange={(e) => setTimeStationForm({ ...timeStationForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Front Desk iPad"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Device ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={timeStationForm.deviceId}
                    onChange={(e) => setTimeStationForm({ ...timeStationForm, deviceId: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="IPAD-001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Location *
                  </label>
                  <select
                    required
                    value={timeStationForm.locationId}
                    onChange={(e) => setTimeStationForm({ ...timeStationForm, locationId: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={timeStationForm.description}
                  onChange={(e) => setTimeStationForm({ ...timeStationForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Located at the main entrance..."
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Status *
                  </label>
                  <select
                    value={timeStationForm.status || 'Active'}
                    onChange={(e) => setTimeStationForm({ ...timeStationForm, status: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <div className="text-sm text-neutral-600 mt-1">
                    Select the status of this time station
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Require Photo
                    </label>
                    <div className="text-sm text-neutral-600">Capture employee photo during clock in/out</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={timeStationForm.allowPhotoCapture}
                      onChange={(e) => setTimeStationForm({ ...timeStationForm, allowPhotoCapture: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Location Tracking
                    </label>
                    <div className="text-sm text-neutral-600">Record GPS coordinates when clocking in/out</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={timeStationForm.locationTracking}
                      onChange={(e) => setTimeStationForm({ ...timeStationForm, locationTracking: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      PIN ClockIN
                    </label>
                    <div className="text-sm text-neutral-600">Allow PIN-based clock in/out</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={timeStationForm.allowPinClockIn === true}
                      onChange={(e) => setTimeStationForm({ ...timeStationForm, allowPinClockIn: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Is Wifi Restricted?
                    </label>
                    <div className="text-sm text-neutral-600">Restrict device to specific WiFi networks</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isWifiRestricted}
                      onChange={(e) => {
                        setIsWifiRestricted(e.target.checked);
                        // If disabling WiFi restriction, clear the WiFi entries
                        if (!e.target.checked) {
                          setTimeStationForm({ ...timeStationForm, allowedWifis: [] });
                        } else {
                          // If enabling, add one empty entry if none exist
                          if (!timeStationForm.allowedWifis || timeStationForm.allowedWifis.length === 0) {
                            setTimeStationForm({ ...timeStationForm, allowedWifis: [{ ssid: '', bssid: '' }] });
                          }
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {isWifiRestricted && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Allowed Wifis
                  </label>
                  <p className="text-sm text-neutral-600 mb-3">
                    Add WiFi networks (SSID and BSSID) that are allowed for this device.
                  </p>
                  {timeStationForm.allowedWifis?.map((wifi, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={wifi.ssid}
                          onChange={(e) => {
                            const newWifis = [...(timeStationForm.allowedWifis || [])];
                            newWifis[index].ssid = e.target.value;
                            setTimeStationForm({ ...timeStationForm, allowedWifis: newWifis });
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="WiFi SSID"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={wifi.bssid || ''}
                          onChange={(e) => {
                            const newWifis = [...(timeStationForm.allowedWifis || [])];
                            newWifis[index].bssid = e.target.value;
                            setTimeStationForm({ ...timeStationForm, allowedWifis: newWifis });
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="BSSID (optional)"
                        />
                      </div>
                      {timeStationForm.allowedWifis && timeStationForm.allowedWifis.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newWifis = timeStationForm.allowedWifis?.filter((_, i) => i !== index) || [];
                            setTimeStationForm({ ...timeStationForm, allowedWifis: newWifis });
                          }}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setTimeStationForm({
                        ...timeStationForm,
                        allowedWifis: [...(timeStationForm.allowedWifis || []), { ssid: '', bssid: '' }]
                      });
                    }}
                    className="mt-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors border border-blue-300"
                  >
                    + Add WiFi
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  IP Address Ranges (Optional)
                </label>
                <p className="text-xs text-neutral-500 mb-3">
                  Add IP address ranges in CIDR notation (e.g., 203.12.18.0/24, 115.113.72.0/23)
                </p>
                <div className="space-y-2">
                  {timeStationForm.allowedIpRanges?.map((ipRange, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={ipRange.ipRange}
                        onChange={(e) => {
                          const newRanges = [...(timeStationForm.allowedIpRanges || [])];
                          newRanges[index] = { ...newRanges[index], ipRange: e.target.value };
                          setTimeStationForm({ ...timeStationForm, allowedIpRanges: newRanges });
                        }}
                        className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="203.12.18.0/24"
                      />
                      {timeStationForm.allowedIpRanges && timeStationForm.allowedIpRanges.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newRanges = timeStationForm.allowedIpRanges?.filter((_, i) => i !== index) || [];
                            setTimeStationForm({ ...timeStationForm, allowedIpRanges: newRanges.length > 0 ? newRanges : [{ ipRange: '' }] });
                          }}
                          className="px-3 py-2 text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setTimeStationForm({
                        ...timeStationForm,
                        allowedIpRanges: [...(timeStationForm.allowedIpRanges || []), { ipRange: '' }]
                      });
                    }}
                    className="w-full px-3 py-2 text-blue-600 hover:text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add IP Range
                  </button>
                </div>
              </div>
              </form>
            </div>

            <div className="p-6 border-t border-neutral-200">
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowTimeStationModal(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="timeStationForm"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingTimeStation ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-2">
              {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
            </h3>
            <p className="text-sm text-neutral-600 mb-6">
              Create or edit work schedules for your organization.
            </p>
            
            <form onSubmit={handleScheduleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Schedule Name *
                </label>
                <input
                  type="text"
                  required
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Morning Shift"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={scheduleForm.description}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Describe the schedule details..."
                />
              </div>

              <BasketSelect
                label="Locations"
                availableItems={locations.filter(loc => loc.isActive).map(loc => ({ id: loc.id, label: loc.name }))}
                selectedIds={scheduleForm.locationIds ? scheduleForm.locationIds.map(id => Number(id)) : []}
                onChange={(ids) => {
                  console.log('BasketSelect onChange called with ids:', ids);
                  const numericIds = ids.map(id => Number(id));
                  console.log('Setting locationIds to:', numericIds);
                  setScheduleForm({ ...scheduleForm, locationIds: numericIds });
                }}
                required={true}
                placeholder="Select at least one location..."
              />
              {scheduleForm.locationIds && scheduleForm.locationIds.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  Selected: {scheduleForm.locationIds.join(', ')}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payroll Department Modal */}
      {showPayrollDepartmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-2">
              {editingPayrollDepartment ? 'Edit Payroll Department' : 'Add Payroll Department'}
            </h3>
            <p className="text-sm text-neutral-600 mb-6">
              Create or edit payroll departments for your organization.
            </p>
            
            <form onSubmit={handlePayrollDepartmentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Department Name *
                </label>
                <input
                  type="text"
                  required
                  value={payrollDepartmentForm.name}
                  onChange={(e) => setPayrollDepartmentForm({ ...payrollDepartmentForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Finance Department"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Code (Optional)
                </label>
                <input
                  type="text"
                  value={payrollDepartmentForm.code || ''}
                  onChange={(e) => setPayrollDepartmentForm({ ...payrollDepartmentForm, code: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., FIN"
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={payrollDepartmentForm.description || ''}
                  onChange={(e) => setPayrollDepartmentForm({ ...payrollDepartmentForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Describe the payroll department..."
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={payrollDepartmentForm.isActive ?? true}
                    onChange={(e) => setPayrollDepartmentForm({ ...payrollDepartmentForm, isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-neutral-700">Active</span>
                </label>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowPayrollDepartmentModal(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingPayrollDepartment ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 my-8 max-h-[90vh] flex flex-col">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">
                {editingRole ? 'Edit Role' : 'Add Role'}
              </h3>
              <p className="text-sm text-neutral-600">
                Create or edit user roles and assign permissions.
              </p>
            </div>
            
            <form onSubmit={handleRoleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-2">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Role Name *
                </label>
                <input
                  type="text"
                  required
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Manager, Admin, Employee"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Describe the role and its responsibilities..."
                />
              </div>

              {tenantMfaEnabled && (
                <div className="border border-neutral-200 rounded-md p-4 space-y-4 bg-neutral-50">
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-900">Multi-Factor Authentication</h4>
                    <p className="text-xs text-neutral-600 mt-1">
                      Configure MFA requirement and methods for this role. Methods are limited to those enabled for the company.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">MFA requirement</label>
                    <select
                      value={roleForm.mfaRequirement || 'None'}
                      onChange={(e) =>
                        setRoleForm({
                          ...roleForm,
                          mfaRequirement: e.target.value as MfaRequirement,
                          ...(e.target.value === 'None'
                            ? { mfaAllowAuthenticator: false, mfaAllowEmail: false, mfaAllowSms: false, mfaDefaultMethod: null }
                            : {}),
                        })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="None">None</option>
                      <option value="Optional">Optional</option>
                      <option value="Required">Required</option>
                    </select>
                  </div>
                  {roleForm.mfaRequirement && roleForm.mfaRequirement !== 'None' && (
                    <>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-neutral-800">Allowed methods</p>
                        {tenantMfaAllowAuthenticator && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-neutral-300"
                              checked={Boolean(roleForm.mfaAllowAuthenticator)}
                              onChange={(e) => {
                                const next = { ...roleForm, mfaAllowAuthenticator: e.target.checked };
                                const selected = getRoleSelectedMfaMethods(next);
                                if (next.mfaDefaultMethod && !selected.includes(next.mfaDefaultMethod)) {
                                  next.mfaDefaultMethod = selected[0] ?? null;
                                }
                                setRoleForm(next);
                              }}
                            />
                            <span className="text-sm text-neutral-700">Authenticator app</span>
                          </label>
                        )}
                        {tenantMfaAllowEmail && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-neutral-300"
                              checked={Boolean(roleForm.mfaAllowEmail)}
                              onChange={(e) => {
                                const next = { ...roleForm, mfaAllowEmail: e.target.checked };
                                const selected = getRoleSelectedMfaMethods(next);
                                if (next.mfaDefaultMethod && !selected.includes(next.mfaDefaultMethod)) {
                                  next.mfaDefaultMethod = selected[0] ?? null;
                                }
                                setRoleForm(next);
                              }}
                            />
                            <span className="text-sm text-neutral-700">Email</span>
                          </label>
                        )}
                        {tenantMfaAllowSms && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-neutral-300"
                              checked={Boolean(roleForm.mfaAllowSms)}
                              onChange={(e) => {
                                const next = { ...roleForm, mfaAllowSms: e.target.checked };
                                const selected = getRoleSelectedMfaMethods(next);
                                if (next.mfaDefaultMethod && !selected.includes(next.mfaDefaultMethod)) {
                                  next.mfaDefaultMethod = selected[0] ?? null;
                                }
                                setRoleForm(next);
                              }}
                            />
                            <span className="text-sm text-neutral-700">SMS</span>
                          </label>
                        )}
                        {!tenantMfaAllowAuthenticator && !tenantMfaAllowEmail && !tenantMfaAllowSms && (
                          <p className="text-xs text-amber-700">No MFA methods are enabled at company level.</p>
                        )}
                      </div>
                      {getRoleSelectedMfaMethods(roleForm).length >= 2 && (
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Default method</label>
                          <select
                            value={roleForm.mfaDefaultMethod || ''}
                            onChange={(e) =>
                              setRoleForm({
                                ...roleForm,
                                mfaDefaultMethod: (e.target.value || null) as MfaMethod | null,
                              })
                            }
                            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select default…</option>
                            {getRoleSelectedMfaMethods(roleForm).map((m) => (
                              <option key={m} value={m}>
                                {m === 'Authenticator' ? 'Authenticator app' : m === 'Sms' ? 'SMS' : m}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-3">
                  Permissions
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto border border-neutral-200 rounded-md p-4 bg-neutral-50">
                  {permissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-start space-x-3 p-3 rounded-lg border bg-white hover:bg-blue-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        id={`permission-${permission.id}`}
                        checked={roleForm.permissionIds?.includes(permission.id) || false}
                        onChange={() => handlePermissionToggle(permission.id)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={`permission-${permission.id}`}
                          className="text-sm font-medium text-neutral-900 cursor-pointer block"
                        >
                          {permission.name}
                        </label>
                        {permission.description && (
                          <p className="text-xs text-neutral-600 mt-1">
                            {permission.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {permissions.length === 0 && (
                  <p className="text-sm text-neutral-500 text-center py-4">
                    No permissions available
                  </p>
                )}
              </div>
            </form>

            <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-neutral-200">
              <button
                type="button"
                onClick={() => setShowRoleModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRoleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {editingRole ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Activation/Deactivation Confirmation Dialog */}
      {locationConfirmDialog.open && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setLocationConfirmDialog({ open: false, locationId: null, locationName: '', action: null })}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Icon */}
              <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${
                locationConfirmDialog.action === 'activate' 
                  ? 'bg-green-100' 
                  : 'bg-orange-100'
              }`}>
                {locationConfirmDialog.action === 'activate' ? (
                  <Power className="h-10 w-10 text-green-600" />
                ) : (
                  <PowerOff className="h-10 w-10 text-orange-600" />
                )}
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-neutral-900 mb-2 text-center">
                {locationConfirmDialog.action === 'activate' 
                  ? 'Activate Location?' 
                  : 'Deactivate Location?'}
              </h3>

              {/* Message */}
              <p className="text-sm text-neutral-600 mb-6 text-center">
                Are you sure you want to {locationConfirmDialog.action === 'activate' ? 'activate' : 'deactivate'} the location{' '}
                <strong className="font-medium text-neutral-900">"{locationConfirmDialog.locationName}"</strong>?
                {locationConfirmDialog.action === 'deactivate' && (
                  <span className="block mt-2">
                    This location will be hidden from active location lists but can be reactivated later.
                  </span>
                )}
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setLocationConfirmDialog({ open: false, locationId: null, locationName: '', action: null })}
                  className="flex-1 px-4 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-700 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmLocationAction}
                  className={`flex-1 px-4 py-2 rounded-md text-white font-medium transition-colors ${
                    locationConfirmDialog.action === 'activate'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {locationConfirmDialog.action === 'activate' ? 'Activate' : 'Deactivate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
    </div>
  );
};

export default Settings;
